// systems/offlineProgress.js
// Offline progress calculation and application

import { gameState } from '../game/gameState.js';
import { OFFLINE_CONFIG, SKILLS, FARM_CONFIG } from '../game/constants.js';
import { addXP } from './experience.js';
import { getItemName } from '../data/items/index.js';
import logger from '../utils/logger.js';
import { farmingSkill } from '../skills/farming/farming.js';
import { isBloomholdEligible } from './bloomhold.js';

// Skill data imports for node lookups
import { ROCKS } from '../skills/mining/miningData.js';
import { TREES } from '../skills/woodcutting/woodcuttingData.js';
import { FISH } from '../skills/fishing/fishingData.js';
import { TARGETS, PILFERER_SPEED_CONFIG, PILFERER_XP_CONFIG, GUILD_DOMAINS, getDomainItemsAtTier } from '../skills/pilfering/pilferingData.js';
import { pilferingSkill } from '../skills/pilfering/pilfering.js';
import { CROPS } from '../skills/farming/farmingData.js';
import { BLUEPRINTS } from '../skills/architecting/architectingData.js';
import { JEWELRY, ALL_ARMOR, ALL_MAGIC_ARMOR } from '../skills/crafting/craftingData.js';
import { BARS, ARROW_HEADS, SWORDS, DIRKS, GREATSWORDS, MELEE_ARMOR, BULWARKS } from '../skills/smithing/smithingData.js';
import { LOGS, RITUALS, getRitualAshesOutput } from '../skills/pyre/pyreData.js';
import { FOOD, EMPOWERED_RECIPES } from '../skills/cooking/cookingData.js';
// New skills
import { SHAFT_RECIPES, ARROW_RECIPES, BOW_RECIPES, STAFF_RECIPES } from '../skills/woodworking/woodworkingData.js';
import { ESSENCE_NODES, GLYPH_RECIPES, GLYPH_CORE_RECIPES, FOCUS_RECIPES } from '../skills/glyphbinding/glyphbindingData.js';
import { HERB_CLEANING, POTION_RECIPES } from '../skills/alchemy/alchemyData.js';
import { GATHERING_ZONES, TIER_DROPS, FOOD_YIELDS } from '../skills/gatherer/gathererData.js';
import { husbandrySkill } from '../skills/husbandry/husbandry.js';
import { PET_CONTRACTS, PETS, getContractTierRewards } from '../skills/husbandry/husbandryData.js';
import { architectingSkill } from '../skills/architecting/architecting.js';
import { bountySkill } from '../skills/bounty/bounty.js';
import { MILESTONES, CIVIC_CONTRACTS } from '../skills/architecting/architectingData.js';
// Combat imports for offline progress
import { getZone, getMob, getRandomMobForZone, getZoneLoot, getMobLoot, getZoneAverageXP, getVisibleMobsForZone, isZoneVisible, getBossMechanics, getMobMechanic } from '../skills/combat/combatData.js';
import { getBountyMark } from '../skills/bounty/bountyData.js';
// Pyre Veil lore system for offline level-ups
import { checkPyreLoreTriggers } from '../ui/pyreVeilLoreModal.js';
import { COMBAT_CONFIG } from '../game/constants.js';
import { getItem } from '../data/items/index.js';
import { getSpell } from '../data/spells.js';
// Talent system imports for offline bonuses
import { getTalentLootBonus, getTalentProcChance, getTalentFlatBonus } from './talentTree/talentTree.js';
// Set bonus data for offline combat simulation
import { SET_BONUSES } from '../data/items/setBonusData.js';

/**
 * Calculate architecting XP from auto-eat (Kitchen Overseer talent) for offline
 * @param {number} foodConsumed - Number of food eaten via auto-eat
 * @returns {number} Total architecting XP to award
 */
function calculateAutoEatArchXP(foodConsumed) {
    if (foodConsumed <= 0) return 0;

    // Check if Mess Hall milestone is built
    if (!gameState.hasMilestone('mess_hall')) return 0;

    // Get talent rank for Kitchen Overseer (scaling: 15/25/40)
    const talentTree = gameState.getTalentTree(SKILLS.ARCHITECTING);
    if (!talentTree?.nodes?.arch_kitchen) return 0;

    const rank = talentTree.nodes.arch_kitchen || 0;
    if (rank <= 0) return 0;

    const scalingValues = [15, 25, 40];
    const baseXP = scalingValues[rank - 1] || 0;

    // Apply Realm Steward passive XP bonus if active
    const passiveBonus = getTalentFlatBonus(SKILLS.ARCHITECTING, 'passiveXPBonus');
    const xpPerEat = Math.floor(baseXP * (1 + passiveBonus));

    return xpPerEat * foodConsumed;
}

/**
 * Calculate architecting XP from BloomHold operations (Realm Steward talent) for offline
 * @param {number} operations - Number of BloomHold plant/harvest operations
 * @returns {number} Total architecting XP to award
 */
function calculateBloomholdArchXP(operations) {
    if (operations <= 0) return 0;

    // Check if Greenhouse milestone is built
    if (!gameState.hasMilestone('greenhouse')) return 0;

    // Get flat bonus from Realm Steward capstone (flat 10 XP)
    const baseXP = getTalentFlatBonus(SKILLS.ARCHITECTING, 'bloomholdXP');
    if (baseXP <= 0) return 0;

    // Note: passiveXPBonus doesn't apply to bloomholdXP (same capstone)
    return baseXP * operations;
}

/**
 * Simulate talent procs for offline progress
 * Returns object with bonus items to award
 * @param {string} skillId - The skill ID
 * @param {number} actionsCompleted - Number of actions completed offline
 * @param {object} savedAction - The saved action data
 * @param {object} nodeData - The node/resource data for drop info
 * @returns {object} Bonus items from procs { itemId: quantity }
 */
function simulateTalentProcs(skillId, actionsCompleted, savedAction, nodeData) {
    const bonusItems = {};

    // Helper to calculate expected proc count
    const getExpectedProcs = (procName) => {
        // Roll proc once to get chance, then calculate expected count
        // We use a different approach: get proc chance directly
        const procChance = getProcChance(skillId, procName);
        if (procChance <= 0) return 0;
        // Expected procs = actions * chance, with some variance
        const expectedProcs = actionsCompleted * procChance;
        // Add slight random variance (±10%) to make it feel more natural
        const variance = 0.1;
        const minProcs = Math.floor(expectedProcs * (1 - variance));
        const maxProcs = Math.ceil(expectedProcs * (1 + variance));
        return Math.floor(Math.random() * (maxProcs - minProcs + 1)) + minProcs;
    };

    // Helper to add bonus items
    const addBonus = (itemId, quantity) => {
        if (quantity > 0) {
            bonusItems[itemId] = (bonusItems[itemId] || 0) + quantity;
        }
    };

    // Random gem helper - scales with mining level, weighted by tier
    const getRandomGem = () => {
        const miningLevel = gameState.getSkill(SKILLS.MINING).level;
        const gemPool = [
            { id: 'sapphire', weight: 6 },
            { id: 'emerald', weight: 5 }
        ];
        if (miningLevel >= 20) gemPool.push({ id: 'ruby', weight: 4 });
        if (miningLevel >= 40) gemPool.push({ id: 'diamond', weight: 3 });
        if (miningLevel >= 60) gemPool.push({ id: 'astryx_stone', weight: 2 });
        if (miningLevel >= 80) gemPool.push({ id: 'onyx', weight: 1 });

        const totalWeight = gemPool.reduce((sum, g) => sum + g.weight, 0);
        let roll = Math.random() * totalWeight;
        for (const gem of gemPool) {
            roll -= gem.weight;
            if (roll <= 0) return gem.id;
        }
        return gemPool[0].id;
    };

    // Skill-specific proc handling
    switch (skillId) {
        case SKILLS.MINING: {
            // Grand Prospector: +25% to all mining proc chances (matches online rollTalentProc bonusMultiplier)
            const miningDropChanceBonus = getTalentFlatBonus(SKILLS.MINING, 'dropChanceBonus') || 0;
            const miningProcMult = 1 + miningDropChanceBonus;

            // Gem drop proc (from Gem Finder talent + Grand Prospector flat bonus)
            const gemProcs = Math.floor(getExpectedProcs('gemDrop') * miningProcMult);
            // Gem Bonanza talent doubles gem quantity
            const gemQuantity = getTalentFlatBonus(SKILLS.MINING, 'gemQuantity') > 0 ? 2 : 1;
            // Grand Prospector flat gem chance
            const gemDropFlat = getTalentFlatBonus(SKILLS.MINING, 'gemDropFlat');
            const flatGemProcs = Math.floor(actionsCompleted * gemDropFlat);
            const totalGemProcs = gemProcs + flatGemProcs;
            for (let i = 0; i < totalGemProcs; i++) {
                addBonus(getRandomGem(), gemQuantity);
            }
            // Treasure Nose (Husbandry) - bonus gems from global rare loot
            const miningGlobalRare = getTalentFlatBonus(SKILLS.HUSBANDRY, 'globalRareLoot');
            if (miningGlobalRare > 0) {
                const globalRareProcs = Math.floor(actionsCompleted * miningGlobalRare);
                for (let i = 0; i < globalRareProcs; i++) {
                    addBonus(getRandomGem(), gemQuantity);
                }
            }
            // Rare Earth - bonus random ore type per proc
            const bonusOreProcs = Math.floor(getExpectedProcs('bonusOre') * miningProcMult);
            if (bonusOreProcs > 0) {
                const ores = ['pyrestone_ore', 'pale_shard_ore', 'blackiron_ore', 'deepcoal', 'gold_ore', 'skyiron_ore', 'verdant_core_ore', 'voidsteel_ore'];
                for (let i = 0; i < bonusOreProcs; i++) {
                    const randomOre = ores[Math.floor(Math.random() * ores.length)];
                    addBonus(randomOre, 1);
                }
            }

            // Auto-Smelt proc (Heart of the Mountain) - awards smelted bar + Smithing XP
            const autoSmeltProcs = Math.floor(getExpectedProcs('autoSmelt') * miningProcMult);
            if (autoSmeltProcs > 0 && nodeData?.drops?.[0]) {
                const oreToBar = {
                    'pyrestone_ore': 'pyrestone_bar',
                    'pale_shard_ore': 'pale_shard_bar',
                    'blackiron_ore': 'blackiron_bar',
                    'gold_ore': 'gold_bar',
                    'skyiron_ore': 'skyiron_bar',
                    'verdant_core_ore': 'verdant_core_bar',
                    'voidsteel_ore': 'voidsteel_bar'
                };
                const primaryOre = nodeData.drops[0].itemId;
                const barId = oreToBar[primaryOre];
                if (barId) {
                    addBonus(barId, autoSmeltProcs);
                    // Award Smithing XP for smelted bars (matches online behavior)
                    const bar = Object.values(BARS).find(b => b.id === barId);
                    if (bar?.xp) {
                        addXP(SKILLS.SMITHING, Math.floor(bar.xp * autoSmeltProcs), null, { skipTracking: true });
                    }
                }
            }
            break;
        }

        case SKILLS.WOODCUTTING:
            // Rhythmic Precision: +8% flat to all proc chances at max stack
            // For offline, assume player is at max stack (reached within a few actions)
            const wcRhythmicBonus = getTalentFlatBonus(SKILLS.WOODCUTTING, 'rhythmicPrecision');

            // Seed drop proc (+ rhythmic bonus)
            const wcSeedChance = getProcChance(SKILLS.WOODCUTTING, 'seedDrop') + wcRhythmicBonus;
            if (wcSeedChance > 0 && savedAction) {
                const seedProcs = Math.floor(actionsCompleted * wcSeedChance);
                const treeId = savedAction.treeId?.toLowerCase() || savedAction.tree?.id;
                const seedMap = {
                    'common_timber_tree': 'ironwood_seed',
                    'ironwood_tree': 'ironwood_seed',
                    'softbark_tree': 'softbark_seed',
                    'goldleaf_tree': 'goldleaf_seed',
                    'redgrain_tree': 'redgrain_seed',
                    'gravewood_tree': 'gravewood_seed',
                    'arcanewood_tree': 'arcanewood_seed'
                };
                const seedId = seedMap[treeId] || 'ironwood_seed';
                if (seedProcs > 0) addBonus(seedId, seedProcs);
            }

            // Ancient Seed drop (+ rhythmic bonus)
            const wcAncientChance = getProcChance(SKILLS.WOODCUTTING, 'ancientSeedDrop') + wcRhythmicBonus;
            if (wcAncientChance > 0) {
                const ancientProcs = Math.floor(actionsCompleted * wcAncientChance);
                if (ancientProcs > 0) addBonus('ancient_seed', ancientProcs);
            }

            // Kindling Master - guaranteed ash every N chops (threshold-based)
            const wcKindlingMaster = getTalentFlatBonus(SKILLS.WOODCUTTING, 'kindlingMaster');
            if (wcKindlingMaster > 0) {
                const treeLevel = nodeData?.level || 1;
                const threshold = treeLevel >= 50 ? 5 : 8;
                const kindlingAshes = Math.floor(actionsCompleted / threshold);
                if (kindlingAshes > 0) addBonus('ashes', kindlingAshes);
            }

            // Nature's Blessing - Timber! also drops 1-3 random seeds
            const wcNaturesBlessing = getTalentFlatBonus(SKILLS.WOODCUTTING, 'naturesBlessing');
            if (wcNaturesBlessing > 0) {
                const timberChance = getProcChance(SKILLS.WOODCUTTING, 'timberProc') + wcRhythmicBonus;
                if (timberChance > 0) {
                    const expectedTimberProcs = Math.floor(actionsCompleted * timberChance);
                    if (expectedTimberProcs > 0) {
                        const allSeeds = ['ironwood_seed', 'softbark_seed', 'goldleaf_seed', 'redgrain_seed', 'gravewood_seed', 'arcanewood_seed'];
                        // Average 2 seeds per timber proc (1-3 range)
                        const totalSeeds = expectedTimberProcs * 2;
                        for (let i = 0; i < totalSeeds; i++) {
                            const randomSeed = allSeeds[Math.floor(Math.random() * allSeeds.length)];
                            addBonus(randomSeed, 1);
                        }
                    }
                }
            }

            // Treasure Nose (Husbandry) - bonus seeds from global rare loot
            const wcGlobalRare = getTalentFlatBonus(SKILLS.HUSBANDRY, 'globalRareLoot');
            if (wcGlobalRare > 0 && savedAction) {
                const wcGlobalProcs = Math.floor(actionsCompleted * wcGlobalRare);
                if (wcGlobalProcs > 0) {
                    const wcTreeId = savedAction.treeId?.toLowerCase() || savedAction.tree?.id;
                    const wcGlobalSeedMap = {
                        'common_timber_tree': 'ironwood_seed',
                        'ironwood_tree': 'ironwood_seed',
                        'softbark_tree': 'softbark_seed',
                        'goldleaf_tree': 'goldleaf_seed',
                        'redgrain_tree': 'redgrain_seed',
                        'gravewood_tree': 'gravewood_seed',
                        'arcanewood_tree': 'arcanewood_seed'
                    };
                    const wcGlobalSeed = wcGlobalSeedMap[wcTreeId] || 'ironwood_seed';
                    addBonus(wcGlobalSeed, wcGlobalProcs);
                }
            }
            break;

        case SKILLS.FISHING:
            // Bycatch proc (random bonus items with weighted tiers)
            const bycatchProcs = getExpectedProcs('bycatch');
            for (let i = 0; i < bycatchProcs; i++) {
                // Weighted bycatch table matching fishing.js getBycatchItem()
                const bycatchTable = [
                    // Common tier (weight 50)
                    { itemId: 'potato_seed', weight: 8 },
                    { itemId: 'onion_seed', weight: 8 },
                    { itemId: 'cabbage_seed', weight: 6 },
                    { itemId: 'greenleaf_seed', weight: 6 },
                    { itemId: 'duskleaf_seed', weight: 5 },
                    { itemId: 'grimy_greenleaf', weight: 6 },
                    { itemId: 'grimy_duskleaf', weight: 5 },
                    { itemId: 'small_bone', weight: 6 },
                    // Uncommon tier (weight 30)
                    { itemId: 'tomato_seed', weight: 4 },
                    { itemId: 'corn_seed', weight: 4 },
                    { itemId: 'ironbloom_seed', weight: 3 },
                    { itemId: 'emberroot_seed', weight: 3 },
                    { itemId: 'ironwood_seed', weight: 3 },
                    { itemId: 'softbark_seed', weight: 3 },
                    { itemId: 'grimy_ironbloom', weight: 3 },
                    { itemId: 'grimy_emberroot', weight: 3 },
                    { itemId: 'grimy_sanctum_leaf', weight: 2 },
                    { itemId: 'charred_bone', weight: 2 },
                    // Rare tier (weight 15)
                    { itemId: 'strawberry_seed', weight: 2 },
                    { itemId: 'watermelon_seed', weight: 1 },
                    { itemId: 'sunpetal_seed', weight: 2 },
                    { itemId: 'deepvine_seed', weight: 1 },
                    { itemId: 'goldleaf_seed', weight: 2 },
                    { itemId: 'redgrain_seed', weight: 1 },
                    { itemId: 'grimy_sunpetal', weight: 2 },
                    { itemId: 'grimy_deepvine', weight: 1 },
                    { itemId: 'grimy_stonebark', weight: 1 },
                    { itemId: 'dragon_bone', weight: 2 }
                ];
                const totalWeight = bycatchTable.reduce((sum, item) => sum + item.weight, 0);
                let roll = Math.random() * totalWeight;
                for (const item of bycatchTable) {
                    roll -= item.weight;
                    if (roll <= 0) {
                        addBonus(item.itemId, 1);
                        break;
                    }
                }
            }
            // Lucky Haul doubles bycatch too
            const luckyHaulProcs = getExpectedProcs('luckyHaul');
            if (luckyHaulProcs > 0 && bycatchProcs > 0) {
                // Approximate: for each luckyHaul that coincides with bycatch
                const doubleBycatch = Math.min(luckyHaulProcs, bycatchProcs);
                // Add extra bycatch items (they get doubled)
                for (let i = 0; i < doubleBycatch; i++) {
                    addBonus('potato_seed', 1); // Simplified - just add common item
                }
            }

            // Treasure Nose (Husbandry) - bonus treasure from global rare loot
            const fishGlobalRare = getTalentFlatBonus(SKILLS.HUSBANDRY, 'globalRareLoot');
            if (fishGlobalRare > 0) {
                const fishGlobalProcs = Math.floor(actionsCompleted * fishGlobalRare);
                for (let i = 0; i < fishGlobalProcs; i++) {
                    // Matches fishing.js getTreasureItem() distribution
                    const roll = Math.random();
                    if (roll < 0.4) {
                        // 40% coins (average 45 gold from two coin entries)
                        gameState.addGold(Math.floor(Math.random() * 41) + 10);
                    } else if (roll < 0.6) {
                        addBonus('ruby', 1);
                    } else if (roll < 0.8) {
                        addBonus('emerald', 1);
                    } else {
                        addBonus('sapphire', 1);
                    }
                }
            }
            break;

        case SKILLS.SMITHING:
            // Furnace Mastery - generate ash from bar smelting (counter-based)
            if (savedAction?.bar || savedAction?.barId) {
                const furnaceMastery = getTalentFlatBonus(SKILLS.SMITHING, 'furnaceMastery');
                if (furnaceMastery > 0) {
                    // High tier bars (level >= 50): 1 ash per 3 crafts
                    // Low tier bars (level < 50): 1 ash per 5 crafts
                    const threshold = nodeData?.level >= 50 ? 3 : 5;
                    const ashesGenerated = Math.floor(actionsCompleted / threshold);
                    if (ashesGenerated > 0) addBonus('ashes', ashesGenerated);
                }
            }

            // Forge Momentum handled via bonus actions (lines above) - no extra item bonus needed

            // Bulk Commission - extra equipment piece (equipment only)
            const isSmithEquipment = savedAction?.sword || savedAction?.swordId || savedAction?.dirk || savedAction?.dirkId ||
                savedAction?.greatsword || savedAction?.greatswordId || savedAction?.meleeArmor || savedAction?.meleeArmorId ||
                savedAction?.bulwark || savedAction?.bulwarkId;
            if (isSmithEquipment) {
                const bulkProcs = getExpectedProcs('bulkCommission');
                if (bulkProcs > 0 && nodeData?.drops?.[0]) {
                    addBonus(nodeData.drops[0].itemId, bulkProcs);
                }
            }
            break;

        case SKILLS.CRAFTING: {
            // Material save: bonus actions from saved materials
            const craftMaterialSaveChance = getProcChance(SKILLS.CRAFTING, 'materialSave');
            if (craftMaterialSaveChance > 0) {
                const bonusFromSave = Math.min(
                    Math.floor(actionsCompleted * craftMaterialSaveChance / (1 - craftMaterialSaveChance)),
                    timeBasedActions - actionsCompleted
                );
                if (bonusFromSave > 0) {
                    actionsCompleted += bonusFromSave;
                }
            }

            // Patron's Favor: gold tip (25% of output item value)
            const patronChance = getProcChance(SKILLS.CRAFTING, 'patronsFavor');
            if (patronChance > 0 && nodeData?.drops?.[0]?.itemId) {
                const outputItem = getItem(nodeData.drops[0].itemId);
                if (outputItem?.value) {
                    const tipAmount = Math.floor(outputItem.value * 0.25);
                    const expectedProcs = Math.floor(actionsCompleted * patronChance);
                    if (expectedProcs > 0 && tipAmount > 0) {
                        gameState.addGold(expectedProcs * tipAmount);
                    }
                }
            }
            break;
        }

        case SKILLS.PYRE:
            // Cinder Storm proc (3x ashes) - higher priority than bonus ashes
            const cinderStormChance = getProcChance(skillId, 'cinderStorm');
            const cinderStormProcs = getExpectedProcs('cinderStorm');
            if (cinderStormProcs > 0 && nodeData?.drops?.[0]?.quantity) {
                // 3x ashes means 2 bonus ashes per base (already getting 1, add 2 more)
                const baseAshes = nodeData.drops[0].quantity;
                addBonus('ashes', cinderStormProcs * baseAshes * 2);
            }

            // Bonus ashes proc (Smoke Reading) - online uses else-if with cinderStorm
            // Reduce by cinderStorm chance since online only fires when cinderStorm doesn't
            const bonusAshesProcs = getExpectedProcs('bonusAshes');
            const adjustedBonusAshes = Math.floor(bonusAshesProcs * (1 - cinderStormChance));
            if (adjustedBonusAshes > 0) addBonus('ashes', adjustedBonusAshes);

            // Veil's Blessing proc (rituals only - random potion based on Alchemy level)
            if (savedAction?.type === 'pyre_ritual') {
                const veilBlessingProcs = getExpectedProcs('veilBlessing');
                if (veilBlessingProcs > 0) {
                    const alchemyLevel = gameState.getSkill(SKILLS.ALCHEMY)?.level || 1;
                    const availablePotions = Object.values(POTION_RECIPES).filter(p => p.level <= alchemyLevel);

                    for (let i = 0; i < veilBlessingProcs; i++) {
                        if (availablePotions.length > 0) {
                            const randomPotion = availablePotions[Math.floor(Math.random() * availablePotions.length)];
                            const potionDrop = randomPotion.drops?.[0];
                            if (potionDrop) {
                                addBonus(potionDrop.itemId, 1);
                            }
                        } else {
                            // Fallback
                            addBonus('healing_potion_1', 1);
                        }
                    }
                }
            }
            break;

        case SKILLS.ALCHEMY: {
            // Alchemy procs depend on subType (cleaning vs brewing)
            if (savedAction?.subType === 'cleaning') {
                // Herb Save: bonus actions from saved grimy herbs
                const herbSaveChance = getProcChance(SKILLS.ALCHEMY, 'herbSave');
                if (herbSaveChance > 0) {
                    const bonusFromSave = Math.min(
                        Math.floor(actionsCompleted * herbSaveChance / (1 - herbSaveChance)),
                        timeBasedActions - actionsCompleted
                    );
                    if (bonusFromSave > 0) {
                        actionsCompleted += bonusFromSave;
                    }
                }

                // 5x herbs proc (Botanical Genius capstone)
                const fiveXHerbProcs = getExpectedProcs('fiveXHerbs');
                if (fiveXHerbProcs > 0 && savedAction?.recipe?.drops?.[0]?.itemId) {
                    // Each proc gives 4 extra herbs (total 5x)
                    addBonus(savedAction.recipe.drops[0].itemId, fiveXHerbProcs * 4);
                }

                // Transmutation proc (bonus higher-tier herb, or extra of same if max tier)
                const transmutationProcs = getExpectedProcs('transmutation');
                if (transmutationProcs > 0 && savedAction?.recipe?.drops?.[0]?.itemId) {
                    const HERB_TIER_ORDER = [
                        'greenleaf', 'duskleaf', 'ironbloom', 'emberroot', 'sanctum_leaf',
                        'sunpetal', 'deepvine', 'stonebark', 'brightmaw', 'nightvine',
                        'glowstem', 'ashbloom', 'crownwort'
                    ];
                    const currentHerb = savedAction.recipe.drops[0].itemId;
                    const currentIndex = HERB_TIER_ORDER.indexOf(currentHerb);
                    if (currentIndex !== -1) {
                        if (currentIndex < HERB_TIER_ORDER.length - 1) {
                            // Give next tier herb
                            const nextTierHerb = HERB_TIER_ORDER[currentIndex + 1];
                            addBonus(nextTierHerb, transmutationProcs);
                        } else {
                            // Max tier - give extra of same herb
                            addBonus(currentHerb, transmutationProcs);
                        }
                    }
                }
            } else if (savedAction?.subType === 'potion') {
                // Brew Herb Save: bonus actions from saved herbs during brewing
                const brewHerbSaveChance = getProcChance(SKILLS.ALCHEMY, 'brewHerbSave');
                if (brewHerbSaveChance > 0) {
                    const bonusFromSave = Math.min(
                        Math.floor(actionsCompleted * brewHerbSaveChance / (1 - brewHerbSaveChance)),
                        timeBasedActions - actionsCompleted
                    );
                    if (bonusFromSave > 0) {
                        actionsCompleted += bonusFromSave;
                    }
                }

                // Double brew proc (Double Batch talent)
                const doubleBrewProcs = getExpectedProcs('doubleBrew');
                if (doubleBrewProcs > 0 && savedAction?.recipe?.drops?.[0]?.itemId) {
                    addBonus(savedAction.recipe.drops[0].itemId, doubleBrewProcs);
                }
            }
            break;
        }

        case SKILLS.GATHERER: {
            // Herb Attunement - upgrade herb tier for bonus materials
            const herbTierUpgrade = getTalentFlatBonus(SKILLS.GATHERER, 'herbTierUpgrade');
            const herbTiers = [
                'grimy_greenleaf', 'grimy_duskleaf', 'grimy_ironbloom', 'grimy_emberroot',
                'grimy_sanctum_leaf', 'grimy_sunpetal', 'grimy_deepvine', 'grimy_frostmint',
                'grimy_stonebark', 'grimy_voidroot', 'grimy_soulwort', 'grimy_gloomshade', 'grimy_celestine'
            ];

            // Bonus material chance (base chance + gatherer talent + husbandry Forager's Friend)
            if (savedAction?.zone?.tier) {
                const gathererBonusMat = getTalentFlatBonus(SKILLS.GATHERER, 'bonusMaterialChance');
                const husbandryBonusMat = getTalentFlatBonus(SKILLS.HUSBANDRY, 'gathererBonusMaterial');
                const zoneTierForMat = savedAction.zone.tier;
                const tierDataForMat = TIER_DROPS[zoneTierForMat];
                const baseBonusMatChance = tierDataForMat?.bonusMaterials?.chance || 0;
                const totalBonusMatChance = baseBonusMatChance + gathererBonusMat + husbandryBonusMat;
                if (totalBonusMatChance > 0) {
                    const zoneTier = savedAction.zone.tier;
                    const tierData = TIER_DROPS[zoneTier];
                    if (tierData?.bonusMaterials?.items) {
                        const bonusMatProcs = Math.floor(actionsCompleted * totalBonusMatChance);
                        const totalWeight = tierData.bonusMaterials.items.reduce((sum, item) => sum + item.weight, 0);
                        for (let i = 0; i < bonusMatProcs; i++) {
                            let roll = Math.random() * totalWeight;
                            for (const item of tierData.bonusMaterials.items) {
                                roll -= item.weight;
                                if (roll <= 0) {
                                    let itemId = item.itemId;
                                    // Apply Herb Attunement upgrade
                                    if (herbTierUpgrade > 0 && itemId.startsWith('grimy_')) {
                                        const idx = herbTiers.indexOf(itemId);
                                        if (idx >= 0 && idx < herbTiers.length - 1) {
                                            itemId = herbTiers[idx + 1];
                                        }
                                    }
                                    addBonus(itemId, 1);
                                    break;
                                }
                            }
                        }
                    }
                }
            }

            // Seed Finder - awards random seeds
            const seedFindProcs = getExpectedProcs('seedFind');
            if (seedFindProcs > 0) {
                const allSeeds = [
                    'ironwood_seed', 'softbark_seed', 'goldleaf_seed', 'redgrain_seed',
                    'gravewood_seed', 'arcanewood_seed',
                    'greenleaf_seed', 'duskleaf_seed', 'ironbloom_seed', 'emberroot_seed',
                    'sanctum_leaf_seed', 'sunpetal_seed', 'deepvine_seed', 'frostmint_seed',
                    'stonebark_seed', 'voidroot_seed', 'soulwort_seed', 'gloomshade_seed', 'celestine_seed'
                ];
                for (let i = 0; i < seedFindProcs; i++) {
                    const randomSeed = allSeeds[Math.floor(Math.random() * allSeeds.length)];
                    addBonus(randomSeed, 1);
                }
            }

            // Hidden Cache - bonus loot (3-5 items per proc, simplified to food + rare)
            const hiddenCacheProcs = getExpectedProcs('hiddenCache');
            if (hiddenCacheProcs > 0 && savedAction?.zone?.tier) {
                const zoneTier = savedAction.zone.tier;
                const tierData = TIER_DROPS[zoneTier];
                if (tierData) {
                    // Each cache averages 4 items: ~2 food + 1 bonus material + 1 rare
                    addBonus(tierData.food, hiddenCacheProcs * 3);
                    if (tierData.rare) addBonus(tierData.rare, hiddenCacheProcs);
                }
            }

            // Treasure Nose (Husbandry) - extra rare drops from global rare loot bonus
            const gathererGlobalRare = getTalentFlatBonus(SKILLS.HUSBANDRY, 'globalRareLoot');
            if (gathererGlobalRare > 0 && savedAction?.zone?.tier) {
                const gatherRareTierMap = {
                    1: 'twisted_root_fiber',
                    2: 'living_arcane_husk',
                    3: 'condensed_arcane_core_fragment'
                };
                const gatherRareItem = gatherRareTierMap[savedAction.zone.tier] || 'twisted_root_fiber';
                const gathererRareProcs = Math.floor(actionsCompleted * gathererGlobalRare);
                if (gathererRareProcs > 0) addBonus(gatherRareItem, gathererRareProcs);
            }

            // Material Transmutation - upgrade gathered bonus materials to next tier
            const transmuteProcs = getExpectedProcs('materialTransmutation');
            if (transmuteProcs > 0 && savedAction?.zone?.tier) {
                const zoneTier = savedAction.zone.tier;
                const tierData = TIER_DROPS[zoneTier];
                if (tierData?.bonusMaterials?.items) {
                    // Tier upgrade chains (crops, cloth, bones)
                    const cropTiers = ['potato', 'onion', 'cabbage', 'tomato', 'corn', 'strawberry', 'watermelon', 'brightmaw', 'kings_fruit'];
                    const clothTiers = ['linenweave', 'silkweave', 'aetherweave', 'runebound_cloth', 'shadowweave', 'voidcloth'];
                    const boneTiers = ['small_bone', 'charred_bone', 'ancient_bone', 'dragon_bone', 'primordial_bone'];

                    // Pick first upgradeable bonus material from the tier
                    for (const matItem of tierData.bonusMaterials.items) {
                        let upgradedItem = null;
                        for (const tierList of [cropTiers, clothTiers, boneTiers]) {
                            const idx = tierList.indexOf(matItem.itemId);
                            if (idx >= 0 && idx < tierList.length - 1) {
                                upgradedItem = tierList[idx + 1];
                                break;
                            }
                        }
                        if (upgradedItem) {
                            addBonus(upgradedItem, transmuteProcs);
                            break;
                        }
                    }
                }
            }
            break;
        }

        case SKILLS.GLYPHBINDING:
            // Bonus ichor proc (Essence Siphon talent) - ichor gathering only
            // Note: bonusIchor is defined as flatBonus type (not procChance), so we use
            // getTalentFlatBonus directly instead of getExpectedProcs/getProcChance
            if (savedAction?.subType === 'ichor') {
                const bonusIchorChance = getTalentFlatBonus(SKILLS.GLYPHBINDING, 'bonusIchor');
                const bonusIchorProcs = bonusIchorChance > 0 ? Math.floor(actionsCompleted * bonusIchorChance) : 0;
                if (bonusIchorProcs > 0 && savedAction?.node?.id) {
                    // Get ichor type based on node being gathered
                    const ichorMap = {
                        'arcane_residue_vat': 'arcane_residue',
                        'refined_ichor_vat': 'refined_arcane_ichor',
                        'purified_core_vat': 'purified_arcane_core'
                    };
                    const ichorId = ichorMap[savedAction.node.id] || 'arcane_residue';
                    // Each bonus proc gives ~50% of a single gather
                    addBonus(ichorId, bonusIchorProcs);
                }
            }
            // Ichor Burst proc (3x ichor) - ichor gathering only
            if (savedAction?.subType === 'ichor') {
                const ichorBurstProcs = getExpectedProcs('ichorBurst');
                if (ichorBurstProcs > 0 && savedAction?.node?.id) {
                    const ichorMap = {
                        'arcane_residue_vat': 'arcane_residue',
                        'refined_ichor_vat': 'refined_arcane_ichor',
                        'purified_core_vat': 'purified_arcane_core'
                    };
                    const ichorId = ichorMap[savedAction.node.id] || 'arcane_residue';
                    // 3x ichor means 2 bonus per base proc
                    addBonus(ichorId, ichorBurstProcs * 2);
                }
            }
            // Ichor Save: bonus actions from saved materials (glyph crafting only)
            if (savedAction?.subType === 'glyph') {
                const ichorSaveChance = getProcChance(SKILLS.GLYPHBINDING, 'ichorSave');
                if (ichorSaveChance > 0) {
                    const bonusFromSave = Math.min(
                        Math.floor(actionsCompleted * ichorSaveChance / (1 - ichorSaveChance)),
                        timeBasedActions - actionsCompleted
                    );
                    if (bonusFromSave > 0) {
                        actionsCompleted += bonusFromSave;
                    }
                }
            }
            break;

        case SKILLS.WOODWORKING:
            // Arrow Storm proc (3x arrows) - arrows only, higher priority than bonusArrows
            if (savedAction?.subType === 'arrows') {
                const arrowStormChance = getProcChance(SKILLS.WOODWORKING, 'arrowStorm');
                const arrowStormProcs = getExpectedProcs('arrowStorm');
                if (arrowStormProcs > 0 && savedAction?.recipe?.drops?.[0]) {
                    // Each proc gives 2x bonus arrows (already getting 1x base)
                    const baseArrows = savedAction.recipe.drops[0].quantity || 10;
                    addBonus(savedAction.recipe.drops[0].itemId, arrowStormProcs * baseArrows * 2);
                }
                // Bonus arrows proc (Precise Fletching talent) - mutually exclusive with arrowStorm
                // Reduce expected procs by arrowStorm probability (else-if online)
                const bonusArrowsChance = getProcChance(SKILLS.WOODWORKING, 'bonusArrows');
                const adjustedBonusChance = bonusArrowsChance * (1 - arrowStormChance);
                if (adjustedBonusChance > 0 && savedAction?.recipe?.drops?.[0]?.itemId) {
                    const expectedProcs = actionsCompleted * adjustedBonusChance;
                    const variance = 0.1;
                    const minProcs = Math.floor(expectedProcs * (1 - variance));
                    const maxProcs = Math.ceil(expectedProcs * (1 + variance));
                    const bonusArrowProcs = Math.floor(Math.random() * (maxProcs - minProcs + 1)) + minProcs;
                    if (bonusArrowProcs > 0) {
                        // Each proc gives +5 arrows
                        addBonus(savedAction.recipe.drops[0].itemId, bonusArrowProcs * 5);
                    }
                }
            }
            // Staffs only procs
            if (savedAction?.subType === 'staffs') {
                // Arcane Overflow proc (random ichor)
                const bonusIchorProcs = getExpectedProcs('bonusIchor');
                if (bonusIchorProcs > 0) {
                    const ichorTypes = ['arcane_residue', 'refined_arcane_ichor', 'purified_arcane_core'];
                    for (let i = 0; i < bonusIchorProcs; i++) {
                        const randomIchor = ichorTypes[Math.floor(Math.random() * ichorTypes.length)];
                        addBonus(randomIchor, 1);
                    }
                }
                // Veil-Touched Timber proc (random glyph based on Glyphbinding level)
                const veilTimberProcs = getExpectedProcs('veilTimber');
                if (veilTimberProcs > 0) {
                    const glyphbindingLevel = gameState.getSkill(SKILLS.GLYPHBINDING)?.level || 1;
                    const availableGlyphs = Object.values(GLYPH_RECIPES)
                        .filter(g => g.level <= glyphbindingLevel)
                        .map(g => g.drops[0].itemId);

                    for (let i = 0; i < veilTimberProcs; i++) {
                        if (availableGlyphs.length > 0) {
                            const randomGlyph = availableGlyphs[Math.floor(Math.random() * availableGlyphs.length)];
                            addBonus(randomGlyph, 1);
                        } else {
                            addBonus('storm_glyph', 1);
                        }
                    }
                }
            }
            break;

        // Other skills don't have bonus item procs (just multiplier procs handled via lootBonus)
    }

    return bonusItems;
}

/**
 * Get proc chance for a talent (wrapper around talent system)
 * Uses the actual talent tree data and purchased nodes
 * @param {string} skillId - The skill ID
 * @param {string} procName - The proc name (e.g., 'doubleOre', 'gemDrop')
 * @returns {number} Proc chance (0-1)
 */
function getProcChance(skillId, procName) {
    // Use the talent system's built-in function which:
    // - Looks up all nodes with this proc type
    // - Sums up proc chances based on purchased ranks
    // - Applies mastery multiplier
    return getTalentProcChance(skillId, procName);
}

/**
 * Calculate loot multiplier from proc effects (double, 5x, 10x chances)
 * Returns a multiplier >= 1.0 representing expected extra drops from procs
 * @param {string} skillId - The skill ID
 * @param {number} actionsCompleted - Number of actions completed
 * @returns {number} Multiplier (1.0 = no bonus, 1.1 = 10% more drops on average)
 */
function calculateLootMultiplierProcs(skillId, actionsCompleted, savedAction = null) {
    let multiplier = 1.0;

    // Helper to get expected bonus from a proc
    const getExpectedBonus = (procName, bonusMultiplier) => {
        const procChance = getProcChance(skillId, procName);
        if (procChance <= 0) return 0;
        // Expected bonus: chance * (multiplier - 1) since base is already counted
        // e.g., 5% double = 0.05 * 1 = 0.05 extra (5% more drops on average)
        // e.g., 3% 5x = 0.03 * 4 = 0.12 extra (12% more drops on average)
        return procChance * (bonusMultiplier - 1);
    };

    // Skill-specific multiplier procs
    switch (skillId) {
        case SKILLS.MINING: {
            // Grand Prospector: +25% to all mining proc chances
            const miningDropBonus = getTalentFlatBonus(SKILLS.MINING, 'dropChanceBonus') || 0;
            const miningMult = 1 + miningDropBonus;
            // doubleOre: 2x drops (Keen Eye)
            multiplier += getExpectedBonus('doubleOre', 2) * miningMult;
            // bonusOre: handled in simulateTalentProcs (awards random ore, not same ore)
            // pureVein: 5x drops
            multiplier += getExpectedBonus('pureVein', 5) * miningMult;
            // motherlode: 10x drops
            multiplier += getExpectedBonus('motherlode', 10) * miningMult;
            // jackpot: 25x drops (from Motherlode capstone)
            multiplier += getExpectedBonus('jackpot', 25) * miningMult;
            break;
        }

        case SKILLS.WOODCUTTING:
            // Rhythmic Precision: +8% flat bonus to all proc chances at max stack
            const wcLootRhythmicBonus = getTalentFlatBonus(SKILLS.WOODCUTTING, 'rhythmicPrecision');
            // doubleLogs: 2x drops (+ rhythmic bonus)
            const doubleLogsChance = getProcChance(SKILLS.WOODCUTTING, 'doubleLogs') + wcLootRhythmicBonus;
            if (doubleLogsChance > 0) multiplier += doubleLogsChance * (2 - 1);
            // timberProc: 5x drops (+ rhythmic bonus)
            const timberProcChance = getProcChance(SKILLS.WOODCUTTING, 'timberProc') + wcLootRhythmicBonus;
            if (timberProcChance > 0) multiplier += timberProcChance * (5 - 1);
            break;

        case SKILLS.FISHING:
            // doubleFish: 2x drops (Well Hooked talent)
            multiplier += getExpectedBonus('doubleFish', 2);
            // luckyHaul: 2x ALL drops (Lucky Haul talent)
            multiplier += getExpectedBonus('luckyHaul', 2);
            // feedingFrenzy: guaranteed double ONLY after a doubleFish or luckyHaul proc fires
            // Effect = P(trigger) * P(frenzy) * (2-1), where trigger = doubleFish + luckyHaul
            const frenzyChance = getProcChance(skillId, 'feedingFrenzy');
            if (frenzyChance > 0) {
                const triggerChance = getProcChance(skillId, 'doubleFish') + getProcChance(skillId, 'luckyHaul');
                multiplier += triggerChance * frenzyChance * (2 - 1);
            }
            break;

        case SKILLS.COOKING:
            // doubleFood: 2x drops (Seared Perfection)
            multiplier += getExpectedBonus('doubleFood', 2);
            // banquet: 5x drops
            multiplier += getExpectedBonus('banquet', 5);
            break;

        case SKILLS.PYRE:
            // cinderStorm handled in simulateTalentProcs (awards bonus ashes per proc)
            break;

        case SKILLS.CRAFTING:
            // doubleOutput: 2x drops
            multiplier += getExpectedBonus('doubleOutput', 2);
            // bulkProduce: +2 extra items (approximated as 3x for qty 1 outputs)
            multiplier += getExpectedBonus('bulkProduce', 3);
            break;

        case SKILLS.GATHERER:
            // doubleFood: 2x drops
            multiplier += getExpectedBonus('doubleFood', 2);
            // tenXDrops: 10x drops (Master Explorer capstone)
            multiplier += getExpectedBonus('tenXDrops', 10);
            // petAssist: 2x drops (requires pet, but we assume player has one for offline)
            multiplier += getExpectedBonus('petAssist', 2);
            break;

        case SKILLS.PILFERING:
            // shadowClone: 2x coins (also doubles XP online, handled in XP multiplier)
            multiplier += getExpectedBonus('shadowClone', 2);
            // phantomStrike: 2x coins (Master Thief) - reduced from 5x
            multiplier += getExpectedBonus('phantomStrike', 2);
            // tenXCoins: 5x coins (Master Thief capstone) - reduced from 10x
            multiplier += getExpectedBonus('tenXCoins', 5);
            // bonusLootFind: +50% bonus drops (Bonus Loot talent)
            multiplier += getExpectedBonus('bonusLootFind', 1.5);
            break;

        case SKILLS.GLYPHBINDING:
            // doubleGlyph/tripleGlyph only apply to glyph crafting subtype (not ichor/cores/foci)
            if (!savedAction?.subType || savedAction.subType === 'glyph') {
                // doubleGlyph: 2x output (Resonant Glyphs)
                multiplier += getExpectedBonus('doubleGlyph', 2);
                // tripleGlyph: 3x output (Master Inscriber)
                multiplier += getExpectedBonus('tripleGlyph', 3);
            }
            break;

        case SKILLS.SMITHING:
            // No loot multiplier procs in smithing talent tree
            break;
    }

    return multiplier;
}

/**
 * Calculate XP multiplier from proc effects (double XP chances)
 * Returns a multiplier >= 1.0 representing expected extra XP from procs
 * @param {string} skillId - The skill ID
 * @returns {number} Multiplier (1.0 = no bonus, 1.05 = 5% more XP on average)
 */
function calculateXPMultiplierProcs(skillId) {
    let multiplier = 1.0;

    // Helper to get expected XP bonus from a proc
    const getExpectedBonus = (procName, bonusMultiplier) => {
        const procChance = getProcChance(skillId, procName);
        if (procChance <= 0) return 0;
        // Expected bonus: chance * (multiplier - 1)
        // e.g., 5% double XP = 0.05 * 1 = 0.05 extra (5% more XP on average)
        return procChance * (bonusMultiplier - 1);
    };

    // Skill-specific XP multiplier procs
    switch (skillId) {
        case SKILLS.CRAFTING:
            // inspiredCraft: 2x XP (5% chance)
            multiplier += getExpectedBonus('inspiredCraft', 2);
            break;

        case SKILLS.PILFERING:
            // shadowClone: 2x XP (also 2x coins, handled in loot multiplier)
            multiplier += getExpectedBonus('shadowClone', 2);
            break;
    }

    return multiplier;
}

/**
 * Get item count including BloomHold (for alchemy materials)
 * @param {string} itemId - The item ID
 * @returns {{ inventory: number, bloomhold: number, total: number }}
 */
function getItemCountWithBloomhold(itemId) {
    const inventoryCount = gameState.getItemCount(itemId);
    const bloomholdCount = gameState.isBloomholdUnlocked()
        ? gameState.getBloomholdItemCount(itemId)
        : 0;
    return {
        inventory: inventoryCount,
        bloomhold: bloomholdCount,
        total: inventoryCount + bloomholdCount
    };
}

/**
 * Calculate material-limited actions for crafting-type skills
 * Returns the limited action count and materials to consume
 * @param {object} node - The action node data
 * @param {object} savedAction - The saved action
 * @param {number} timeBasedActions - Actions based on time alone
 * @returns {{ limitedActions: number, materialsToConsume: object, bloomholdToConsume: object, goldToConsume: number }}
 */
function calculateMaterialLimit(node, savedAction, timeBasedActions) {
    // Skills that don't consume materials (gathering skills)
    // Note: active architecting builds DO consume materials (buildMode: 'active')
    const gatheringTypes = ['mining', 'woodcutting', 'fishing', 'pilfering', 'gatherer'];
    if (gatheringTypes.includes(savedAction?.type)) {
        return { limitedActions: timeBasedActions, materialsToConsume: {}, bloomholdToConsume: {}, goldToConsume: 0 };
    }
    // Passive architecting (no buildMode) doesn't consume per action (materials consumed on start)
    if (savedAction?.type === 'architecting' && savedAction?.buildMode !== 'active') {
        return { limitedActions: timeBasedActions, materialsToConsume: {}, bloomholdToConsume: {}, goldToConsume: 0 };
    }

    const materialsToConsume = {};
    const bloomholdToConsume = {};
    let maxPossibleActions = timeBasedActions;
    let goldToConsume = 0;

    // Check if this is an alchemy action (uses BloomHold for materials)
    const isAlchemy = savedAction?.type === 'alchemy';

    // Check for single input (cooking, smithing bars, pyre, alchemy herbs)
    if (node.inputItem && node.inputQuantity) {
        if (isAlchemy) {
            // Alchemy: check inventory + BloomHold
            const counts = getItemCountWithBloomhold(node.inputItem);
            const actionsFromMaterial = Math.floor(counts.total / node.inputQuantity);
            maxPossibleActions = Math.min(maxPossibleActions, actionsFromMaterial);
            if (maxPossibleActions > 0) {
                const totalNeeded = maxPossibleActions * node.inputQuantity;
                // Consume from inventory first, then BloomHold
                const fromInventory = Math.min(counts.inventory, totalNeeded);
                const fromBloomhold = totalNeeded - fromInventory;
                if (fromInventory > 0) materialsToConsume[node.inputItem] = fromInventory;
                if (fromBloomhold > 0) bloomholdToConsume[node.inputItem] = fromBloomhold;
            }
        } else {
            // Non-alchemy: inventory only
            const available = gameState.getItemCount(node.inputItem);
            const actionsFromMaterial = Math.floor(available / node.inputQuantity);
            maxPossibleActions = Math.min(maxPossibleActions, actionsFromMaterial);
            if (maxPossibleActions > 0) {
                materialsToConsume[node.inputItem] = maxPossibleActions * node.inputQuantity;
            }
        }
    }

    // Check for secondary item (smithing deepcoal, alchemy vials)
    if (node.secondaryItem && node.secondaryQuantity) {
        if (isAlchemy) {
            // Alchemy: check inventory + BloomHold for vials
            const counts = getItemCountWithBloomhold(node.secondaryItem);
            const actionsFromMaterial = Math.floor(counts.total / node.secondaryQuantity);
            maxPossibleActions = Math.min(maxPossibleActions, actionsFromMaterial);
            if (maxPossibleActions > 0) {
                const totalNeeded = maxPossibleActions * node.secondaryQuantity;
                // Consume from inventory first, then BloomHold
                const fromInventory = Math.min(counts.inventory, totalNeeded);
                const fromBloomhold = totalNeeded - fromInventory;
                if (fromInventory > 0) materialsToConsume[node.secondaryItem] = fromInventory;
                if (fromBloomhold > 0) bloomholdToConsume[node.secondaryItem] = fromBloomhold;
            }
        } else {
            // Non-alchemy: inventory only
            const available = gameState.getItemCount(node.secondaryItem);
            const actionsFromMaterial = Math.floor(available / node.secondaryQuantity);
            maxPossibleActions = Math.min(maxPossibleActions, actionsFromMaterial);
            if (maxPossibleActions > 0) {
                materialsToConsume[node.secondaryItem] = maxPossibleActions * node.secondaryQuantity;
            }
        }
    }

    // Check for inputs array (empowered cooking, multi-input recipes)
    // Empowered cooking pulls eligible items (crops) from BloomHold like the
    // online tick does — see cooking.js completeEmpowering(). Other multi-input
    // recipes whose items aren't BloomHold-eligible just see inventory.
    if (node.inputs && Array.isArray(node.inputs)) {
        for (const input of node.inputs) {
            const counts = getItemCountWithBloomhold(input.itemId);
            const eligible = isBloomholdEligible(input.itemId);
            const available = eligible ? counts.total : counts.inventory;
            const actionsFromMaterial = Math.floor(available / input.quantity);
            maxPossibleActions = Math.min(maxPossibleActions, actionsFromMaterial);
        }
        // Track consumption for all inputs (inventory first, then BloomHold)
        if (maxPossibleActions > 0) {
            for (const input of node.inputs) {
                const totalNeeded = maxPossibleActions * input.quantity;
                const counts = getItemCountWithBloomhold(input.itemId);
                const eligible = isBloomholdEligible(input.itemId);
                const fromInventory = Math.min(counts.inventory, totalNeeded);
                const fromBloomhold = eligible ? totalNeeded - fromInventory : 0;
                if (fromInventory > 0) materialsToConsume[input.itemId] = fromInventory;
                if (fromBloomhold > 0) bloomholdToConsume[input.itemId] = fromBloomhold;
            }
        }
    }

    // Check for inputItems array (woodworking arrows, bows, staffs)
    if (node.inputItems && Array.isArray(node.inputItems)) {
        for (const input of node.inputItems) {
            const available = gameState.getItemCount(input.itemId);
            const actionsFromMaterial = Math.floor(available / input.quantity);
            maxPossibleActions = Math.min(maxPossibleActions, actionsFromMaterial);
        }
        // Track consumption for all inputItems
        if (maxPossibleActions > 0) {
            for (const input of node.inputItems) {
                materialsToConsume[input.itemId] = maxPossibleActions * input.quantity;
            }
        }
    }

    // Check for materials object (crafting jewelry, some smithing)
    if (node.materials && typeof node.materials === 'object') {
        for (const [itemId, quantity] of Object.entries(node.materials)) {
            const available = gameState.getItemCount(itemId);
            const actionsFromMaterial = Math.floor(available / quantity);
            maxPossibleActions = Math.min(maxPossibleActions, actionsFromMaterial);
        }
        // Track consumption
        if (maxPossibleActions > 0) {
            for (const [itemId, quantity] of Object.entries(node.materials)) {
                materialsToConsume[itemId] = maxPossibleActions * quantity;
            }
        }
    }

    // Check for gold cost (crafting armor)
    if (node.goldCost && node.goldCost > 0) {
        const playerGold = gameState.getGold();
        // Crafting goldWaive proc reduces effective gold cost
        const goldWaiveChance = skillId === SKILLS.CRAFTING ? getProcChance(SKILLS.CRAFTING, 'goldWaive') : 0;
        const effectiveGoldCost = goldWaiveChance > 0 ? node.goldCost * (1 - goldWaiveChance) : node.goldCost;
        const actionsFromGold = Math.floor(playerGold / effectiveGoldCost);
        maxPossibleActions = Math.min(maxPossibleActions, actionsFromGold);
        if (maxPossibleActions > 0) {
            goldToConsume = Math.floor(maxPossibleActions * effectiveGoldCost);
        }
    }

    // Check for pyre ritual bone consumption (always 1 per ritual)
    if (savedAction?.type === 'pyre_ritual' && savedAction?.ritualId) {
        // Look up the ritual to get the bone ID
        const ritual = Object.values(RITUALS).find(r => r.id === savedAction.ritualId);
        if (ritual && ritual.boneId) {
            const boneAvailable = gameState.getItemCount(ritual.boneId);
            maxPossibleActions = Math.min(maxPossibleActions, boneAvailable); // 1 bone per action
            if (maxPossibleActions > 0) {
                materialsToConsume[ritual.boneId] = maxPossibleActions; // 1 per action
            }
        }
    }

    return { limitedActions: maxPossibleActions, materialsToConsume, bloomholdToConsume, goldToConsume };
}

/**
 * Get node data for a skill action
 */
function getNodeData(action) {
    if (!action) return null;

    // Helper to find by id property (consistent with skill lookups)
    const findById = (data, id) => Object.values(data).find(item => item.id === id);

    switch (action.type) {
        case 'mining':
            return action.rock || (action.rockId ? findById(ROCKS, action.rockId) : null);
        case 'woodcutting':
            return action.tree || (action.treeId ? findById(TREES, action.treeId) : null);
        case 'fishing':
            return action.fish || (action.fishId ? findById(FISH, action.fishId) : null);
        case 'pilfering':
            return action.target || (action.targetId ? findById(TARGETS, action.targetId) : null);
        case 'farming':
            return action.crop || (action.cropId ? findById(CROPS, action.cropId) : null);
        case 'architecting':
            return action.blueprint || (action.blueprintId ? findById(BLUEPRINTS, action.blueprintId) : null);
        case 'crafting':
            // Check for jewelry, ranged armor, or magic armor crafting
            if (action.item) return action.item;
            if (action.itemId) {
                // Try jewelry first, then ranged armor, then magic armor
                return findById(JEWELRY, action.itemId) || findById(ALL_ARMOR, action.itemId) || findById(ALL_MAGIC_ARMOR, action.itemId);
            }
            return null;
        case 'smithing':
            // Handle bar, arrowhead, sword, dirk, greatsword, and armor smithing
            if (action.arrowhead || action.arrowheadId) {
                return action.arrowhead || findById(ARROW_HEADS, action.arrowheadId);
            }
            if (action.sword || action.swordId) {
                return action.sword || findById(SWORDS, action.swordId);
            }
            if (action.dirk || action.dirkId) {
                return action.dirk || findById(DIRKS, action.dirkId);
            }
            if (action.greatsword || action.greatswordId) {
                return action.greatsword || findById(GREATSWORDS, action.greatswordId);
            }
            if (action.meleeArmor || action.meleeArmorId) {
                return action.meleeArmor || findById(MELEE_ARMOR, action.meleeArmorId);
            }
            if (action.bulwark || action.bulwarkId) {
                return action.bulwark || findById(BULWARKS, action.bulwarkId);
            }
            return action.bar || (action.barId ? findById(BARS, action.barId) : null);
        case 'pyre':
            return action.log || (action.logId ? findById(LOGS, action.logId) : null);
        case 'pyre_ritual':
            // Rituals use the same log data, with pre-calculated XP in action.ritualXp
            const ritualLog = action.log || (action.logId ? findById(LOGS, action.logId) : null);
            if (ritualLog && action.ritualXp) {
                // Calculate ritual ashes based on log tier (rituals give more ashes)
                const ritualAshes = getRitualAshesOutput(ritualLog.tier);
                // Return a modified node with ritual XP and scaled ashes drops
                return {
                    ...ritualLog,
                    xp: action.ritualXp,
                    drops: [{ itemId: 'ashes', weight: 1, quantity: ritualAshes }]
                };
            }
            return ritualLog;
        case 'cooking':
            return action.food || (action.foodId ? findById(FOOD, action.foodId) : null);
        case 'empowering':
            // Empowered food crafting (combines cooked food + vegetables)
            const empowerRecipe = action.recipe || (action.recipeId ? findById(EMPOWERED_RECIPES, action.recipeId) : null);
            if (empowerRecipe) {
                // Convert output format to drops format for consistency
                return {
                    ...empowerRecipe,
                    drops: [{ itemId: empowerRecipe.output.itemId, quantity: empowerRecipe.output.quantity }]
                };
            }
            return null;
        case 'woodworking':
            // Handle shafts, arrows, bows, and staffs
            if (action.recipe) return action.recipe;
            if (action.subType === 'shafts' && action.recipeId) {
                return findById(SHAFT_RECIPES, action.recipeId);
            }
            if (action.subType === 'arrows' && action.recipeId) {
                return findById(ARROW_RECIPES, action.recipeId);
            }
            if (action.subType === 'bows' && action.recipeId) {
                return findById(BOW_RECIPES, action.recipeId);
            }
            if (action.subType === 'staffs' && action.recipeId) {
                return findById(STAFF_RECIPES, action.recipeId);
            }
            return null;
        case 'glyphbinding':
            // Handle ichor gathering, glyph crafting, and glyph core crafting
            if (action.subType === 'ichor') {
                if (action.node?.id) {
                    return findById(ESSENCE_NODES, action.node.id);
                }
            }
            if (action.subType === 'glyph') {
                if (action.glyph?.id) {
                    return findById(GLYPH_RECIPES, action.glyph.id);
                }
            }
            if (action.subType === 'glyphCore') {
                if (action.glyphCore?.id) {
                    return findById(GLYPH_CORE_RECIPES, action.glyphCore.id);
                }
            }
            if (action.subType === 'focus') {
                if (action.focus?.id) {
                    return findById(FOCUS_RECIPES, action.focus.id);
                }
            }
            return null;
        case 'alchemy':
            // Handle herb cleaning and potion brewing
            if (action.recipe) return action.recipe;
            if (action.subType === 'cleaning' && action.recipe?.id) {
                return findById(HERB_CLEANING, action.recipe.id);
            }
            if (action.subType === 'potion' && action.recipe?.id) {
                return findById(POTION_RECIPES, action.recipe.id);
            }
            return null;
        case 'gatherer':
            return action.zone || (action.zoneId ? findById(GATHERING_ZONES, action.zoneId.toLowerCase()) : null);
        default:
            return null;
    }
}

/**
 * Get skill ID from action type
 */
function getSkillId(actionType) {
    const mapping = {
        mining: SKILLS.MINING,
        woodcutting: SKILLS.WOODCUTTING,
        fishing: SKILLS.FISHING,
        pilfering: SKILLS.PILFERING,
        farming: SKILLS.FARMING,
        architecting: SKILLS.ARCHITECTING,
        crafting: SKILLS.CRAFTING,
        smithing: SKILLS.SMITHING,
        pyre: SKILLS.PYRE,
        pyre_ritual: SKILLS.PYRE,
        cooking: SKILLS.COOKING,
        empowering: SKILLS.COOKING,
        woodworking: SKILLS.WOODWORKING,
        glyphbinding: SKILLS.GLYPHBINDING,
        alchemy: SKILLS.ALCHEMY,
        gatherer: SKILLS.GATHERER,
    };
    return mapping[actionType] || null;
}

/**
 * Calculate farm plot progress during offline time
 * Farm plots grow independently of currentAction
 * Supports talent effects: yield bonuses, procs, auto-replant
 * Simulates MULTIPLE harvest cycles if offline long enough
 * @returns {object} Farm offline progress data
 */
function calculateFarmOfflineProgress() {
    const plots = gameState.getFarmPlots();
    if (!plots) return null;

    const farmSettings = gameState.getFarmSettings();
    const autoHarvestEnabled = farmSettings.autoHarvestEnabled && farmingSkill.isAutoHarvestUnlocked();
    const completedPlots = [];
    let totalFarmXp = 0;
    const farmItemGains = {};
    let totalHarvestCycles = 0;

    // Get talent bonuses once (XP bonus applied by addXP(), not here)
    const lootBonus = getTalentLootBonus(SKILLS.FARMING);
    const abundantYieldBonus = getTalentFlatBonus(SKILLS.FARMING, 'abundantYieldBonus');
    const herbYieldBonus = getTalentFlatBonus(SKILLS.FARMING, 'herbYieldBonus');
    const treeYieldBonus = getTalentFlatBonus(SKILLS.FARMING, 'treeYieldBonus');
    const cropYieldBonus = getTalentFlatBonus(SKILLS.FARMING, 'cropYieldBonus');
    const secondHarvestChance = getTalentProcChance(SKILLS.FARMING, 'secondHarvest');
    const seedReclaimChance = getTalentProcChance(SKILLS.FARMING, 'seedReclaim');
    const bonusSeedChance = getTalentProcChance(SKILLS.FARMING, 'bonusSeedDrop');
    const hasAutoReplant = getTalentFlatBonus(SKILLS.FARMING, 'autoReplant') > 0 && farmSettings.autoPlantEnabled !== false;
    const freeSeedChance = getTalentProcChance(SKILLS.FARMING, 'freeSeed');

    // Resolve specific seed for auto-plant if mode is 'specific'
    const useSpecificSeed = hasAutoReplant && farmSettings.autoPlantMode === 'specific'
        && farmSettings.autoPlantCropType && farmSettings.autoPlantCropId;
    const specificCropData = useSpecificSeed ? farmingSkill.getCropData(farmSettings.autoPlantCropType, farmSettings.autoPlantCropId) : null;

    // Growth time reduction talents (for calculating replant duration)
    const growthTimeReduction = getTalentFlatBonus(SKILLS.FARMING, 'growthTimeReduction');
    const verdantGrowthReduction = getTalentFlatBonus(SKILLS.FARMING, 'verdantGrowthReduction');
    const treeGrowthReduction = getTalentFlatBonus(SKILLS.FARMING, 'treeGrowthReduction');
    const fastGrowthChance = getTalentProcChance(SKILLS.FARMING, 'fastGrowth');

    // Get tool loot multiplier
    const toolLootMultiplier = farmingSkill.getLootMultiplier();

    // Track virtual seed inventory for multi-cycle simulation
    // Start with current inventory + bloomhold seeds
    const virtualSeeds = {};

    /**
     * Get available seeds for a crop (inventory + bloomhold + virtual gains)
     */
    const getAvailableSeeds = (seedId) => {
        const inventorySeeds = gameState.getItemCount(seedId);
        const bloomholdSeeds = gameState.isBloomholdUnlocked() ? gameState.getBloomholdItemCount(seedId) : 0;
        const virtualGains = virtualSeeds[seedId] || 0;
        return inventorySeeds + bloomholdSeeds + virtualGains;
    };

    /**
     * Calculate effective growth time for a crop type
     */
    const getEffectiveGrowthTime = (baseTime, cropType) => {
        let time = baseTime;
        if (growthTimeReduction > 0) time *= (1 - growthTimeReduction);
        if (verdantGrowthReduction > 0) time *= (1 - verdantGrowthReduction);
        if (cropType === 'tree' && treeGrowthReduction > 0) time *= (1 - treeGrowthReduction);
        // Average in fastGrowth proc (statistically reduces time)
        if (fastGrowthChance > 0) time *= (1 - fastGrowthChance * 0.5);
        return Math.floor(time);
    };

    /**
     * Simulate one harvest cycle for a plot
     */
    const simulateHarvest = (cropData, cropType) => {
        // Apply skill-specific XP bonuses (talent XP bonus applied by addXP())
        const baseXp = cropData.xp || 0;
        let xpMult = 1;
        if (cropType === 'tree') {
            const farmingXPBonus = getTalentFlatBonus(SKILLS.WOODCUTTING, 'farmingXPBonus');
            if (farmingXPBonus > 0) xpMult *= (1 + farmingXPBonus);
        }
        totalFarmXp += Math.floor(baseXp * xpMult);

        // Calculate yield multiplier based on crop type (type bonuses apply before tool mult)
        let talentYieldMult = 1 + lootBonus + abundantYieldBonus;
        if (cropType === 'herb') talentYieldMult += herbYieldBonus;
        else if (cropType === 'tree') talentYieldMult += treeYieldBonus;
        else if (cropType === 'crop') talentYieldMult += cropYieldBonus;
        const yieldMultiplier = toolLootMultiplier * talentYieldMult;

        // Add base drops with yield multiplier
        for (const drop of (cropData.drops || [])) {
            const boostedQty = Math.floor(drop.quantity * yieldMultiplier);
            farmItemGains[drop.itemId] = (farmItemGains[drop.itemId] || 0) + boostedQty;
        }

        // Roll for secondHarvest proc (double harvest)
        if (secondHarvestChance > 0 && Math.random() < secondHarvestChance) {
            for (const drop of (cropData.drops || [])) {
                const boostedQty = Math.floor(drop.quantity * yieldMultiplier);
                farmItemGains[drop.itemId] = (farmItemGains[drop.itemId] || 0) + boostedQty;
            }
        }

        // Roll for seedReclaim proc (return seed)
        if (seedReclaimChance > 0 && cropData.seedId && Math.random() < seedReclaimChance) {
            const seedCost = cropData.seedCost || 1;
            farmItemGains[cropData.seedId] = (farmItemGains[cropData.seedId] || 0) + seedCost;
            virtualSeeds[cropData.seedId] = (virtualSeeds[cropData.seedId] || 0) + seedCost;
        }

        // Roll for bonusSeedDrop proc (Abundant Fields capstone)
        if (bonusSeedChance > 0 && cropData.seedId && Math.random() < bonusSeedChance) {
            farmItemGains[cropData.seedId] = (farmItemGains[cropData.seedId] || 0) + 1;
            virtualSeeds[cropData.seedId] = (virtualSeeds[cropData.seedId] || 0) + 1;
        }

        totalHarvestCycles++;
    };

    const now = Date.now();

    for (let i = 0; i < FARM_CONFIG.MAX_PLOTS; i++) {
        const plot = plots[i];
        if (!plot || !plot.unlocked) continue;
        if (plot.state !== 'growing' && plot.state !== 'ready') continue;

        const cropData = plot.cropData;
        const cropType = plot.cropType;
        const seedId = cropData?.seedId;
        const seedCost = cropData?.seedCost || 1;

        // Plots already in 'ready' state completed before logout
        const alreadyReady = plot.state === 'ready';

        // Check if plot completed during offline time
        let timeInPlot = alreadyReady ? Infinity : (now - plot.plantedAt);
        if (!alreadyReady && timeInPlot < plot.growthDuration) continue; // Not ready yet

        // First harvest always happens (it was planted before going offline)
        completedPlots.push({
            plotIndex: i,
            cropData: cropData,
            cropType: cropType,
            cropId: plot.cropId,
            autoHarvested: autoHarvestEnabled
        });

        if (autoHarvestEnabled && cropData) {
            simulateHarvest(cropData, cropType);
            // For 'ready' plots, use full elapsed time for replant cycles
            // For 'growing' plots, subtract the initial growth duration
            timeInPlot = alreadyReady ? (now - (plot.plantedAt || now)) : (timeInPlot - plot.growthDuration);

            // Simulate additional cycles if auto-replant is active
            if (hasAutoReplant) {
                // Use specific seed data if mode is 'specific', otherwise use original crop
                const replantData = specificCropData || cropData;
                const replantType = specificCropData ? farmSettings.autoPlantCropType : cropType;
                const replantSeedId = replantData?.seedId;
                const replantSeedCost = replantData?.seedCost || 1;

                if (replantSeedId) {
                    const effectiveGrowthTime = getEffectiveGrowthTime(replantData.growthTime, replantType);

                    // Keep harvesting while we have time and seeds
                    while (timeInPlot >= effectiveGrowthTime) {
                        // Check if we have seeds to replant
                        const availableSeeds = getAvailableSeeds(replantSeedId);
                        if (availableSeeds < replantSeedCost) break; // Out of seeds

                        // Consume seeds (unless freeSeed proc)
                        const freeSeed = freeSeedChance > 0 && Math.random() < freeSeedChance;
                        if (!freeSeed) {
                            // Consume from virtual pool first (represents harvested seeds)
                            if ((virtualSeeds[replantSeedId] || 0) >= replantSeedCost) {
                                virtualSeeds[replantSeedId] -= replantSeedCost;
                                // Also reduce farmItemGains since we're using them
                                const newSeedQty = (farmItemGains[replantSeedId] || 0) - replantSeedCost;
                                if (newSeedQty > 0) {
                                    farmItemGains[replantSeedId] = newSeedQty;
                                } else {
                                    delete farmItemGains[replantSeedId];
                                }
                            }
                            // Otherwise it will consume from inventory/bloomhold when applied
                        }

                        // Harvest this cycle (use the replant crop data)
                        simulateHarvest(replantData, replantType);
                        timeInPlot -= effectiveGrowthTime;
                    }
                }
            }
        }
    }

    if (completedPlots.length === 0) return null;

    // Track final plot states for replanting
    const finalPlotStates = [];
    if (hasAutoReplant) {
        for (const plotInfo of completedPlots) {
            // Use specific seed if mode is 'specific', otherwise use original crop
            const replantData = specificCropData || plotInfo.cropData;
            const replantType = specificCropData ? farmSettings.autoPlantCropType : plotInfo.cropType;
            const replantId = specificCropData ? farmSettings.autoPlantCropId : plotInfo.cropId;
            const seedId = replantData?.seedId;
            const seedCost = replantData?.seedCost || 1;
            if (seedId && getAvailableSeeds(seedId) >= seedCost) {
                finalPlotStates.push({
                    plotIndex: plotInfo.plotIndex,
                    cropType: replantType,
                    cropId: replantId,
                    seedId: seedId,
                    seedCost: seedCost
                });
            }
        }
    }

    logger.debug(`[OFFLINE] Farm: ${totalHarvestCycles} harvest cycles across ${completedPlots.length} plots`);

    // Count BloomHold operations for architecting XP
    // BloomHold XP is awarded for each item deposit when auto-harvest destination is 'bloomhold'
    let bloomholdOperations = 0;
    if (autoHarvestEnabled && farmSettings.autoHarvestDestination === 'bloomhold' && gameState.isBloomholdUnlocked()) {
        // Each harvest item deposit counts as a BloomHold operation
        // Approximate: one operation per item type deposited per harvest cycle
        const itemTypes = Object.keys(farmItemGains).filter(itemId => isBloomholdEligible(itemId));
        bloomholdOperations = itemTypes.length * totalHarvestCycles;
    }

    const bloomholdArchXp = calculateBloomholdArchXP(bloomholdOperations);

    return {
        completedPlots,
        autoHarvestEnabled,
        autoHarvestDestination: farmSettings.autoHarvestDestination,
        farmXp: totalFarmXp,
        farmItemGains,
        plotsToReplant: finalPlotStates,
        hasAutoReplant,
        totalHarvestCycles,
        // Architecting XP from Realm Steward talent
        bloomholdArchXp,
        bloomholdOperations
    };
}

/**
 * Calculate guild (pilferer) progress during offline time
 * Pilferers gather items to the guild chest independently
 * Respects chest capacity (stops when full unless Arch 99)
 * @returns {object|null} Guild offline progress data
 */
function calculateGuildOfflineProgress() {
    // Check if guild is unlocked
    if (!gameState.isGuildUnlocked()) return null;

    const activePilferers = gameState.getActivePilferers();
    if (!activePilferers || activePilferers.length === 0) return null;

    const pilferingLevel = gameState.getSkill(SKILLS.PILFERING)?.level || 1;
    const chestFull = gameState.isGuildChestFull();
    const hasAutoLoot = gameState.hasAutoLootChests(); // Architect's Sanctum enables auto-loot

    // If chest is already full and no auto-loot, no progress
    if (chestFull && !hasAutoLoot) return null;

    // Calculate gather duration using pilferingSkill helper
    const gatherDuration = pilferingSkill.getGatherDuration(pilferingLevel, false);

    // Calculate pilferer XP per action
    const xpPerAction = Math.floor(PILFERER_XP_CONFIG.BASE_XP + (pilferingLevel * PILFERER_XP_CONFIG.SCALE_PER_LEVEL));

    // Calculate how many items can fit in chest (or unlimited with auto-loot)
    const chestSlots = gameState.getMaxGuildChestSlots();
    const chestItems = gameState.getGuildChest();
    const maxStack = gameState.getGuildChestMaxStack();

    // Count current chest usage
    let currentItemCount = 0;
    for (const slot of chestItems) {
        if (slot.itemId) {
            currentItemCount += slot.quantity;
        }
    }

    // With auto-loot, items go to bank instead of chest - no capacity limit
    const maxChestCapacity = hasAutoLoot ? Infinity : chestSlots * maxStack;
    const remainingCapacity = Math.max(0, maxChestCapacity - currentItemCount);

    // Track items gathered and XP earned
    const itemsGathered = {};
    let totalXP = 0;
    let totalItemsGathered = 0;

    // Process each active pilferer
    for (const pilferer of activePilferers) {
        if (!pilferer.startedAt) continue;

        const domain = Object.values(GUILD_DOMAINS).find(d => d.id === pilferer.domainId);
        if (!domain) continue;

        // Calculate how many actions this pilferer completed offline
        const elapsed = Date.now() - pilferer.startedAt;
        const actionsCompleted = Math.floor(elapsed / gatherDuration);

        if (actionsCompleted <= 0) continue;

        // Get tier-aware item pool for this domain
        const tierLevel = pilferingSkill.getPilferingTierLevel();
        const availableItems = getDomainItemsAtTier(domain.id, tierLevel);

        // For each action, pick a random item from tier-expanded domain pool
        for (let i = 0; i < actionsCompleted; i++) {
            // Check capacity (unless auto-loot is enabled)
            if (!hasAutoLoot && totalItemsGathered >= remainingCapacity) {
                break; // Chest full, stop this pilferer
            }

            // Pick random item from tier-expanded pool
            const itemId = availableItems[Math.floor(Math.random() * availableItems.length)];
            itemsGathered[itemId] = (itemsGathered[itemId] || 0) + 1;
            totalItemsGathered++;
            totalXP += xpPerAction;
        }
    }

    if (totalItemsGathered === 0) return null;

    return {
        itemsGathered,
        totalItemsGathered,
        pilferingXP: totalXP,
        gatherDuration,
        chestWasFull: !hasAutoLoot && totalItemsGathered >= remainingCapacity
    };
}

/**
 * Calculate pet contract progress during offline time
 * Pet contracts run independently of currentAction (like farm plots)
 * @returns {object|null} Pet contract offline progress data
 */
function calculatePetContractOfflineProgress() {
    const pets = gameState.getPets();
    if (!pets || pets.length === 0) return null;

    const completedContracts = [];
    let totalHusbandryXp = 0;
    let totalGathererXp = 0;
    let totalBountyXp = 0;
    const petItemGains = {};

    // Get talent bonuses for all contracts
    const chestLootBonus = getTalentFlatBonus(SKILLS.HUSBANDRY, 'chestLootBonus');
    const doubleXpProcChance = getTalentProcChance(SKILLS.HUSBANDRY, 'doubleContractXP');
    const giftGiverChance = getTalentProcChance(SKILLS.HUSBANDRY, 'giftGiver');
    const packInstinctChance = getTalentProcChance(SKILLS.HUSBANDRY, 'packInstinct');

    // Track pets that haven't completed yet (for packInstinct boost)
    const incompletePets = [];

    // Helper: process a single contract slot completion
    const processCompletion = (pet, contractId, contract, slot) => {
        completedContracts.push({ petId: pet.id, contractId, contract, slot });

        let contractXp = contract.xp || 0;
        if (doubleXpProcChance > 0) {
            contractXp = Math.floor(contractXp * (1 + doubleXpProcChance));
        }
        totalHusbandryXp += contractXp;

        if (contract.type === 'resource') totalGathererXp += contractXp;
        else if (contract.type === 'combat') totalBountyXp += contractXp;

        const dropMultiplier = husbandrySkill.getPetDropMultiplier(pet.id);
        const totalLootMultiplier = dropMultiplier * (1 + chestLootBonus);
        for (const reward of contract.rewards) {
            if (Math.random() < reward.chance) {
                const baseQty = Math.floor(Math.random() * (reward.maxQty - reward.minQty + 1)) + reward.minQty;
                const finalQty = Math.floor(baseQty * totalLootMultiplier);
                if (finalQty > 0) {
                    petItemGains[reward.itemId] = (petItemGains[reward.itemId] || 0) + finalQty;
                }
            }
        }

        // Roll tier bonus rewards (Beastmaster's Lodge milestones)
        const tierLevel = husbandrySkill.getHusbandryTierLevel();
        if (tierLevel > 0) {
            const tierBonusRewards = getContractTierRewards(contract.type, tierLevel);
            for (const tierReward of tierBonusRewards) {
                if (Math.random() < tierReward.chance) {
                    const baseQty = Math.floor(Math.random() * (tierReward.maxQty - tierReward.minQty + 1)) + tierReward.minQty;
                    const finalQty = Math.floor(baseQty * totalLootMultiplier);
                    if (finalQty > 0) {
                        petItemGains[tierReward.itemId] = (petItemGains[tierReward.itemId] || 0) + finalQty;
                    }
                }
            }
        }

        if (giftGiverChance > 0 && Math.random() < giftGiverChance) {
            const petData = Object.values(PETS).find(p => p.id === pet.id);
            if (petData) {
                const giftItem = husbandrySkill.rollGiftItem(petData.role);
                if (giftItem) {
                    petItemGains[giftItem.itemId] = (petItemGains[giftItem.itemId] || 0) + giftItem.quantity;
                }
            }
        }
    };

    for (const pet of pets) {
        if (!pet.unlocked) continue;

        const speedBonus = husbandrySkill.getPetSpeedBonus(pet.id);

        // Check primary contract
        if (pet.contractId && pet.contractStartedAt) {
            const contract = PET_CONTRACTS[pet.contractId];
            if (contract) {
                const effectiveDuration = contract.duration * (1 - speedBonus);
                const elapsed = Date.now() - pet.contractStartedAt;

                if (elapsed >= effectiveDuration) {
                    processCompletion(pet, pet.contractId, contract, 'primary');
                } else {
                    incompletePets.push({ pet, contractId: pet.contractId, contract, effectiveDuration, elapsed, slot: 'primary' });
                }
            }
        }

        // Check secondary contract
        if (pet.secondaryContractId && pet.secondaryContractStartedAt) {
            const contract = PET_CONTRACTS[pet.secondaryContractId];
            if (contract) {
                const effectiveDuration = contract.duration * (1 - speedBonus);
                const elapsed = Date.now() - pet.secondaryContractStartedAt;

                if (elapsed >= effectiveDuration) {
                    processCompletion(pet, pet.secondaryContractId, contract, 'secondary');
                } else {
                    incompletePets.push({ pet, contractId: pet.secondaryContractId, contract, effectiveDuration, elapsed, slot: 'secondary' });
                }
            }
        }
    }

    // Pack Instinct: completed pets boost ALL incomplete pets by flat 15% progress
    // Online: always fires when talent owned (it's a boost amount, not a probability)
    // Capped at 95% to prevent chain completion cascades (matches online behavior)
    const PACK_INSTINCT_CAP = 0.95;
    if (packInstinctChance > 0 && completedContracts.length > 0 && incompletePets.length > 0) {
        for (const completed of completedContracts) {
            for (let i = incompletePets.length - 1; i >= 0; i--) {
                const { pet, contractId, contract, effectiveDuration, elapsed, slot } = incompletePets[i];
                const currentProgress = elapsed / effectiveDuration;
                const boostedProgress = Math.min(currentProgress + packInstinctChance, PACK_INSTINCT_CAP);
                if (boostedProgress >= 1) {
                    processCompletion(pet, contractId, contract, slot);
                    incompletePets.splice(i, 1);
                } else {
                    // Update elapsed time to reflect the capped boost
                    incompletePets[i].elapsed = boostedProgress * effectiveDuration;
                }
            }
        }
    }

    if (completedContracts.length === 0) return null;

    return {
        completedContracts,
        husbandryXp: totalHusbandryXp,
        gathererXp: totalGathererXp,
        bountyXp: totalBountyXp,
        petItemGains
    };
}

/**
 * Calculate blueprint progress during offline time
 * Blueprints run independently of currentAction (passive system)
 * @returns {object|null} Blueprint offline progress data
 */
function calculateBlueprintOfflineProgress() {
    const activeBlueprints = gameState.getActiveBlueprints();
    if (activeBlueprints.length === 0) return null;

    // Safety: passive blueprints require contractor
    if (!gameState.hasQuestUnlock('contractor_hired')) return null;

    const completedBlueprints = [];
    let totalXp = 0;
    const refundedMaterials = {};

    // Get talent bonuses (XP bonus applied by addXP(), not here)
    const passiveXPBonus = getTalentFlatBonus(SKILLS.ARCHITECTING, 'passiveXPBonus');
    const preserveChance = getProcChance(SKILLS.ARCHITECTING, 'preserveMaterials');

    for (let i = 0; i < activeBlueprints.length; i++) {
        const ab = activeBlueprints[i];
        const elapsed = Date.now() - ab.startedAt;
        if (elapsed >= ab.duration) {
            const blueprint = Object.values(BLUEPRINTS).find(b => b.id === ab.blueprintId);
            if (blueprint) {
                completedBlueprints.push({ blueprint, blueprintId: ab.blueprintId, slotIndex: i });
                // Apply passive XP bonus (talent XP bonus applied by addXP())
                const blueprintXp = Math.floor(blueprint.xp * (1 + passiveXPBonus));
                totalXp += blueprintXp;

                // Simulate preserveMaterials proc (refund materials)
                if (preserveChance > 0 && Math.random() < preserveChance && blueprint.materials) {
                    for (const [itemId, qty] of Object.entries(blueprint.materials)) {
                        refundedMaterials[itemId] = (refundedMaterials[itemId] || 0) + qty;
                    }
                }
            }
        }
    }

    if (completedBlueprints.length === 0) return null;

    return {
        completed: true,
        // Backwards compat: first completed blueprint
        blueprint: completedBlueprints[0].blueprint,
        blueprintId: completedBlueprints[0].blueprintId,
        completedBlueprints,
        xp: totalXp,
        refundedMaterials
    };
}

/**
 * Calculate milestone progress during offline time
 * Milestones run independently of currentAction (passive system)
 * @returns {object|null} Milestone offline progress data
 */
function calculateMilestoneOfflineProgress() {
    const activeMilestone = gameState.getActiveMilestone();
    if (!activeMilestone) return null;

    // Safety: milestones require contractor
    if (!gameState.hasQuestUnlock('contractor_hired')) return null;

    const milestone = Object.values(MILESTONES).find(m => m.id === activeMilestone.milestoneId);
    if (!milestone) return null;

    const elapsed = Date.now() - activeMilestone.startedAt;
    // Use stored duration (may include dual penalty) rather than base buildTime
    if (elapsed >= activeMilestone.duration) {
        return {
            completed: true,
            milestone,
            milestoneId: activeMilestone.milestoneId,
            xp: milestone.xp
        };
    }

    return null;
}

/**
 * Calculate civic contract progress during offline time
 * Civic contracts have multi-phase structure (build/wait phases)
 * Wait phases auto-advance, build phases require player materials
 * @returns {object|null} Civic contract offline progress data
 */
function calculateCivicContractOfflineProgress() {
    const activeContract = gameState.getActiveCivicContract();
    if (!activeContract) return null;

    // Can't progress if waiting for player to start next build phase
    if (activeContract.status === 'ready_for_next') return null;

    const contract = Object.values(CIVIC_CONTRACTS).find(c => c.id === activeContract.contractId);
    if (!contract) return null;

    // Get talent bonuses
    const contractXPBonus = getTalentFlatBonus(SKILLS.ARCHITECTING, 'contractXPBonus');
    const contractGoldBonus = getTalentFlatBonus(SKILLS.ARCHITECTING, 'contractGoldBonus');

    let currentPhaseIndex = activeContract.currentPhase;
    let phaseStartedAt = activeContract.phaseStartedAt;
    let totalPhaseXp = 0;
    const completedPhases = [];
    let contractCompleted = false;
    let finalStatus = null;

    // Simulate phase progression
    while (currentPhaseIndex < contract.phases.length) {
        const phase = contract.phases[currentPhaseIndex];
        const elapsed = Date.now() - phaseStartedAt;

        if (elapsed < phase.duration) {
            // Phase not complete yet, stop here
            break;
        }

        // Phase completed
        if (phase.xp > 0) {
            totalPhaseXp += phase.xp;
        }
        completedPhases.push({ phaseIndex: currentPhaseIndex, phase });

        // Check if this was the last phase
        if (currentPhaseIndex >= contract.phases.length - 1) {
            contractCompleted = true;
            break;
        }

        // Move to next phase
        const nextPhaseIndex = currentPhaseIndex + 1;
        const nextPhase = contract.phases[nextPhaseIndex];

        if (nextPhase.type === 'wait') {
            // Wait phases auto-start after previous phase completes
            phaseStartedAt = phaseStartedAt + phase.duration;
            currentPhaseIndex = nextPhaseIndex;
            // Continue loop to check if this wait phase also completed
        } else {
            // Build phase - requires player materials, can't auto-advance
            finalStatus = 'ready_for_next';
            break;
        }
    }

    if (completedPhases.length === 0) return null;

    // Calculate rewards if contract fully completed
    let completionXp = 0;
    let completionGold = 0;
    let completionItems = [];
    if (contractCompleted) {
        completionXp = Math.floor(contract.rewards.xp * (1 + contractXPBonus));
        completionGold = Math.floor(contract.rewards.gold * (1 + contractGoldBonus));
        completionItems = contract.rewards.items || [];
    }

    return {
        completed: contractCompleted,
        contractId: activeContract.contractId,
        contract,
        completedPhases,
        phaseXp: totalPhaseXp,
        completionXp,
        completionGold,
        completionItems,
        totalXp: totalPhaseXp + completionXp,
        // State update info (for advancing phase if not fully complete)
        newPhaseIndex: contractCompleted ? null : completedPhases[completedPhases.length - 1].phaseIndex + 1,
        newPhaseStartedAt: phaseStartedAt,
        finalStatus
    };
}

// ============================================================
// OFFLINE COMBAT BONUS HELPERS
// These calculate statistical multipliers from equipment, talents,
// and set bonuses for the offline combat simulation.
// ============================================================

/**
 * Calculate equipment special effect bonuses for offline combat.
 * Reads item IDs from savedStats and computes statistical DPS/defense multipliers.
 * @param {object} savedStats - Saved combat stats (includes equippedWeaponId, etc.)
 * @param {object} combatParams - { avgPlayerDamage, attacksPerKill, timeToKill, avgMobAttackSpeed, avgMobDamageAfterDef, mobHitRate, maxHp, hitRate, effectiveAttackSpeed, critRate }
 * @returns {object} Bonuses object
 */
function getEquipmentOfflineBonuses(savedStats, combatParams) {
    const bonuses = {
        dpsMultiplier: 1.0,
        extraDpsFlat: 0,
        healPerKill: 0,
        foodHealMultiplier: 1.0,
        noAmmoNeeded: false,
        extraDefense: 0,
    };

    if (!savedStats) return bonuses;

    const { avgPlayerDamage, attacksPerKill, timeToKill, avgMobAttackSpeed, avgMobDamageAfterDef, mobHitRate, maxHp, hitRate, effectiveAttackSpeed, critRate } = combatParams;
    const attackStyle = savedStats.attackStyle;
    const hitsPerSec = hitRate * 1000 / effectiveAttackSpeed;
    const mobHitsPerSec = mobHitRate * 1000 / avgMobAttackSpeed;

    // --- Pendant effects ---
    const neckItem = savedStats.equippedNeckId ? getItem(savedStats.equippedNeckId) : null;
    if (neckItem?.uniqueEffect) {
        const ue = neckItem.uniqueEffect;
        switch (ue.type) {
            case 'allDamageBonus':
                bonuses.dpsMultiplier *= (1 + ue.value);
                break;
            case 'lifesteal':
                // Heal = lifesteal% * avgDamage * attacksPerKill
                bonuses.healPerKill += Math.floor(ue.value * avgPlayerDamage * attacksPerKill);
                break;
            case 'spellDamageBonus':
                if (attackStyle === 'magic') {
                    bonuses.dpsMultiplier *= (1 + ue.value);
                }
                break;
            case 'berserkerEnhance':
                // +X% damage below 50% HP. Conservatively assume 25% of fight in range.
                bonuses.dpsMultiplier *= (1 + 0.25 * ue.lowHpDamageBonus);
                break;
            // dotDamageBonus (Scorched Talisman): handled in bleed/fire DoT calculations
        }
    }

    // --- Weapon effects ---
    const weaponItem = savedStats.equippedWeaponId ? getItem(savedStats.equippedWeaponId) : null;
    if (weaponItem?.specialEffect) {
        const se = weaponItem.specialEffect;
        switch (se.type) {
            case 'smolderingStrikes':
                // Every Nth hit → +X% bonus damage. Avg boost = X/N
                bonuses.dpsMultiplier *= (1 + se.bonusDamagePercent / se.hitInterval);
                break;
            case 'eclipseSlash':
                // Every Nth hit → +X% bonus damage wave
                bonuses.dpsMultiplier *= (1 + se.bonusDamagePercent / se.hitInterval);
                break;
            case 'searingParry':
                // On mob hit: procChance → counter at counterDamagePercent of attack
                if (timeToKill > 0) {
                    const mobHitsPerKill = mobHitsPerSec * timeToKill / 1000;
                    const counterTotal = se.procChance * mobHitsPerKill * (savedStats.attack * se.counterDamagePercent);
                    bonuses.extraDpsFlat += counterTotal * 1000 / timeToKill;
                }
                break;
            case 'searingArrows': {
                // 15% ignite: 3 dmg per 2s for 6s = 9 total per proc
                const dotTotal = se.dotDamage * (se.dotDuration / se.dotInterval);
                const avgProcsPerKill = attacksPerKill * se.procChance;
                let igniteTotal = avgProcsPerKill * dotTotal;
                // Scorched Talisman DoT bonus
                if (neckItem?.uniqueEffect?.type === 'dotDamageBonus') {
                    igniteTotal *= (1 + neckItem.uniqueEffect.value);
                }
                if (timeToKill > 0) {
                    bonuses.extraDpsFlat += igniteTotal * 1000 / timeToKill;
                }
                break;
            }
            case 'eclipseReaver':
                // +5% lifesteal (crit already in savedStats)
                bonuses.healPerKill += Math.floor(se.lifestealPercent * avgPlayerDamage * attacksPerKill);
                break;
            case 'eclipsePiercer':
                // +10% ranged damage + built-in ammo
                if (attackStyle === 'ranged') {
                    bonuses.dpsMultiplier *= (1 + se.rangedDamageBonus);
                    bonuses.noAmmoNeeded = true;
                }
                break;
            case 'eclipseConduit':
                // +10% spell damage (builtInSpell already factors into magicDamage stat)
                if (attackStyle === 'magic') {
                    bonuses.dpsMultiplier *= (1 + se.spellDamageBonus);
                }
                break;
            // pyreResonance: spell duration bonus — skip for offline (assumed 100% uptime)
        }
    }

    // Check builtInAmmo on weapon directly (e.g., Eclipse Piercer, Practice Bow)
    if (weaponItem?.builtInAmmo && attackStyle === 'ranged') {
        bonuses.noAmmoNeeded = true;
    }

    // --- Ring effects ---
    const ringItem = savedStats.equippedRingId ? getItem(savedStats.equippedRingId) : null;
    if (ringItem?.uniqueEffect) {
        const re = ringItem.uniqueEffect;
        switch (re.type) {
            case 'emberShield': {
                // On mob hit: procChance → +defenseBonus for duration
                // Avg uptime ≈ min(1, procChance * mobHitsPerSec * duration/1000)
                const avgUptime = Math.min(1.0, re.procChance * mobHitsPerSec * re.duration / 1000);
                bonuses.extraDefense += avgUptime * re.defenseBonus;
                break;
            }
            case 'flameBarrier':
                bonuses.foodHealMultiplier *= (1 + re.foodHealBonus);
                break;
            case 'infernosFury':
                // +X% damage to mobs below threshold HP. Avg: threshold% of fight in range.
                bonuses.dpsMultiplier *= (1 + re.executeThreshold * re.executeDamageBonus);
                break;
            case 'relentless': {
                // Crits → speed stacks. Avg stacks = min(max, critHitsPerSec * duration/1000)
                const critHitsPerSec = critRate * hitsPerSec;
                const avgStacks = Math.min(re.maxStacks, critHitsPerSec * re.stackDuration / 1000);
                // Speed bonus reduces attack speed → more DPS. Apply as DPS multiplier.
                const speedPct = avgStacks * (re.speedBonusPerStack / 100);
                if (speedPct > 0) {
                    bonuses.dpsMultiplier *= (1 / (1 - speedPct)); // Faster attacks → more DPS
                }
                break;
            }
            case 'markedPrey': {
                // Crits mark target: +X% damage for duration. Avg uptime = min(1, critHitsPerSec * duration/1000)
                const markCritHitsPerSec = critRate * hitsPerSec;
                const markUptime = Math.min(1.0, markCritHitsPerSec * re.markDuration / 1000);
                bonuses.dpsMultiplier *= (1 + markUptime * re.damageAmplification);
                break;
            }
            case 'arcaneOverflow':
                // +10% all damage during spell effects (assume always active for magic)
                if (attackStyle === 'magic') {
                    bonuses.dpsMultiplier *= (1 + re.baseDamageBonus);
                    // +8% spell damage with 2+ effects (need cascade talent/set)
                    const hasCascade = getProcChance(SKILLS.COMBAT, 'elementalCascade') > 0;
                    const hasSetCascade = savedStats.activeSetBonuses?.pyreweave?.activeTiers?.includes(4);
                    if (hasCascade || hasSetCascade) {
                        bonuses.dpsMultiplier *= (1 + re.multiEffectSpellBonus);
                    }
                }
                break;
        }
    }

    // --- Shield/Offhand effects ---
    const offhandItem = savedStats.equippedOffhandId ? getItem(savedStats.equippedOffhandId) : null;
    if (offhandItem?.specialEffect) {
        const oe = offhandItem.specialEffect;
        switch (oe.type) {
            case 'wardensResolve': {
                // On mob hit: procChance → heal healPercent of maxHP
                if (timeToKill > 0) {
                    const mobHitsPerKill = mobHitsPerSec * timeToKill / 1000;
                    bonuses.healPerKill += Math.floor(oe.procChance * oe.healPercent * maxHp * mobHitsPerKill);
                }
                break;
            }
            case 'eclipseReflection': {
                // 10% damage reflected → bonus mob DPS contribution
                const reflectDps = oe.reflectPercent * avgMobDamageAfterDef * mobHitsPerSec;
                bonuses.extraDpsFlat += reflectDps;
                break;
            }
            case 'smolderingResonance': {
                // 25% proc chance on spell cast → fire DoT (3 dmg × 4 ticks = 12 per proc)
                if (attackStyle === 'magic') {
                    const dotTotal = oe.dotDamage * oe.dotTicks;
                    const avgProcsPerKill = attacksPerKill * oe.procChance;
                    let igniteTotal = avgProcsPerKill * dotTotal;
                    // Scorched Talisman DoT bonus
                    if (neckItem?.uniqueEffect?.type === 'dotDamageBonus') {
                        igniteTotal *= (1 + neckItem.uniqueEffect.value);
                    }
                    if (timeToKill > 0) {
                        bonuses.extraDpsFlat += igniteTotal * 1000 / timeToKill;
                    }
                }
                break;
            }
        }
    }

    // --- Quiver effects ---
    const backItem = savedStats.equippedBackId ? getItem(savedStats.equippedBackId) : null;
    if (backItem?.specialEffect) {
        const qe = backItem.specialEffect;
        if (qe.type === 'volatileArrows' && attackStyle === 'ranged') {
            // 8% bonus fire damage (15% on crit)
            const avgBonusPct = (1 - critRate) * qe.baseBonusDamage + critRate * qe.critBonusDamage;
            bonuses.dpsMultiplier *= (1 + avgBonusPct);
        }
    }

    return bonuses;
}

/**
 * Calculate combat talent bonuses for offline simulation.
 * Handles talents currently marked as "online-only" that need offline support.
 * @param {object} savedStats - Saved combat stats
 * @param {object} combatParams - { avgPlayerDamage, attacksPerKill, timeToKill, avgMobAttackSpeed, avgMobDamageAfterDef, mobHitRate, maxHp, hitRate, effectiveAttackSpeed, critRate }
 * @returns {object} Bonuses object
 */
function getCombatTalentOfflineBonuses(savedStats, combatParams) {
    const bonuses = {
        dpsMultiplier: 1.0,
        extraDpsFlat: 0,
        bleedDpsFlat: 0,  // Tracked separately so Cindermark 2pc multiplier can be applied
        healPerKill: 0,
        maxHpBonus: 0,
        ammoSaveChance: 0,
        damageTakenMultiplier: 1.0,
    };

    if (!savedStats) return bonuses;

    const { avgPlayerDamage, attacksPerKill, timeToKill, avgMobAttackSpeed, avgMobDamageAfterDef, mobHitRate, maxHp, hitRate, effectiveAttackSpeed } = combatParams;
    const attackStyle = savedStats.attackStyle;
    const hitsPerSec = hitRate * 1000 / effectiveAttackSpeed;
    const mobHitsPerSec = mobHitRate * 1000 / avgMobAttackSpeed;
    const neckItem = savedStats.equippedNeckId ? getItem(savedStats.equippedNeckId) : null;

    // --- Melee talents ---
    if (attackStyle === 'melee') {
        // Berserker's Rampage: kills grant +10% damage for 30s, max 5 stacks (+2 with pendant)
        const berserkerVal = getTalentFlatBonus(SKILLS.COMBAT, 'berserkerRampage') || 0;
        if (berserkerVal > 0) {
            const hasBerserkerPendant = neckItem?.uniqueEffect?.type === 'berserkerEnhance';
            const maxStacks = 5 + (hasBerserkerPendant ? 2 : 0);
            // Offline kills are continuous; conservatively assume avg 3 stacks
            const avgStacks = Math.min(maxStacks, 3);
            bonuses.dpsMultiplier *= (1 + avgStacks * 0.10);
        }

        // Whirlwind: 15% chance to hit twice
        const doubleStrikeChance = getProcChance(SKILLS.COMBAT, 'doubleStrike');
        if (doubleStrikeChance > 0) {
            bonuses.dpsMultiplier *= (1 + doubleStrikeChance);
        }

        // Deep Wounds: 35% chance to apply bleed (5 dmg/tick × 5 ticks per rank)
        const meleeBleed = getTalentFlatBonus(SKILLS.COMBAT, 'meleeBleed') || 0;
        const meleeBleedChance = getTalentFlatBonus(SKILLS.COMBAT, 'meleeBleedChance') || 0;
        if (meleeBleed > 0 && meleeBleedChance > 0) {
            let totalBleedDmg = meleeBleed * 5; // 5 ticks per bleed
            // Scorched Talisman: +20% DoT damage
            if (neckItem?.uniqueEffect?.type === 'dotDamageBonus') {
                totalBleedDmg *= (1 + neckItem.uniqueEffect.value);
            }
            const avgBleedDmgPerHit = meleeBleedChance * totalBleedDmg;
            bonuses.bleedDpsFlat += avgBleedDmgPerHit * hitsPerSec;
        }

        // Executioner: +25% damage to enemies below 25% HP
        const executeBonus = getTalentFlatBonus(SKILLS.COMBAT, 'executeBonus') || 0;
        if (executeBonus > 0) {
            bonuses.dpsMultiplier *= (1 + 0.25 * executeBonus);
        }
    }

    // --- Ranged talents ---
    if (attackStyle === 'ranged') {
        // Barbed Arrows: 35% chance to apply bleed (4 dmg/tick × 5 ticks per rank)
        const rangedBleed = getTalentFlatBonus(SKILLS.COMBAT, 'rangedBleed') || 0;
        const rangedBleedChance = getTalentFlatBonus(SKILLS.COMBAT, 'rangedBleedChance') || 0;
        if (rangedBleed > 0 && rangedBleedChance > 0) {
            let totalBleedDmg = rangedBleed * 5;
            // Scorched Talisman DoT bonus
            if (neckItem?.uniqueEffect?.type === 'dotDamageBonus') {
                totalBleedDmg *= (1 + neckItem.uniqueEffect.value);
            }
            const avgBleedDmgPerHit = rangedBleedChance * totalBleedDmg;
            bonuses.bleedDpsFlat += avgBleedDmgPerHit * hitsPerSec;
        }

        // Ammo Conservation: 10% per rank chance to save arrow
        bonuses.ammoSaveChance = getProcChance(SKILLS.COMBAT, 'ammoSave');

        // Multi-Shot: 20% chance to fire 2 extra arrows (no ammo cost)
        const multiShotChance = getProcChance(SKILLS.COMBAT, 'multiShot');
        if (multiShotChance > 0) {
            bonuses.dpsMultiplier *= (1 + multiShotChance * 2);
        }

        // Arrow Storm: 10% chance to fire 5 arrows (4 extra)
        const arrowStormChance = getProcChance(SKILLS.COMBAT, 'arrowStorm');
        if (arrowStormChance > 0) {
            bonuses.dpsMultiplier *= (1 + arrowStormChance * 4);
        }
    }

    // --- Magic talents ---
    if (attackStyle === 'magic') {
        // Elemental Cascade: 25% chance to cast 2nd spell at 50% power
        const cascadeChance = getProcChance(SKILLS.COMBAT, 'elementalCascade');
        if (cascadeChance > 0) {
            bonuses.dpsMultiplier *= (1 + cascadeChance * 0.50);
        }

        // Spell Echo: every 3rd spell casts twice (counter-based)
        const hasSpellEcho = getTalentFlatBonus(SKILLS.COMBAT, 'spellEcho') || 0;
        if (hasSpellEcho > 0) {
            bonuses.dpsMultiplier *= (1 + 1 / 3);
        }
    }

    // --- Survival talents (all styles) ---
    // Vital Force: +5 max HP per rank
    // NOTE: NOT added here — gameState.getMaxHp() already includes Vital Force talent bonus
    // Adding it here would double-count since maxHp at line 2614 uses getMaxHp()

    // Regeneration: heal 1 HP every 5 seconds
    const regenVal = getTalentFlatBonus(SKILLS.COMBAT, 'combatRegen') || 0;
    if (regenVal > 0 && timeToKill > 0) {
        bonuses.healPerKill += Math.floor(regenVal * timeToKill / 5000);
    }

    // Thorn Armor: reflect 10% damage taken per rank
    const thornVal = getTalentFlatBonus(SKILLS.COMBAT, 'thornDamage') || 0;
    if (thornVal > 0) {
        bonuses.extraDpsFlat += avgMobDamageAfterDef * thornVal * mobHitsPerSec;
    }

    return bonuses;
}

/**
 * Calculate set bonus effects for offline combat simulation.
 * @param {object} savedStats - Saved combat stats (includes activeSetBonuses)
 * @param {object} combatParams - Combat calculation parameters
 * @returns {object} Bonuses object
 */
function getSetBonusOfflineBonuses(savedStats, combatParams) {
    const bonuses = {
        dpsMultiplier: 1.0,
        extraDpsFlat: 0,
        healPerKill: 0,
        damageTakenMultiplier: 1.0,
        bleedDpsMultiplier: 1.0,
    };

    if (!savedStats?.activeSetBonuses) return bonuses;

    const { avgPlayerDamage, attacksPerKill, timeToKill, avgMobAttackSpeed, avgMobDamageAfterDef, mobHitRate, maxHp, hitRate, effectiveAttackSpeed, critRate } = combatParams;
    const activeSets = savedStats.activeSetBonuses;
    const attackStyle = savedStats.attackStyle;
    const hitsPerSec = hitRate * 1000 / effectiveAttackSpeed;
    const mobHitsPerSec = mobHitRate * 1000 / avgMobAttackSpeed;

    // --- Ashforged (melee) ---
    if (activeSets.ashforged && attackStyle === 'melee') {
        const tiers = activeSets.ashforged.activeTiers || [];

        // 2pc Momentum: +2% damage per stack, max 10. Avg ≈ min(10, attacksPerKill/2)
        if (tiers.includes(2)) {
            const avgMomentum = Math.min(10, attacksPerKill > 20 ? 8 : attacksPerKill / 2);
            const momentumData = SET_BONUSES.ashforged.bonuses[2];
            bonuses.dpsMultiplier *= (1 + avgMomentum * momentumData.damagePerStack);
        }

        // 4pc Devastating Blow: at max momentum (10), 20% chance → 4x dmg + 50% lifesteal
        if (tiers.includes(4)) {
            const devData = SET_BONUSES.ashforged.bonuses[4];
            // Fraction of hits at max momentum
            const hitsAtMax = Math.max(0, attacksPerKill - 10);
            const fractionAtMax = attacksPerKill > 0 ? hitsAtMax / attacksPerKill : 0;
            // Extra DPS: fractionAtMax * procChance * (multiplier - 1)
            bonuses.dpsMultiplier *= (1 + fractionAtMax * devData.procChance * (devData.damageMultiplier - 1));
            // Lifesteal from Devastating Blow procs
            if (avgPlayerDamage > 0 && attacksPerKill > 0) {
                const devBlowProcs = hitsAtMax * devData.procChance;
                bonuses.healPerKill += Math.floor(devBlowProcs * avgPlayerDamage * devData.damageMultiplier * devData.lifestealPercent);
            }
        }

        // 6pc Weapon Mastery
        if (tiers.includes(6)) {
            if (savedStats.isTwoHanded) {
                // Afterimage: clone at 50% damage for 10s, 30s cooldown. Uptime = 10/(10+30) = 25%
                const afterData = SET_BONUSES.ashforged.bonuses[6].afterimage;
                bonuses.dpsMultiplier *= (1 + (afterData.duration / (afterData.duration + afterData.cooldown)) * afterData.damagePercent);
            } else {
                // Riposte: every 3rd hit taken → counter at 75% damage
                const riposteData = SET_BONUSES.ashforged.bonuses[6].riposte;
                if (timeToKill > 0) {
                    const mobHitsPerKill = mobHitsPerSec * timeToKill / 1000;
                    const ripostes = Math.floor(mobHitsPerKill / riposteData.hitInterval);
                    const riposteDmg = ripostes * savedStats.attack * riposteData.damagePercent;
                    bonuses.extraDpsFlat += riposteDmg * 1000 / timeToKill;
                }
            }
        }
    }

    // --- Cindermark (ranged) ---
    if (activeSets.cindermark && attackStyle === 'ranged') {
        const tiers = activeSets.cindermark.activeTiers || [];

        // 2pc Barbed Acceleration: bleeds tick 2x as fast → 2x bleed DPS
        // Applied to talent bleedDpsFlat during bonus merge
        if (tiers.includes(2)) {
            bonuses.bleedDpsMultiplier = SET_BONUSES.cindermark.bonuses[2].bleedSpeedMultiplier;
        }

        // 4pc Piercing Shot: every 5th hit → 3x damage ignoring defense
        if (tiers.includes(4)) {
            const pierceData = SET_BONUSES.cindermark.bonuses[4];
            // Raw damage ignoring defense vs normal post-defense damage
            const rawPierceDmg = savedStats.attack * pierceData.damageMultiplier;
            const pierceExtra = rawPierceDmg - avgPlayerDamage;
            if (pierceExtra > 0 && attacksPerKill > 0) {
                // Extra damage per cycle of 5 hits
                bonuses.extraDpsFlat += (pierceExtra / pierceData.hitInterval) * hitsPerSec;
            }
        }

        // 6pc Phantom Archer: after each Piercing Shot → 3 shots at 60% damage
        // Online: phantom shots use raw baseDamage (stats.attack), bypassing mob defense
        if (tiers.includes(6) && tiers.includes(4)) {
            const phantomData = SET_BONUSES.cindermark.bonuses[6];
            const pierceInterval = SET_BONUSES.cindermark.bonuses[4].hitInterval;
            // Piercing shots per kill ≈ attacksPerKill / pierceInterval
            const pierceCount = attacksPerKill / pierceInterval;
            // Use raw attack (savedStats.attack) to match online behavior where phantom shots bypass defense
            const phantomDmgPerPierce = phantomData.shotCount * savedStats.attack * phantomData.shotDamagePercent;
            if (timeToKill > 0) {
                bonuses.extraDpsFlat += (pierceCount * phantomDmgPerPierce) * 1000 / timeToKill;
            }
        }
    }

    // --- Pyreweave (magic) ---
    if (activeSets.pyreweave && attackStyle === 'magic') {
        const tiers = activeSets.pyreweave.activeTiers || [];

        // 2pc Spell Echo + 4pc Arcane Cascade: compute holistically to avoid compounding
        // Online behavior: main spell (100%) + echo (25% at 75%) + cascade 3rd (50%) + echo→cascade (25% × 50%)
        if (tiers.includes(4) && tiers.includes(2)) {
            const echoData = SET_BONUSES.pyreweave.bonuses[2];
            const cascadeData = SET_BONUSES.pyreweave.bonuses[4];
            const echoContrib = echoData.echoChance * echoData.echoPowerPercent;      // 0.1875
            const cascadeContrib = cascadeData.thirdSpellPower;                        // 0.50
            const echoCascadeContrib = echoData.echoChance * cascadeData.thirdSpellPower; // 0.125
            bonuses.dpsMultiplier *= (1 + echoContrib + cascadeContrib + echoCascadeContrib);
        } else if (tiers.includes(2)) {
            // Only 2pc echo active
            const echoData = SET_BONUSES.pyreweave.bonuses[2];
            bonuses.dpsMultiplier *= (1 + echoData.echoChance * echoData.echoPowerPercent);
        }

        // 6pc Spell Fusion: combo effects when 2+ spell effects active
        // Online: 6 possible fusions based on which 2+ spell elements overlap
        // Offline: average across all possible element combinations, weighted by expected fusion count
        if (tiers.includes(6)) {
            const hasCascadeTalent = getProcChance(SKILLS.COMBAT, 'elementalCascade') > 0;
            const hasSetCascade = tiers.includes(4);
            if (hasCascadeTalent || hasSetCascade) {
                const fusions = SET_BONUSES.pyreweave.bonuses[6].fusions;
                const hasStrongCascade = hasCascadeTalent && hasSetCascade;

                // Fusion uptime: how often 2+ spell effects are simultaneously active
                // With cascade talent only: ~60% (2nd element has limited uptime)
                // With 4pc + talent: ~70% (3rd element always fires, keeping overlaps high)
                const fusionUptime = hasStrongCascade ? 0.70 : 0.60;

                // Model each fusion's DPS contribution (% boost) when active:
                // Shatter (fire+ice): next hit 2x every 3s → extra hit per cooldown cycle
                const shatterBoost = Math.min(1.0, effectiveAttackSpeed / fusions.shatter.cooldown);
                // Magma (fire+earth): -15% mob defense → ~8% DPS from reduced defense
                const magmaBoost = 0.08;
                // Firestorm (fire+air): fire DoT ticks 50% faster + 30% more damage
                // Fire DoT is ~15% of total magic DPS → enhanced by 95% = ~14% of DoT → ~2-3% overall
                const firestormBoost = 0.15 * ((1 + fusions.firestorm.tickSpeedBonus) * (1 + fusions.firestorm.damageBonus) - 1);
                // Hailstorm (ice+air): +8% all damage from all sources
                const hailstormBoost = fusions.hailstorm.value;
                // Harmony (earth+air): buffs 40% stronger → ~5% DPS from enhanced crit/effects
                const harmonyDpsBoost = 0.05;
                // Permafrost (ice+earth): no direct DPS boost (only reduces damage taken)

                // Damage reduction contributions:
                // Permafrost: mob -25% attack speed → 20% less effective damage
                const permafrostReduction = 1 - (1 / (1 + fusions.permafrost.attackSpeedReduction));
                // Harmony: defense buff 40% stronger → ~8% less damage taken
                const harmonyDmgReduction = 0.08;

                // Average across all 6 fusions (each equally likely based on random element combos)
                // With 3 elements: C(3,2)=3 active fusions from C(4,3)=4 possible element combos
                // With 2 elements: 1 active fusion
                const expectedFusions = hasStrongCascade ? 3 : 1;

                const avgDpsPerFusion = (shatterBoost + magmaBoost + firestormBoost + hailstormBoost + harmonyDpsBoost) / 6;
                const avgDmgReductionPerFusion = (permafrostReduction + harmonyDmgReduction) / 6;

                bonuses.dpsMultiplier *= (1 + expectedFusions * avgDpsPerFusion * fusionUptime);
                bonuses.damageTakenMultiplier *= (1 - Math.min(0.30, expectedFusions * avgDmgReductionPerFusion * fusionUptime));
            }
        }
    }

    return bonuses;
}

/**
 * Calculate boss mechanic adjustments for offline simulation.
 * Statistically models enrage timers, phase transitions, healing, DoTs,
 * and damage floor checks to produce multipliers for the combat loop.
 *
 * @param {string} mobId - Boss mob ID
 * @param {number} estimatedTTK - Estimated time to kill in ms (pre-boss adjustments)
 * @param {Object} mob - Full mob data object
 * @returns {Object|null} Adjustments or null if not a boss
 */
function getBossOfflineAdjustments(mobId, estimatedTTK, mob) {
    const mechanics = getBossMechanics(mobId);
    if (!mechanics) return null;

    const result = {
        canWin: true,
        damageFloor: mechanics.damageFloor || 0,
        effectiveHpMultiplier: 1.0,       // Increases effective mob HP (healing, defense phases)
        mobDamageMultiplier: 1.0,          // Boss deals more damage (enrage, damage phases)
        mobSpeedMultiplier: 1.0,           // Boss attacks faster (enrage speed, phase speed)
        playerDpsMultiplier: 1.0,          // Player deals less damage (Void Barrier)
        extraPlayerDamagePerFight: 0,      // Extra damage to player per fight (DoTs, true damage)
        defenseIgnorePercent: 0,           // Mob ignores % of player defense (Cindered Knight)
    };

    // === ENRAGE ===
    // If fight lasts longer than enrage timer, boss gets stronger for the remainder.
    // Weight the normal and enraged portions of the fight.
    // Multi-stage enrage: iterate stages to find the highest applicable stage.
    if (mechanics.enrage && mechanics.enrage.stages) {
        const stages = mechanics.enrage.stages;
        // Find the highest stage reached within the estimated TTK
        let activeStage = null;
        for (let i = stages.length - 1; i >= 0; i--) {
            if (estimatedTTK > stages[i].timer) {
                activeStage = stages[i];
                break;
            }
        }

        if (activeStage) {
            // Cap enraged fraction at 80% to avoid runaway (iterative refinement would fix this)
            const enragedFraction = Math.min(0.80, (estimatedTTK - activeStage.timer) / estimatedTTK);
            const normalFraction = 1 - enragedFraction;

            const enrageDmg = activeStage.damageMult || 1.0;
            const enrageSpd = activeStage.speedMult || 1.0;

            result.mobDamageMultiplier = normalFraction * 1.0 + enragedFraction * enrageDmg;
            // speedMult < 1 = faster attacks (lower interval), so mob DPS increases
            result.mobSpeedMultiplier = normalFraction * 1.0 + enragedFraction * enrageSpd;
        }
    }

    // === PHASES ===
    // Phases are HP-threshold based. Weight each phase's modifiers by the fraction of
    // total HP spent in that phase.
    if (mechanics.phases && mechanics.phases.length > 1) {
        const sorted = [...mechanics.phases].sort((a, b) => b.hpThreshold - a.hpThreshold);

        let weightedDmgMult = 0;
        let weightedSpeedMult = 0;
        let weightedDefBonus = 0;
        let weightedPlayerDpsReduction = 0;

        for (let i = 0; i < sorted.length; i++) {
            const phase = sorted[i];
            const nextThreshold = i + 1 < sorted.length ? sorted[i + 1].hpThreshold : 0;
            const weight = phase.hpThreshold - nextThreshold;
            const mods = phase.mobMods || {};

            weightedDmgMult += (mods.damageMult || 1.0) * weight;
            weightedSpeedMult += (mods.speedMult || 1.0) * weight;
            weightedDefBonus += (mods.defenseBonus || 0) * weight;

            // Defense ignore (e.g., Cindered Knight below 40%)
            if (mods.ignoreDefensePercent) {
                result.defenseIgnorePercent += mods.ignoreDefensePercent * weight;
            }

            // Player effects (DoTs, true damage, damage reduction)
            if (phase.playerEffects) {
                for (const effect of phase.playerEffects) {
                    if (effect.type === 'damage_reduction') {
                        // Player deals less damage (e.g., Void Barrier -25%)
                        weightedPlayerDpsReduction += effect.value * weight;
                    }
                    if (effect.type === 'dot' || effect.type === 'true_dot') {
                        // Damage to player per second during this phase
                        const dps = effect.damage / (effect.interval / 1000);
                        const timeInPhaseMs = weight * estimatedTTK;
                        result.extraPlayerDamagePerFight += dps * (timeInPhaseMs / 1000);
                    }
                }
            }
        }

        // Apply phase-weighted modifiers (multiply with enrage, since both can apply)
        result.mobDamageMultiplier *= weightedDmgMult;
        result.mobSpeedMultiplier *= weightedSpeedMult;
        result.playerDpsMultiplier *= (1 - weightedPlayerDpsReduction);

        // Extra defense increases effective HP (harder to damage the boss)
        if (weightedDefBonus !== 0 && mob.defense > 0) {
            // Defense increase as fraction of base: reduces player damage proportionally
            // Using MOB defense formula: reduction = def / (def + 50) — matches online combat.js
            const baseDmgReduction = mob.defense / (mob.defense + 50);
            const adjustedDef = mob.defense + weightedDefBonus;
            const newDmgReduction = adjustedDef / (adjustedDef + 50);
            // Effective HP multiplier: (1 - baseDmgReduction) / (1 - newDmgReduction)
            const passthrough = (1 - baseDmgReduction);
            const newPassthrough = (1 - newDmgReduction);
            if (newPassthrough > 0) {
                result.effectiveHpMultiplier *= passthrough / newPassthrough;
            }
        }
    }

    // === PERIODIC EFFECTS (Boss Healing + True Damage to Player) ===
    if (mechanics.periodicEffects) {
        let totalHealingPerFight = 0;

        // Use adjusted TTK estimate for periodic effect calculations
        const adjustedTTK = estimatedTTK * result.effectiveHpMultiplier / Math.max(0.1, result.playerDpsMultiplier);

        for (const effect of mechanics.periodicEffects) {
            if (effect.type === 'heal_mob') {
                if (effect.activePhase) {
                    // Healing only in specific phase(s)
                    const phase = mechanics.phases?.find(p => p.id === effect.activePhase);
                    if (phase) {
                        const sorted = [...mechanics.phases].sort((a, b) => b.hpThreshold - a.hpThreshold);
                        const phaseIdx = sorted.findIndex(p => p.id === effect.activePhase);
                        const nextThreshold = phaseIdx + 1 < sorted.length ? sorted[phaseIdx + 1].hpThreshold : 0;
                        const phaseWeight = phase.hpThreshold - nextThreshold;
                        const phaseTimeMs = phaseWeight * adjustedTTK;
                        const heals = Math.floor(phaseTimeMs / effect.interval);
                        totalHealingPerFight += heals * effect.amount;

                        // Intensified healing in later phase (e.g., Molten Colossus)
                        if (effect.intensify) {
                            const intPhase = mechanics.phases?.find(p => p.id === effect.intensify.phase);
                            if (intPhase) {
                                const intIdx = sorted.findIndex(p => p.id === effect.intensify.phase);
                                const intNext = intIdx + 1 < sorted.length ? sorted[intIdx + 1].hpThreshold : 0;
                                const intWeight = intPhase.hpThreshold - intNext;
                                const intTimeMs = intWeight * adjustedTTK;
                                const intHeals = Math.floor(intTimeMs / effect.interval);
                                totalHealingPerFight += intHeals * effect.intensify.amount;
                            }
                        }
                    }
                } else {
                    // Healing active in all phases
                    const heals = Math.floor(adjustedTTK / effect.interval);
                    totalHealingPerFight += heals * effect.amount;
                }
            }

            if (effect.type === 'true_damage_player') {
                // True damage ignores player defense
                if (effect.activePhase) {
                    const phase = mechanics.phases?.find(p => p.id === effect.activePhase);
                    if (phase) {
                        const sorted = [...mechanics.phases].sort((a, b) => b.hpThreshold - a.hpThreshold);
                        const phaseIdx = sorted.findIndex(p => p.id === effect.activePhase);
                        const nextThreshold = phaseIdx + 1 < sorted.length ? sorted[phaseIdx + 1].hpThreshold : 0;
                        const phaseWeight = phase.hpThreshold - nextThreshold;
                        const phaseTimeMs = phaseWeight * adjustedTTK;
                        const procs = Math.floor(phaseTimeMs / effect.interval);
                        result.extraPlayerDamagePerFight += procs * effect.amount;
                    }
                } else {
                    const procs = Math.floor(adjustedTTK / effect.interval);
                    result.extraPlayerDamagePerFight += procs * effect.amount;
                }
            }
        }

        // Boss healing increases effective HP
        if (totalHealingPerFight > 0 && mob.hp > 0) {
            result.effectiveHpMultiplier *= (1 + totalHealingPerFight / mob.hp);
        }
    }

    return result;
}

/**
 * Calculate statistical mob mechanic adjustments for offline combat.
 * Averages mechanic effects across all mobs to approximate online combat difficulty.
 * @param {string[]} mobIds - Mob IDs to check mechanics for
 * @param {object} params - Combat parameters for calculations
 * @returns {object} Averaged adjustments: playerDpsMult, effectiveHpMult, extraDamagePerFight, mobDpsMult, defenseReduction
 */
function getMobMechanicOfflineAdjustments(mobIds, params) {
    const { avgPlayerDamage, timeToKill, avgMobAttackSpeed, avgMobHp, effectiveAttackSpeed } = params;
    const count = mobIds.length;
    if (count === 0) return { playerDpsMult: 1, effectiveHpMult: 1, extraDamagePerFight: 0, mobDpsMult: 1, defenseReduction: 0 };

    let totalDpsMult = 0;
    let totalHpMult = 0;
    let totalExtraDamage = 0;
    let totalMobDpsMult = 0;
    let totalDefReduction = 0;

    for (const mobId of mobIds) {
        const mechanic = getMobMechanic(mobId);
        let dpsMult = 1, hpMult = 1, extraDamage = 0, mobDpsMult = 1, defReduction = 0;

        if (mechanic) {
            const mob = getMob(mobId);
            const mobSpeed = mob?.attackSpeed || avgMobAttackSpeed;
            const numMobAttacks = Math.max(1, Math.floor(timeToKill / mobSpeed));
            const numPlayerAttacks = Math.max(1, Math.floor(timeToKill / effectiveAttackSpeed));
            const mobHp = mob?.hp || avgMobHp;

            switch (mechanic.type) {
                case 'snare':
                    // Slows player attack speed; uptime = chance * duration / mobAttackInterval
                    dpsMult = 1 - Math.min(1, mechanic.chance * mechanic.duration / mobSpeed) * mechanic.slowPercent;
                    break;
                case 'fireDot':
                case 'poison':
                    // Constant DoT after first hit (refreshes on each hit)
                    extraDamage = Math.floor(timeToKill / mechanic.interval) * mechanic.damage;
                    break;
                case 'curse':
                    // chance per hit to reduce player defense for duration
                    defReduction = Math.min(1, mechanic.chance * mechanic.duration / mobSpeed) * mechanic.defenseReduction;
                    break;
                case 'armorShred':
                    // Stacking defense reduction, ramps up during fight
                    defReduction = Math.min(mechanic.maxStacks, numMobAttacks) * 0.5 * mechanic.defensePerStack;
                    break;
                case 'selfHeal':
                    // Mob heals on attack, increasing effective HP
                    hpMult = 1 + (numMobAttacks * mechanic.chance * mechanic.healAmount / mobHp);
                    break;
                case 'enrage':
                    // Mob attacks faster below HP threshold
                    mobDpsMult = 1 + (mechanic.hpThreshold * mechanic.speedBoost);
                    break;
                case 'phaseShift':
                    // Mob dodges player attacks
                    dpsMult = 1 - mechanic.chance;
                    break;
                case 'absorption':
                    // Mob heals portion of damage dealt to player
                    hpMult = 1 + (numPlayerAttacks * Math.min(avgPlayerDamage * mechanic.healPercent, mechanic.healCap) / mobHp);
                    break;
                case 'timeWarp':
                    // Random attack speed; compare average to base
                    mobDpsMult = mobSpeed / ((mechanic.minSpeed + mechanic.maxSpeed) / 2);
                    break;
                case 'damageReflection':
                    // Reflects damage back to player
                    extraDamage = numPlayerAttacks * Math.min(avgPlayerDamage * mechanic.reflectPercent, mechanic.reflectCap);
                    break;
            }
        }

        totalDpsMult += dpsMult;
        totalHpMult += hpMult;
        totalExtraDamage += extraDamage;
        totalMobDpsMult += mobDpsMult;
        totalDefReduction += defReduction;
    }

    return {
        playerDpsMult: totalDpsMult / count,
        effectiveHpMult: totalHpMult / count,
        extraDamagePerFight: totalExtraDamage / count,
        mobDpsMult: totalMobDpsMult / count,
        defenseReduction: totalDefReduction / count
    };
}

/**
 * Calculate combat progress during offline time
 * Simulates HP exchange combat with auto-eat support
 * @param {number} offlineMs - Time offline in milliseconds
 * @param {object} savedAction - The saved combat action (has zoneId and savedStats)
 * @returns {object|null} Combat offline progress data
 */
function calculateCombatOfflineProgress(offlineMs, savedAction) {
    const combat = gameState.getCombat();

    // Get zone from savedAction first, fallback to combat state
    const zoneId = savedAction?.zoneId || combat.currentZone;

    // Only calculate if we have a zone and player isn't dead
    if (!zoneId || combat.isDead) return null;

    const zone = getZone(zoneId);
    if (!zone) return null;

    // Get player's Pyre level for visibility checks and modifiers
    const pyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;

    // Check if zone is visible (for Pyre Veil hidden zones)
    if (!isZoneVisible(zoneId, pyreLevel)) {
        logger.debug(`[OFFLINE COMBAT] Zone ${zoneId} not visible at Pyre level ${pyreLevel}`);
        return null;
    }

    // Calculate Pyre penalty/bonus for this zone
    // Below zone.pyreLevel: penalty (up to -50% damage, +50% slower, -50% loot)
    // Above zone.pyreLevel: bonus (up to +10% damage, -10% faster, +10% loot)
    let pyreDamageMult = 1.0;
    let pyreSpeedMult = 1.0;
    let pyreLootMult = 1.0;

    if (zone.pyreLevel) {
        if (pyreLevel < zone.pyreLevel) {
            // Below requirement - penalty
            const deficit = (zone.pyreLevel - pyreLevel) / zone.pyreLevel;
            pyreDamageMult = 1 - (deficit * 0.5);  // Up to -50% damage
            pyreSpeedMult = 1 + (deficit * 0.5);   // Up to +50% slower
            pyreLootMult = 1 - (deficit * 0.5);    // Up to -50% loot
            logger.debug(`[OFFLINE COMBAT] Pyre penalty: ${(deficit * 100).toFixed(0)}% deficit, dmg=${pyreDamageMult.toFixed(2)}, speed=${pyreSpeedMult.toFixed(2)}`);
        } else {
            // Above requirement - bonus (up to 10% at level 99)
            const maxPyre = 99;
            const levelsAbove = pyreLevel - zone.pyreLevel;
            const maxLevelsAbove = maxPyre - zone.pyreLevel;
            const bonus = maxLevelsAbove > 0 ? (levelsAbove / maxLevelsAbove) * 0.10 : 0;
            pyreDamageMult = 1 + bonus;   // Up to +10% damage
            pyreSpeedMult = 1 - bonus;    // Up to -10% faster
            pyreLootMult = 1 + bonus;     // Up to +10% loot
            if (bonus > 0) {
                logger.debug(`[OFFLINE COMBAT] Pyre bonus: ${(bonus * 100).toFixed(0)}%, dmg=${pyreDamageMult.toFixed(2)}, speed=${pyreSpeedMult.toFixed(2)}`);
            }
        }
    }

    // Get player stats at time of save
    const combatLevel = Math.min(gameState.getSkill(SKILLS.COMBAT)?.level || 1, 99);
    const maxHp = gameState.getMaxHp();
    let currentHp = combat.currentHp;

    // Use saved stats from when combat started (if available), otherwise fallback to current gear
    const savedStats = savedAction?.savedStats;
    let playerAttack, playerDefense, attackSpeed, attackStyle, playerAccuracy, staffMagicDamage;

    if (savedStats) {
        // Use the stats saved when combat started
        playerAttack = savedStats.attack || 1;
        playerDefense = savedStats.defense || 0;
        attackSpeed = savedStats.attackSpeed || COMBAT_CONFIG.BASE_ATTACK_SPEED;
        attackStyle = savedStats.attackStyle || 'melee';
        playerAccuracy = savedStats.accuracy || 50;
        // For magic, we need to estimate staff damage from the saved attack value
        staffMagicDamage = 1.0; // Will be calculated from spell damage scaling

        logger.debug(`[OFFLINE COMBAT] Using saved stats - Style: ${attackStyle}, Attack: ${playerAttack}, Defense: ${playerDefense}, Speed: ${attackSpeed}ms`);
    } else {
        // Fallback: Calculate from current gear (might be wrong if equipment not restored)
        const activeSetId = gameState.getActiveEquipmentSet?.() || 'melee';
        const gear = gameState.getEquippedGear();
        logger.debug(`[OFFLINE COMBAT] No saved stats, using gear - Active set: ${activeSetId}, Weapon: ${gear.weapon || 'none'}`);

        playerAttack = Math.floor(combatLevel * 0.5) + 1;
        playerDefense = Math.floor(combatLevel * 0.3);
        attackSpeed = COMBAT_CONFIG.BASE_ATTACK_SPEED;
        attackStyle = 'melee';
        staffMagicDamage = 1.0;

        if (gear.weapon) {
            const weapon = getItem(gear.weapon);
            if (weapon) {
                playerAttack += weapon.attackBonus || 0;
                if (weapon.attackSpeed) attackSpeed = weapon.attackSpeed;
                if (weapon.attackStyle) attackStyle = weapon.attackStyle;
                if (weapon.magicDamage) staffMagicDamage = weapon.magicDamage;
            }
        }

        const armorSlots = ['head', 'neck', 'shoulders', 'hands', 'body', 'legs', 'ring', 'feet', 'offhand', 'ammo'];
        for (const slot of armorSlots) {
            if (gear[slot]) {
                const armor = getItem(gear[slot]);
                if (armor) {
                    if (armor.defenseBonus) playerDefense += armor.defenseBonus;
                    if (armor.attackBonus) playerAttack += armor.attackBonus;
                }
            }
        }

        playerAccuracy = Math.min(95, 50 + combatLevel * 0.5);
    }

    // Get magic spell info if using magic
    let activeSpell = null;
    let glyphsAvailable = 0;
    let glyphCostPerCast = 0;
    let glyphIdUsed = null;
    if (attackStyle === 'magic') {
        // Use saved spell from action, fallback to current selected spell
        const spellId = savedStats?.selectedSpell || gameState.getSelectedSpell();
        logger.debug(`[OFFLINE COMBAT] Magic spell: ${spellId || 'none'}`);
        if (spellId) {
            activeSpell = getSpell(spellId);
            if (activeSpell && activeSpell.glyphCost) {
                // Count available glyphs (simplified - assume first glyph type)
                const firstGlyph = activeSpell.glyphCost[0];
                if (firstGlyph) {
                    // Apply Arcane Efficiency talent (reduces glyph cost)
                    const glyphReduction = getTalentFlatBonus(SKILLS.COMBAT, 'glyphCostReduction') || 0;
                    glyphCostPerCast = Math.max(1, Math.floor(firstGlyph.quantity * (1 - glyphReduction)));
                    glyphIdUsed = firstGlyph.glyphId;

                    // Use saved glyph count from when combat started (prevents exploits)
                    if (savedStats?.glyphCount !== undefined) {
                        glyphsAvailable = savedStats.glyphCount;
                        logger.debug(`[OFFLINE COMBAT] Using saved glyph count: ${glyphsAvailable}`);
                    } else {
                        // Fallback: count from current inventory (may be inaccurate if not loaded)
                        if (gameState.isGlyphPouchUnlocked?.()) {
                            const glyphPouch = gameState.getGlyphPouch?.();
                            if (glyphPouch?.slots) {
                                for (const slot of glyphPouch.slots) {
                                    if (slot && slot.itemId === firstGlyph.glyphId) {
                                        glyphsAvailable += slot.quantity;
                                    }
                                }
                            }
                        }
                        glyphsAvailable += gameState.getItemCount(firstGlyph.glyphId);
                        logger.debug(`[OFFLINE COMBAT] Counted glyphs from inventory: ${glyphsAvailable}`);
                    }
                    logger.debug(`[OFFLINE COMBAT] Glyphs available: ${glyphsAvailable}, cost per cast: ${glyphCostPerCast}`);
                }
            }
        }
    }

    // Get ranged ammo info if using ranged
    let arrowsAvailable = 0;
    if (attackStyle === 'ranged') {
        // Use saved arrow count from when combat started (prevents exploits)
        if (savedStats?.arrowCount !== undefined) {
            arrowsAvailable = savedStats.arrowCount;
            logger.debug(`[OFFLINE COMBAT] Using saved arrow count: ${arrowsAvailable}`);
        } else {
            // Fallback: check equipped ammo (may be inaccurate if not loaded)
            const equippedAmmo = gameState.getEquippedAmmo?.();
            if (equippedAmmo && equippedAmmo.itemId) {
                arrowsAvailable = equippedAmmo.quantity || 0;
                logger.debug(`[OFFLINE COMBAT] Checked equipped ammo: ${arrowsAvailable}`);
            }
        }
    }

    // Get auto-eat config - use saved config from when combat started if available
    let autoEatEnabled = false;
    let autoEatThreshold = 50;
    let storedFoodQty = 0;
    let storedFoodHeal = 0;
    let storedFoodId = null;

    if (savedStats?.autoEat) {
        // Use saved auto-eat config (prevents issues with gameState not being loaded)
        const savedAutoEat = savedStats.autoEat;
        autoEatEnabled = savedAutoEat.enabled && savedAutoEat.storedFoodQty > 0 && (savedAutoEat.storedFoodHeal || 0) > 0; // enabled = unlocked + has food (no toggle)
        autoEatThreshold = savedAutoEat.threshold || 50;
        storedFoodQty = savedAutoEat.storedFoodQty || 0;
        // Use total heal (includes HoT from empowered food) if available, otherwise use instant heal
        storedFoodHeal = savedAutoEat.storedFoodTotalHeal || savedAutoEat.storedFoodHeal || 0;
        storedFoodId = savedAutoEat.storedFoodId;
        logger.debug(`[OFFLINE COMBAT] Using saved auto-eat config: enabled=${autoEatEnabled}, threshold=${autoEatThreshold}%, food=${storedFoodQty}x (heals ${storedFoodHeal})`);
    } else {
        // Fallback to current gameState (may be inaccurate if not loaded)
        const autoEat = gameState.getAutoEat();
        storedFoodQty = autoEat.storedFood?.quantity || 0;
        storedFoodId = autoEat.storedFood?.itemId;
        if (storedFoodId) {
            const foodItem = getItem(storedFoodId);
            const instantHeal = foodItem?.healAmount || 0;
            // Include HoT total for empowered food (amount per tick * ticks)
            const hotTotal = foodItem?.hot ? (foodItem.hot.amount * foodItem.hot.ticks) : 0;
            storedFoodHeal = instantHeal + hotTotal;
        }
        autoEatEnabled = autoEat.unlocked && storedFoodQty > 0 && storedFoodHeal > 0;
        autoEatThreshold = autoEat.threshold || 50;
        logger.debug(`[OFFLINE COMBAT] Using gameState auto-eat (may be inaccurate): enabled=${autoEatEnabled}`);
    }

    // Apply food healing talent bonuses (Cooking foodHealBonus + Combat Medic)
    if (storedFoodHeal > 0) {
        const cookingHealBonus = getTalentFlatBonus(SKILLS.COOKING, 'foodHealBonus') || 0;
        const combatFoodHealBonus = getTalentFlatBonus(SKILLS.COMBAT, 'foodHealing') || 0;
        storedFoodHeal = Math.floor((storedFoodHeal + cookingHealBonus) * (1 + combatFoodHealBonus));

        // Apply equipment food heal bonus (Flame Barrier ring: +20%)
        if (savedStats?.equippedRingId) {
            const ringForFood = getItem(savedStats.equippedRingId);
            if (ringForFood?.uniqueEffect?.type === 'flameBarrier') {
                storedFoodHeal = Math.floor(storedFoodHeal * (1 + ringForFood.uniqueEffect.foodHealBonus));
            }
        }
    }

    // Get visible mobs for this zone (filters out Pyre Veil hidden mobs)
    const visibleMobs = getVisibleMobsForZone(zoneId, pyreLevel);
    if (visibleMobs.length === 0) {
        logger.debug(`[OFFLINE COMBAT] No visible mobs in zone ${zoneId} at Pyre level ${pyreLevel}`);
        return null;
    }

    // Calculate average mob stats for the zone (only visible mobs)
    let totalMobHp = 0;
    let totalMobAttack = 0;
    let totalMobDefense = 0;
    let totalMobAccuracy = 0;
    let totalMobAttackSpeed = 0;
    let totalMobXp = 0;
    let totalMarkAccuracy = 0;
    let totalMarkDefIgnore = 0;
    let mobCount = 0;

    for (const mobId of visibleMobs) {
        const mob = getMob(mobId);
        if (mob) {
            totalMobHp += mob.hp;
            totalMobAttack += (mob.attack + (mob.attackMax || mob.attack)) / 2;
            totalMobDefense += mob.defense;
            totalMobAccuracy += mob.accuracy || 70;
            totalMobAttackSpeed += mob.attackSpeed;
            totalMobXp += mob.xp;
            // Bounty mark bonuses (accuracy and defense ignore)
            const mark = getBountyMark(mobId);
            totalMarkAccuracy += mark.accuracy;
            totalMarkDefIgnore += mark.defIgnore;
            mobCount++;
        }
    }

    if (mobCount === 0) return null;

    const avgMobHp = totalMobHp / mobCount;
    const avgMobAttack = totalMobAttack / mobCount;
    const avgMobDefense = totalMobDefense / mobCount;
    const avgMobAccuracy = totalMobAccuracy / mobCount;
    const avgMobAttackSpeed = totalMobAttackSpeed / mobCount;
    const avgMobXp = totalMobXp / mobCount;
    const avgMarkAccuracy = totalMarkAccuracy / mobCount;
    const avgMarkDefIgnore = totalMarkDefIgnore / mobCount;

    // Calculate average weakness/resistance modifiers
    // Style weakness: +25% accuracy, +20% damage | Resistance: -35% accuracy, -25% damage
    // Elemental weakness (magic only): +20% damage if spell element matches
    let avgStyleAccuracyMod = 1.0;
    let avgStyleDamageMod = 1.0;
    let avgElementalDamageMod = 1.0;

    // Check if target farming a specific mob or fighting random visible mobs in zone
    const targetMobId = savedStats?.targetMob;
    const mobsToCheck = targetMobId ? [targetMobId] : visibleMobs;

    let styleModCount = 0;
    let elementalModCount = 0;
    let weakMobCount = 0;
    let resistMobCount = 0;

    for (const mobId of mobsToCheck) {
        const mob = getMob(mobId);
        if (!mob) continue;

        // Style weakness/resistance
        if (mob.weakTo === attackStyle) {
            avgStyleAccuracyMod += 0.25; // +25% accuracy
            avgStyleDamageMod += 0.20;   // +20% damage
            weakMobCount++;
        } else if (mob.resistantTo === attackStyle) {
            avgStyleAccuracyMod -= 0.35; // -35% accuracy
            avgStyleDamageMod -= 0.25;   // -25% damage
            resistMobCount++;
        }
        styleModCount++;

        // Elemental weakness (magic only) - base 20% + talent bonus
        if (attackStyle === 'magic' && activeSpell?.element && mob.elementalWeakness) {
            if (mob.elementalWeakness.includes(activeSpell.element)) {
                const weaknessBonus = getTalentFlatBonus(SKILLS.COMBAT, 'weaknessBonus') || 0;
                avgElementalDamageMod += 0.20 + weaknessBonus;
            }
        }
        elementalModCount++;
    }

    // Average the modifiers across all mobs (or use single mob's modifiers if target farming)
    if (styleModCount > 1) {
        avgStyleAccuracyMod = 1.0 + (avgStyleAccuracyMod - 1.0) / styleModCount;
        avgStyleDamageMod = 1.0 + (avgStyleDamageMod - 1.0) / styleModCount;
    }
    if (elementalModCount > 1) {
        avgElementalDamageMod = 1.0 + (avgElementalDamageMod - 1.0) / elementalModCount;
    }

    // Clamp modifiers to reasonable bounds
    avgStyleAccuracyMod = Math.max(0.5, Math.min(1.5, avgStyleAccuracyMod));
    avgStyleDamageMod = Math.max(0.5, Math.min(1.5, avgStyleDamageMod));
    avgElementalDamageMod = Math.max(1.0, Math.min(1.5, avgElementalDamageMod));

    logger.debug(`[OFFLINE COMBAT] Weakness mods - Style: acc=${avgStyleAccuracyMod.toFixed(2)}, dmg=${avgStyleDamageMod.toFixed(2)}, Elemental: ${avgElementalDamageMod.toFixed(2)}`);

    // Calculate player damage based on attack style
    // Using 1-maxHit roll system: average damage = (1 + maxHit) / 2
    let avgPlayerDamage;
    // New defense formula: def/(def+50) for stronger defense scaling
    // Apply bounty mark defense ignore (flat subtraction, floored at 0)
    const effectiveMobDefense = Math.max(0, avgMobDefense - avgMarkDefIgnore);
    const damageReduction = effectiveMobDefense / (effectiveMobDefense + 50);
    // Apply style accuracy modifier to hit rate (includes bounty mark accuracy bonus)
    const baseHitRate = Math.min(0.95, ((playerAccuracy + avgMarkAccuracy) / 100) * avgStyleAccuracyMod);

    // Mob evasion: def/(def+200) capped at 35%, modified by weakness/resistance ratio
    let mobEvasionChance = Math.min(0.35, effectiveMobDefense / (effectiveMobDefense + 200));
    // Use weak/resist mob counts for evasion modifier (more reliable than averaging accuracy mods)
    const totalStyleMobs = weakMobCount + resistMobCount;
    if (totalStyleMobs > 0) {
        if (weakMobCount > resistMobCount) {
            // Majority weak to player's style - harder to evade
            mobEvasionChance *= 0.50;
        } else if (resistMobCount > weakMobCount) {
            // Majority resistant to player's style - easier to evade
            mobEvasionChance = Math.min(0.50, mobEvasionChance * 1.40);
        }
    }
    // Effective hit rate = accuracy * (1 - mob evasion)
    const hitRate = baseHitRate * (1 - mobEvasionChance);

    logger.debug(`[OFFLINE COMBAT] Mob evasion: ${(mobEvasionChance * 100).toFixed(1)}%, Base hit: ${(baseHitRate * 100).toFixed(1)}%, Effective hit: ${(hitRate * 100).toFixed(1)}%`);

    // Get crit stats for average damage calculation
    const critChance = savedStats?.critChance || 5; // Base 5% if not saved
    const critDamage = savedStats?.critDamage || 50; // Base 50% if not saved
    const spellCritBonus = savedStats?.spellCritBonus || 0; // Air spell crit bonus

    // Calculate average crit multiplier: 1 + (chance * bonus)
    // E.g., 10% crit + 50% damage = 1 + (0.10 * 0.50) = 1.05 (5% avg damage increase)
    const totalCritChance = attackStyle === 'magic' ? critChance + spellCritBonus : critChance;
    const avgCritMultiplier = 1 + (totalCritChance / 100) * (critDamage / 100);

    // Combat talent damage bonuses
    const allDamageBonus = getTalentFlatBonus(SKILLS.COMBAT, 'allDamage') || 0;
    let talentDamageMult = 1 + allDamageBonus;
    if (attackStyle === 'melee') {
        talentDamageMult *= 1 + (getTalentFlatBonus(SKILLS.COMBAT, 'meleeDamage') || 0);
    } else if (attackStyle === 'ranged') {
        talentDamageMult *= 1 + (getTalentFlatBonus(SKILLS.COMBAT, 'rangedDamage') || 0);
    } else if (attackStyle === 'magic') {
        talentDamageMult *= 1 + (getTalentFlatBonus(SKILLS.COMBAT, 'spellDamage') || 0);
    }

    if (attackStyle === 'magic' && activeSpell) {
        // Magic: spell base damage * staff multiplier * level scaling
        const spellDamage = activeSpell.baseDamage * staffMagicDamage * (1 + combatLevel * 0.005);
        // Add DoT damage if fire spell
        const dotDamage = activeSpell.effect?.type === 'dot' ?
            (activeSpell.effect.damage * activeSpell.effect.ticks) : 0;
        // Magic uses mob's magic defense (half of regular defense) with new formula: def/(def+50)
        const magicDefReduction = (effectiveMobDefense * 0.5) / ((effectiveMobDefense * 0.5) + 50);
        // Calculate max hit before rolling
        const magicMaxHit = Math.max(1, Math.floor((spellDamage + dotDamage) * (1 - magicDefReduction) * avgStyleDamageMod * avgElementalDamageMod * pyreDamageMult * talentDamageMult));
        // 1-maxHit roll average = (1 + maxHit) / 2, then apply crit multiplier
        avgPlayerDamage = Math.max(1, Math.floor(((1 + magicMaxHit) / 2) * avgCritMultiplier));
    } else {
        // Melee/Ranged: calculate max hit with all modifiers
        const physicalMaxHit = Math.max(1, Math.floor(playerAttack * (1 - damageReduction) * avgStyleDamageMod * pyreDamageMult * talentDamageMult));
        // 1-maxHit roll average = (1 + maxHit) / 2, then apply crit multiplier
        avgPlayerDamage = Math.max(1, Math.floor(((1 + physicalMaxHit) / 2) * avgCritMultiplier));
    }

    logger.debug(`[OFFLINE COMBAT] Crit: ${totalCritChance}% chance, ${critDamage}% damage, avg multiplier: ${avgCritMultiplier.toFixed(3)}`);

    // Calculate player DPS (apply Pyre speed modifier to attack speed)
    const effectiveAttackSpeed = attackSpeed * pyreSpeedMult;
    let playerDps = (avgPlayerDamage * hitRate * 1000) / effectiveAttackSpeed;

    // Calculate time to kill one mob (in ms)
    let timeToKill = (avgMobHp / playerDps) * 1000;

    logger.debug(`[OFFLINE COMBAT] Avg damage: ${avgPlayerDamage}, DPS: ${playerDps.toFixed(2)}, TTK: ${(timeToKill/1000).toFixed(1)}s, Mob HP: ${avgMobHp}`);
    logger.debug(`[OFFLINE COMBAT] Time available: ${(offlineMs * OFFLINE_CONFIG.EFFICIENCY / 1000).toFixed(1)}s`);

    // Calculate mob DPS against player
    // Evasion: def/(def+500) capped at 60% - reduces effective mob hit rate
    const evasionChance = Math.min(0.60, playerDefense / (playerDefense + 500));
    // Damage reduction: def/(def+800) - smaller reduction, hits hurt more
    const playerDamageReduction = playerDefense / (playerDefense + 800);
    const avgMobDamageAfterDef = Math.max(1, Math.floor(avgMobAttack * (1 - playerDamageReduction)));
    // Mob effective hit rate = base accuracy * (1 - evasion chance)
    const mobHitRate = (avgMobAccuracy / 100) * (1 - evasionChance);
    const mobDps = (avgMobDamageAfterDef * mobHitRate * 1000) / avgMobAttackSpeed;

    logger.debug(`[OFFLINE COMBAT] Defense: ${playerDefense}, Evasion: ${(evasionChance * 100).toFixed(1)}%, DmgReduce: ${(playerDamageReduction * 100).toFixed(1)}%, MobDmg: ${avgMobDamageAfterDef}, MobHitRate: ${(mobHitRate * 100).toFixed(1)}%`);

    // Calculate damage taken per fight (initial estimate before bonus adjustments)
    let damagePerFight = Math.floor(mobDps * timeToKill / 1000);

    // Calculate attacks needed per kill (initial estimate before bonus adjustments)
    let attacksPerKill = Math.ceil(avgMobHp / avgPlayerDamage);

    // --- Apply equipment, talent, and set bonus adjustments ---
    // These helpers compute statistical DPS multipliers from proc effects,
    // equipment specials, combat talents (previously online-only), and set bonuses.
    const critRate = totalCritChance / 100;
    const combatParams = {
        avgPlayerDamage,
        attacksPerKill,
        timeToKill,
        avgMobAttackSpeed,
        avgMobDamageAfterDef,
        mobHitRate,
        maxHp,
        hitRate,
        effectiveAttackSpeed,
        critRate,
    };

    const equipBonuses = getEquipmentOfflineBonuses(savedStats, combatParams);
    const talentBonuses = getCombatTalentOfflineBonuses(savedStats, combatParams);
    const setBonuses = getSetBonusOfflineBonuses(savedStats, combatParams);

    // Merge all bonus sources
    const combinedDpsMult = equipBonuses.dpsMultiplier * talentBonuses.dpsMultiplier * setBonuses.dpsMultiplier;
    // Apply Cindermark 2pc bleed acceleration multiplier to bleed DPS before merging
    const effectiveBleedDps = (talentBonuses.bleedDpsFlat || 0) * (setBonuses.bleedDpsMultiplier || 1);
    const combinedExtraDps = equipBonuses.extraDpsFlat + talentBonuses.extraDpsFlat + effectiveBleedDps + setBonuses.extraDpsFlat;
    const combinedHealPerKill = equipBonuses.healPerKill + talentBonuses.healPerKill + setBonuses.healPerKill;
    const combinedMaxHpBonus = talentBonuses.maxHpBonus || 0;
    const combinedAmmoSave = talentBonuses.ammoSaveChance || 0;
    const noAmmoNeeded = equipBonuses.noAmmoNeeded || false;
    const combinedDamageTakenMult = (talentBonuses.damageTakenMultiplier || 1) * (setBonuses.damageTakenMultiplier || 1);
    const effectiveExtraDefense = equipBonuses.extraDefense || 0;

    // Apply DPS multipliers and recalculate combat values
    if (combinedDpsMult !== 1.0 || combinedExtraDps > 0) {
        playerDps = playerDps * combinedDpsMult + combinedExtraDps;
        timeToKill = playerDps > 0 ? (avgMobHp / playerDps) * 1000 : timeToKill;
        attacksPerKill = Math.ceil(avgMobHp / (avgPlayerDamage * combinedDpsMult));
    }

    // Apply damage-taken reduction from extra defense and effects
    if (effectiveExtraDefense > 0 || combinedDamageTakenMult !== 1.0) {
        const adjustedDefense = playerDefense + effectiveExtraDefense;
        const adjustedReduction = adjustedDefense / (adjustedDefense + 200);
        const adjustedMobDmg = Math.max(1, Math.floor(avgMobAttack * (1 - adjustedReduction)));
        const adjustedMobDps = (adjustedMobDmg * mobHitRate * 1000 / avgMobAttackSpeed) * combinedDamageTakenMult;
        damagePerFight = Math.floor(adjustedMobDps * timeToKill / 1000);
    } else {
        damagePerFight = Math.floor(mobDps * timeToKill / 1000);
    }

    // Effective max HP with talent bonus (Vital Force)
    const effectiveMaxHp = maxHp + combinedMaxHpBonus;

    logger.debug(`[OFFLINE COMBAT] Bonus adjustments: dpsMult=${combinedDpsMult.toFixed(3)}, extraDps=${combinedExtraDps.toFixed(1)}, healPerKill=${combinedHealPerKill}, maxHpBonus=${combinedMaxHpBonus}, ammoSave=${(combinedAmmoSave*100).toFixed(0)}%`);

    // === MOB MECHANIC OFFLINE ADJUSTMENTS ===
    // Apply statistical mechanic effects (snare, DoT, phase shift, etc.) averaged across visible mobs
    const mechAdj = getMobMechanicOfflineAdjustments(mobsToCheck, {
        avgPlayerDamage, timeToKill, avgMobAttackSpeed, avgMobHp, effectiveAttackSpeed
    });

    // Apply player DPS reduction (snare, phase shift)
    if (mechAdj.playerDpsMult < 1.0) {
        playerDps *= mechAdj.playerDpsMult;
        timeToKill = playerDps > 0 ? (avgMobHp / playerDps) * 1000 : timeToKill;
    }

    // Apply effective HP increase (self-heal, absorption)
    if (mechAdj.effectiveHpMult > 1.0) {
        const mechEffectiveHp = avgMobHp * mechAdj.effectiveHpMult;
        timeToKill = playerDps > 0 ? (mechEffectiveHp / playerDps) * 1000 : timeToKill;
    }

    // Apply mob DPS increase (enrage, time warp)
    if (mechAdj.mobDpsMult > 1.0) {
        damagePerFight = Math.floor(damagePerFight * mechAdj.mobDpsMult);
    }

    // Apply extra damage to player (DoTs, damage reflection)
    if (mechAdj.extraDamagePerFight > 0) {
        damagePerFight += Math.floor(mechAdj.extraDamagePerFight);
    }

    // Apply defense reduction (curse, armor shred) as extra damage
    if (mechAdj.defenseReduction > 0) {
        const totalDef = playerDefense + effectiveExtraDefense;
        const reducedDef = Math.max(0, totalDef - mechAdj.defenseReduction);
        const baseReduction = totalDef / (totalDef + 800);
        const mechReduction = reducedDef / (reducedDef + 800);
        // Extra damage per mob hit from lowered defense
        const extraDmgPerHit = Math.floor(avgMobAttack * (baseReduction - mechReduction));
        if (extraDmgPerHit > 0) {
            const mechMobAttacks = Math.max(1, Math.floor(timeToKill / avgMobAttackSpeed));
            damagePerFight += Math.floor(extraDmgPerHit * mechMobAttacks * mobHitRate);
        }
    }

    logger.debug(`[OFFLINE COMBAT] Mob mechanics: dpsMult=${mechAdj.playerDpsMult.toFixed(3)}, hpMult=${mechAdj.effectiveHpMult.toFixed(3)}, mobDpsMult=${mechAdj.mobDpsMult.toFixed(3)}, extraDmg=${mechAdj.extraDamagePerFight.toFixed(0)}, defReduce=${mechAdj.defenseReduction.toFixed(1)}`);

    // === BOSS MECHANICS ADJUSTMENTS ===
    // If target-farming a boss, apply statistical boss mechanic modifiers
    // (enrage, phases, healing, DoTs, damage floor)
    let bossAdjustments = null;
    if (targetMobId) {
        const targetMob = getMob(targetMobId);
        if (targetMob?.isBoss) {
            bossAdjustments = getBossOfflineAdjustments(targetMobId, timeToKill, targetMob);

            if (bossAdjustments) {
                // Apply player DPS reduction (e.g., Void Barrier reduces player damage by 25%)
                if (bossAdjustments.playerDpsMultiplier < 1.0) {
                    playerDps *= bossAdjustments.playerDpsMultiplier;
                }

                // Apply effective HP multiplier (healing + defense phases make boss harder to kill)
                const effectiveBossHp = targetMob.hp * bossAdjustments.effectiveHpMultiplier;
                timeToKill = playerDps > 0 ? (effectiveBossHp / playerDps) * 1000 : timeToKill;

                // Apply boss damage multiplier (enrage + damage phases)
                const bossDmgMult = bossAdjustments.mobDamageMultiplier;
                // Apply boss speed multiplier (< 1 means faster attacks)
                const bossSpeedMult = bossAdjustments.mobSpeedMultiplier;
                // Apply defense ignore (e.g., Cindered Knight ignores 30% of player defense)
                let effectivePlayerDef = playerDefense + effectiveExtraDefense;
                if (bossAdjustments.defenseIgnorePercent > 0) {
                    effectivePlayerDef *= (1 - bossAdjustments.defenseIgnorePercent);
                }

                // Recalculate damage per fight with boss modifiers
                const bossAvgAtk = (targetMob.attack + (targetMob.attackMax || targetMob.attack)) / 2;
                const bossDefReduction = effectivePlayerDef / (effectivePlayerDef + 200);
                const bossHitDmg = Math.max(1, Math.floor(bossAvgAtk * bossDmgMult * (1 - bossDefReduction)));
                const bossHitRate = (targetMob.accuracy || 70) / 100;
                const bossEffectiveSpeed = targetMob.attackSpeed * bossSpeedMult;
                const bossDpsVsPlayer = (bossHitDmg * bossHitRate * 1000 / bossEffectiveSpeed) * combinedDamageTakenMult;
                damagePerFight = Math.floor(bossDpsVsPlayer * timeToKill / 1000);

                // Add extra player damage from boss DoTs/true damage per fight
                damagePerFight += Math.floor(bossAdjustments.extraPlayerDamagePerFight);

                logger.debug(`[OFFLINE COMBAT] Boss ${targetMobId} adjustments: hpMult=${bossAdjustments.effectiveHpMultiplier.toFixed(2)}, dmgMult=${bossDmgMult.toFixed(2)}, spdMult=${bossSpeedMult.toFixed(2)}, playerDpsMult=${bossAdjustments.playerDpsMultiplier.toFixed(2)}, extraDot=${bossAdjustments.extraPlayerDamagePerFight.toFixed(0)}, defIgnore=${(bossAdjustments.defenseIgnorePercent*100).toFixed(0)}%`);
                logger.debug(`[OFFLINE COMBAT] Boss adjusted TTK: ${(timeToKill/1000).toFixed(1)}s, DamagePerFight: ${damagePerFight}`);
            }
        }
    }

    // Simulate combat
    let timeRemaining = offlineMs * OFFLINE_CONFIG.EFFICIENCY; // Apply efficiency
    let kills = 0;
    let deaths = 0;
    let foodConsumed = 0;
    // Second Helping (Cooking talent): chance to not consume food
    const secondHelpingChance = getProcChance(SKILLS.COOKING, 'secondHelping');
    let arrowsConsumed = 0;
    let glyphsConsumed = 0;
    const itemGains = {};
    const killsPerMob = {}; // Track kills by mob type for bounty contracts
    const mobLootHistory = {}; // Track loot per mob for Bestiary

    // Track remaining ammo/glyphs
    let remainingArrows = arrowsAvailable;
    let remainingGlyphs = glyphsAvailable;

    // Auto-eat cooldown simulation (4.5s)
    const autoEatCooldown = COMBAT_CONFIG.AUTO_EAT_COOLDOWN;
    let timeSinceLastEat = 0;

    while (timeRemaining > 0) {
        // Check ammo availability for ranged/magic
        if (attackStyle === 'ranged' && !noAmmoNeeded && remainingArrows < attacksPerKill) {
            // Out of arrows - can't continue
            logger.debug(`[OFFLINE COMBAT] Out of arrows (${remainingArrows} remaining), stopping after ${kills} kills`);
            break;
        }
        if (attackStyle === 'magic' && remainingGlyphs < (attacksPerKill * glyphCostPerCast)) {
            // Out of glyphs - can't continue
            logger.debug(`[OFFLINE COMBAT] Out of glyphs (${remainingGlyphs} remaining), stopping after ${kills} kills`);
            break;
        }

        // Check if we can kill a mob
        if (timeRemaining < timeToKill) {
            // Partial fight - no kill, just take some damage
            const partialDamage = Math.floor(damagePerFight * (timeRemaining / timeToKill));
            currentHp -= partialDamage;
            break;
        }

        // Complete one fight
        currentHp -= damagePerFight;
        timeRemaining -= timeToKill;
        timeSinceLastEat += timeToKill;

        // Consume ammo for the kill (track consumption for later)
        if (attackStyle === 'ranged' && !noAmmoNeeded) {
            // Apply ammo save chance (Ammo Conservation talent)
            const arrowsUsed = combinedAmmoSave > 0
                ? Math.max(1, Math.round(attacksPerKill * (1 - combinedAmmoSave)))
                : attacksPerKill;
            remainingArrows -= arrowsUsed;
            arrowsConsumed += arrowsUsed;
        } else if (attackStyle === 'magic') {
            const glyphsUsed = attacksPerKill * glyphCostPerCast;
            remainingGlyphs -= glyphsUsed;
            glyphsConsumed += glyphsUsed;
        }

        // Check auto-eat BEFORE death check - allows food to save the player
        // In real combat, auto-eat triggers during the fight when HP drops below threshold
        if (autoEatEnabled && storedFoodQty > 0 && timeSinceLastEat >= autoEatCooldown) {
            const hpPercent = (currentHp / effectiveMaxHp) * 100;
            // Trigger auto-eat if below threshold OR if we would die (food could have saved us)
            if (hpPercent <= autoEatThreshold || currentHp <= 0) {
                // Eat food - heal regardless
                currentHp = Math.min(currentHp + storedFoodHeal, effectiveMaxHp);
                // Second Helping: chance to not consume food (Cooking talent)
                if (secondHelpingChance <= 0 || Math.random() >= secondHelpingChance) {
                    storedFoodQty--;
                    foodConsumed++;
                }
                timeSinceLastEat = 0;

                // If out of food, disable auto-eat simulation
                if (storedFoodQty <= 0) {
                    storedFoodQty = 0;
                }
            }
        }

        // Check for death (after auto-eat had a chance to save us)
        if (currentHp <= 0) {
            deaths++;
            currentHp = effectiveMaxHp; // Respawn with full HP (includes Vital Force talent)
            timeRemaining -= COMBAT_CONFIG.RESPAWN_TIME; // 25 second penalty
            timeSinceLastEat = 0;
            continue;
        }

        // Successfully killed the mob
        kills++;

        // Apply heal-per-kill from equipment, talents, and set bonuses
        // (lifesteal, regen, Devastating Blow lifesteal, etc.)
        if (combinedHealPerKill > 0) {
            currentHp = Math.min(currentHp + combinedHealPerKill, effectiveMaxHp);
        }

        // Roll for loot using per-mob loot tables
        // Select random visible mob from zone for loot (same as online combat)
        const randomMobId = visibleMobs[Math.floor(Math.random() * visibleMobs.length)];

        // Track kills by mob type for bounty contract progress
        killsPerMob[randomMobId] = (killsPerMob[randomMobId] || 0) + 1;
        const lootTable = getMobLoot(randomMobId, zone.id);
        // Collect this kill's drops before applying bonusLootFind
        const killDrops = [];

        // Apply Pyre loot modifier and bounty mark drop rate bonus to drop chances
        const lootMark = getBountyMark(randomMobId);
        const markDropBonus = lootMark.dropRate;
        for (const drop of lootTable.common) {
            const adjustedChance = drop.chance * pyreLootMult * (1 + markDropBonus);
            if (Math.random() < adjustedChance) {
                const qty = Math.floor(Math.random() * (drop.maxQty - drop.minQty + 1)) + drop.minQty;
                killDrops.push({ itemId: drop.itemId, quantity: qty });
            }
        }
        // Apply Treasure Nose (Husbandry globalRareLoot) bonus to rare drop chances
        const combatGlobalRare = getTalentFlatBonus(SKILLS.HUSBANDRY, 'globalRareLoot');
        for (const drop of lootTable.rare) {
            const adjustedChance = (drop.chance * pyreLootMult * (1 + markDropBonus)) + combatGlobalRare;
            if (Math.random() < adjustedChance) {
                const qty = Math.floor(Math.random() * (drop.maxQty - drop.minQty + 1)) + drop.minQty;
                killDrops.push({ itemId: drop.itemId, quantity: qty });
            }
        }

        // Pilfering Bonus Loot Find - 20% chance for +50% bonus drops from combat kills
        const bonusLootFindChance = getProcChance(SKILLS.PILFERING, 'bonusLootFind');
        if (bonusLootFindChance > 0 && Math.random() < bonusLootFindChance) {
            for (const drop of killDrops) {
                drop.quantity += Math.max(1, Math.floor(drop.quantity * 0.5));
            }
        }

        // Add kill drops to item gains
        for (const drop of killDrops) {
            itemGains[drop.itemId] = (itemGains[drop.itemId] || 0) + drop.quantity;
        }

        // Track loot history per mob for Bestiary
        for (const drop of killDrops) {
            if (!mobLootHistory[randomMobId]) mobLootHistory[randomMobId] = {};
            mobLootHistory[randomMobId][drop.itemId] = (mobLootHistory[randomMobId][drop.itemId] || 0) + drop.quantity;
        }
    }

    // Calculate total XP
    const totalXp = Math.floor(kills * avgMobXp);

    // Calculate architecting XP from auto-eat (Kitchen Overseer talent)
    const autoEatArchXp = calculateAutoEatArchXP(foodConsumed);

    // Only return if there's meaningful progress
    if (kills === 0 && deaths === 0) return null;

    return {
        zoneId: zone.id,
        zoneName: zone.name,
        kills,
        deaths,
        totalXp,
        itemGains,
        foodConsumed,
        finalHp: Math.max(1, currentHp), // Don't end at 0 HP
        storedFoodRemaining: storedFoodQty,
        // Ammo consumption
        attackStyle,
        arrowsConsumed,
        glyphsConsumed,
        glyphIdUsed,
        remainingArrows,
        remainingGlyphs,
        // Bounty contract support - kills by mob type
        killsPerMob,
        // Mob loot history for Bestiary
        mobLootHistory,
        // Architecting XP from Kitchen Overseer talent
        autoEatArchXp
    };
}

/**
 * Calculate offline progress
 * @param {number} savedAt - Timestamp when game was last saved
 * @param {object} savedAction - The action that was active when saved
 * @returns {object|null} Offline progress result or null if not enough time passed
 */
export function calculateOfflineProgress(savedAt, savedAction) {
    try {
        const now = Date.now();
        let offlineMs = Math.max(0, now - savedAt);

    // Cap at maximum offline time
    offlineMs = Math.min(offlineMs, OFFLINE_CONFIG.MAX_TIME_MS);

    // Check minimum time threshold
    if (offlineMs < OFFLINE_CONFIG.MIN_TIME_MS) {
        return null;
    }

    // Calculate farm progress (independent of current action)
    const farmProgress = calculateFarmOfflineProgress();

    // Calculate pet contract progress (independent of current action)
    const petProgress = calculatePetContractOfflineProgress();

    // Calculate blueprint progress (passive architecting system)
    const blueprintProgress = calculateBlueprintOfflineProgress();

    // Calculate milestone progress (passive architecting system)
    const milestoneProgress = calculateMilestoneOfflineProgress();

    // Calculate civic contract progress (passive architecting system)
    const civicContractProgress = calculateCivicContractOfflineProgress();

    // Calculate guild (pilferer) progress (independent of current action)
    const guildProgress = calculateGuildOfflineProgress();

    // Build initial XP gains from passive systems
    const buildInitialXpGains = () => {
        const xp = {};
        if (farmProgress?.farmXp) xp[SKILLS.FARMING] = farmProgress.farmXp;
        // Pet contract XP - Husbandry always, plus secondary skill based on contract type
        if (petProgress?.husbandryXp) xp[SKILLS.HUSBANDRY] = petProgress.husbandryXp;
        if (petProgress?.gathererXp) {
            xp[SKILLS.GATHERER] = (xp[SKILLS.GATHERER] || 0) + petProgress.gathererXp;
        }
        if (petProgress?.bountyXp) {
            xp[SKILLS.BOUNTY] = (xp[SKILLS.BOUNTY] || 0) + petProgress.bountyXp;
        }
        // Add architecting XP from blueprints and milestones
        if (blueprintProgress?.xp) {
            xp[SKILLS.ARCHITECTING] = (xp[SKILLS.ARCHITECTING] || 0) + blueprintProgress.xp;
        }
        if (milestoneProgress?.xp) {
            xp[SKILLS.ARCHITECTING] = (xp[SKILLS.ARCHITECTING] || 0) + milestoneProgress.xp;
        }
        if (civicContractProgress?.totalXp) {
            xp[SKILLS.ARCHITECTING] = (xp[SKILLS.ARCHITECTING] || 0) + civicContractProgress.totalXp;
        }
        // Add architecting XP from BloomHold operations (Realm Steward talent)
        if (farmProgress?.bloomholdArchXp) {
            xp[SKILLS.ARCHITECTING] = (xp[SKILLS.ARCHITECTING] || 0) + farmProgress.bloomholdArchXp;
        }
        // Add pilfering XP from guild pilferers
        if (guildProgress?.pilferingXP) {
            xp[SKILLS.PILFERING] = (xp[SKILLS.PILFERING] || 0) + guildProgress.pilferingXP;
        }
        return xp;
    };

    // Build initial item gains from passive systems
    const buildInitialItemGains = () => {
        const items = { ...(farmProgress?.farmItemGains || {}) };
        // Pet items go to loot chest, not inventory - handled in apply
        return items;
    };

    // Calculate combat progress (if combat was active)
    const combatProgress = savedAction?.type === 'combat' ? calculateCombatOfflineProgress(offlineMs, savedAction) : null;

    // If no action was running, just return time info (with farm + pet + blueprint + milestone + guild + combat progress)
    if (!savedAction) {
        return {
            offlineMs,
            offlineSeconds: Math.floor(offlineMs / 1000),
            hadAction: false,
            xpGains: buildInitialXpGains(),
            itemGains: buildInitialItemGains(),
            actionsCompleted: 0,
            levelUps: [],
            overflowItems: {},
            farmProgress,
            petProgress,
            blueprintProgress,
            milestoneProgress,
            civicContractProgress,
            guildProgress,
            combatProgress
        };
    }

    // Handle combat actions specially (they don't follow the node pattern)
    if (savedAction.type === 'combat' && combatProgress) {
        // Build XP gains including combat
        const xpGains = buildInitialXpGains();
        xpGains[SKILLS.COMBAT] = (xpGains[SKILLS.COMBAT] || 0) + combatProgress.totalXp;

        // Add architecting XP from auto-eat (Kitchen Overseer talent)
        if (combatProgress.autoEatArchXp) {
            xpGains[SKILLS.ARCHITECTING] = (xpGains[SKILLS.ARCHITECTING] || 0) + combatProgress.autoEatArchXp;
        }

        // Build item gains including combat loot
        const itemGains = { ...buildInitialItemGains(), ...combatProgress.itemGains };

        return {
            offlineMs,
            offlineSeconds: Math.floor(offlineMs / 1000),
            hadAction: true,
            actionName: `Combat in ${combatProgress.zoneName}`,
            actionType: 'combat',
            skillId: SKILLS.COMBAT,
            xpGains,
            itemGains,
            actionsCompleted: combatProgress.kills,
            levelUps: [],
            overflowItems: {},
            farmProgress,
            petProgress,
            blueprintProgress,
            milestoneProgress,
            civicContractProgress,
            guildProgress,
            combatProgress
        };
    }

    const node = getNodeData(savedAction);
    const skillId = getSkillId(savedAction.type);

    if (!node || !skillId) {
        logger.debug('[OFFLINE] Could not find node data for action');
        return {
            offlineMs,
            offlineSeconds: Math.floor(offlineMs / 1000),
            hadAction: true,
            xpGains: buildInitialXpGains(),
            itemGains: buildInitialItemGains(),
            actionsCompleted: 0,
            levelUps: [],
            overflowItems: {},
            farmProgress,
            petProgress,
            blueprintProgress,
            milestoneProgress,
            civicContractProgress,
            guildProgress,
            combatProgress: null
        };
    }

    // Calculate how many actions completed (time-based)
    const actionDuration = savedAction.duration || 4000; // Default 4 seconds
    let actionsCompleted = Math.floor((offlineMs * OFFLINE_CONFIG.EFFICIENCY) / actionDuration);

    if (actionsCompleted === 0) {
        return {
            offlineMs,
            offlineSeconds: Math.floor(offlineMs / 1000),
            hadAction: true,
            actionName: savedAction.name,
            xpGains: buildInitialXpGains(),
            itemGains: buildInitialItemGains(),
            actionsCompleted: 0,
            levelUps: [],
            overflowItems: {},
            farmProgress,
            petProgress,
            blueprintProgress,
            milestoneProgress,
            civicContractProgress,
            guildProgress,
            combatProgress: null
        };
    }

    // CRITICAL: Limit actions by available materials for crafting-type skills
    // and track materials to consume
    const timeBasedActions = actionsCompleted;
    const materialConsumption = calculateMaterialLimit(node, savedAction, actionsCompleted);
    actionsCompleted = materialConsumption.limitedActions;

    // Track if we were limited by materials (for user notification)
    const wasLimitedByMaterials = actionsCompleted < timeBasedActions && actionsCompleted > 0;

    // Fishing: Perfect Cast (instantCatch) gives bonus actions (instant next catch)
    if (skillId === SKILLS.FISHING) {
        const instantCatchChance = getProcChance(SKILLS.FISHING, 'instantCatch');
        if (instantCatchChance > 0) {
            // Each proc gives an extra action, applied with variance
            const expectedBonusActions = actionsCompleted * instantCatchChance;
            const variance = 0.1;
            const minBonus = Math.floor(expectedBonusActions * (1 - variance));
            const maxBonus = Math.ceil(expectedBonusActions * (1 + variance));
            const bonusActions = Math.floor(Math.random() * (maxBonus - minBonus + 1)) + minBonus;
            actionsCompleted += bonusActions;
        }
    }

    // Smithing: Resource Save, Coal Save, and Forge Momentum
    if (skillId === SKILLS.SMITHING) {
        // Resource Save - some actions don't consume primary material, giving more effective actions
        const resourceSaveChance = getProcChance(SKILLS.SMITHING, 'resourceSave');
        if (resourceSaveChance > 0 && actionsCompleted > 0) {
            // With X% save rate, effective actions = base / (1 - saveRate)
            const bonusFromSave = Math.floor(actionsCompleted * resourceSaveChance / (1 - resourceSaveChance));
            actionsCompleted += bonusFromSave;
            // Cap at time-based actions (can't do more than time allows)
            actionsCompleted = Math.min(actionsCompleted, timeBasedActions);
        }

        // Coal Save - reduces secondary material (deepcoal) consumption
        const coalSaveChance = getProcChance(SKILLS.SMITHING, 'coalSave');
        if (coalSaveChance > 0 && node?.secondaryItem) {
            const coalKey = node.secondaryItem;
            if (materialConsumption.materialsToConsume[coalKey]) {
                const savedCoal = Math.floor(materialConsumption.materialsToConsume[coalKey] * coalSaveChance);
                materialConsumption.materialsToConsume[coalKey] -= savedCoal;
            }
        }

        // Forge Momentum - 5% instant next action (extra actions like fishing's instantCatch)
        const forgeInstantChance = getProcChance(SKILLS.SMITHING, 'forgeInstant');
        if (forgeInstantChance > 0) {
            const expectedBonusActions = actionsCompleted * forgeInstantChance;
            const variance = 0.1;
            const minBonus = Math.floor(expectedBonusActions * (1 - variance));
            const maxBonus = Math.ceil(expectedBonusActions * (1 + variance));
            const bonusActions = Math.floor(Math.random() * (maxBonus - minBonus + 1)) + minBonus;
            actionsCompleted += bonusActions;
        }
    }

    // Woodworking: Log Save and Perfect Form (instant craft)
    if (skillId === SKILLS.WOODWORKING) {
        // Wood Whisperer - 8% chance to save log materials (bonus actions from saved materials)
        // Only applies to shafts, bows, and staffs (not arrows - they don't use logs)
        const logSaveChance = getProcChance(SKILLS.WOODWORKING, 'logSave');
        if (logSaveChance > 0 && actionsCompleted > 0 && savedAction?.subType !== 'arrows') {
            const bonusFromSave = Math.floor(actionsCompleted * logSaveChance / (1 - logSaveChance));
            actionsCompleted += bonusFromSave;
            actionsCompleted = Math.min(actionsCompleted, timeBasedActions);
        }

        // Perfect Form - 5% instant next craft (extra actions like fishing's instantCatch)
        const instantCraftChance = getProcChance(SKILLS.WOODWORKING, 'instantCraft');
        if (instantCraftChance > 0) {
            const expectedBonusActions = actionsCompleted * instantCraftChance;
            const variance = 0.1;
            const minBonus = Math.floor(expectedBonusActions * (1 - variance));
            const maxBonus = Math.ceil(expectedBonusActions * (1 + variance));
            const bonusActions = Math.floor(Math.random() * (maxBonus - minBonus + 1)) + minBonus;
            actionsCompleted += bonusActions;
        }
    }

    // Cooking: Efficient Cooking - ingredient save gives bonus actions
    if (skillId === SKILLS.COOKING) {
        const ingredientSaveChance = getProcChance(SKILLS.COOKING, 'ingredientSave');
        if (ingredientSaveChance > 0 && actionsCompleted > 0) {
            const bonusFromSave = Math.floor(actionsCompleted * ingredientSaveChance / (1 - ingredientSaveChance));
            actionsCompleted += bonusFromSave;
            actionsCompleted = Math.min(actionsCompleted, timeBasedActions);
        }
    }

    // Pyre: Efficient Burn (log save) and Inferno (instant burn)
    if (skillId === SKILLS.PYRE) {
        const pyreLogSaveChance = getProcChance(SKILLS.PYRE, 'logSave');
        if (pyreLogSaveChance > 0 && actionsCompleted > 0) {
            const bonusFromSave = Math.floor(actionsCompleted * pyreLogSaveChance / (1 - pyreLogSaveChance));
            actionsCompleted += bonusFromSave;
            actionsCompleted = Math.min(actionsCompleted, timeBasedActions);
        }
        // Spiritual Offering - reduces bone consumption for rituals
        const boneSaveChance = getProcChance(SKILLS.PYRE, 'boneSave');
        if (boneSaveChance > 0 && savedAction?.type === 'pyre_ritual') {
            const boneKey = savedAction?.ritual?.boneId;
            if (boneKey && materialConsumption.materialsToConsume[boneKey]) {
                const savedBones = Math.floor(materialConsumption.materialsToConsume[boneKey] * boneSaveChance);
                materialConsumption.materialsToConsume[boneKey] -= savedBones;
            }
        }
        // Inferno - 5% instant next burn (bonus actions)
        const instantBurnChance = getProcChance(SKILLS.PYRE, 'instantBurn');
        if (instantBurnChance > 0) {
            const expectedBonusActions = actionsCompleted * instantBurnChance;
            const variance = 0.1;
            const minBonus = Math.floor(expectedBonusActions * (1 - variance));
            const maxBonus = Math.ceil(expectedBonusActions * (1 + variance));
            const bonusActions = Math.floor(Math.random() * (maxBonus - minBonus + 1)) + minBonus;
            actionsCompleted += bonusActions;
        }
    }

    // Pilfering: Vanishing Act - 5% instant next pickpocket (bonus actions)
    if (skillId === SKILLS.PILFERING) {
        const vanishingActChance = getProcChance(SKILLS.PILFERING, 'vanishingAct');
        if (vanishingActChance > 0) {
            const expectedBonusActions = actionsCompleted * vanishingActChance;
            const variance = 0.1;
            const minBonus = Math.floor(expectedBonusActions * (1 - variance));
            const maxBonus = Math.ceil(expectedBonusActions * (1 + variance));
            const bonusActions = Math.floor(Math.random() * (maxBonus - minBonus + 1)) + minBonus;
            actionsCompleted += bonusActions;
        }
    }

    // Calculate XP gained (apply XP procs - talent XP bonus is applied by addXP())
    const xpPerAction = node.xp || 0;
    const xpMultiplierFromProcs = calculateXPMultiplierProcs(skillId);

    // Smithing: Equipment Expertise (+15% XP when crafting equipment)
    let skillSpecificXPMult = 1;
    if (skillId === SKILLS.SMITHING) {
        const isEquipment = savedAction?.sword || savedAction?.swordId || savedAction?.dirk || savedAction?.dirkId ||
            savedAction?.greatsword || savedAction?.greatswordId || savedAction?.meleeArmor || savedAction?.meleeArmorId ||
            savedAction?.bulwark || savedAction?.bulwarkId;
        if (isEquipment) {
            const equipXPBonus = getTalentFlatBonus(SKILLS.SMITHING, 'equipmentXPBonus');
            if (equipXPBonus > 0) skillSpecificXPMult = 1 + equipXPBonus;
        }
    }

    // Woodworking: Arcane Sensitivity (+10-20% XP when crafting staffs)
    if (skillId === SKILLS.WOODWORKING && savedAction?.subType === 'staffs') {
        const staffXpBonus = getTalentFlatBonus(SKILLS.WOODWORKING, 'staffXpBonus');
        if (staffXpBonus > 0) skillSpecificXPMult = 1 + staffXpBonus;
    }

    // Pyre: Ritual XP bonus (Spiritual Focus + Ritual Potency)
    if (skillId === SKILLS.PYRE && savedAction?.type === 'pyre_ritual') {
        const ritualXpBonus = getTalentFlatBonus(SKILLS.PYRE, 'ritualXpBonus');
        if (ritualXpBonus > 0) skillSpecificXPMult *= (1 + ritualXpBonus);
    }

    // Crafting: Artisan's Pride (+25% XP for highest tier items in category)
    if (skillId === SKILLS.CRAFTING && node?.level) {
        const highTierBonus = getTalentFlatBonus(SKILLS.CRAFTING, 'highTierXPBonus');
        if (highTierBonus > 0) {
            const craftingLevel = gameState.getSkill(SKILLS.CRAFTING)?.level || 1;
            const nodeId = node.id;
            let isHighestTier = false;
            if (Object.values(JEWELRY).some(j => j.id === nodeId)) {
                const maxLevel = Math.max(...Object.values(JEWELRY).filter(j => j.level <= craftingLevel).map(j => j.level));
                isHighestTier = node.level === maxLevel;
            } else if (Object.values(ALL_ARMOR).some(a => a.id === nodeId)) {
                const maxLevel = Math.max(...Object.values(ALL_ARMOR).filter(a => a.level <= craftingLevel).map(a => a.level));
                isHighestTier = node.level === maxLevel;
            } else if (Object.values(ALL_MAGIC_ARMOR).some(a => a.id === nodeId)) {
                const maxLevel = Math.max(...Object.values(ALL_MAGIC_ARMOR).filter(a => a.level <= craftingLevel).map(a => a.level));
                isHighestTier = node.level === maxLevel;
            }
            if (isHighestTier) {
                skillSpecificXPMult *= (1 + highTierBonus);
            }
        }
    }

    const totalXp = Math.floor(xpPerAction * actionsCompleted * xpMultiplierFromProcs * skillSpecificXPMult);

    // Calculate items dropped (apply talent loot bonus and simulate procs)
    const itemGains = {};
    const overflowItems = {};

    // Get talent loot bonus for this skill
    const lootBonus = getTalentLootBonus(skillId);

    // Calculate loot multiplier from procs (double, 5x, 10x chances)
    const lootMultiplierFromProcs = calculateLootMultiplierProcs(skillId, actionsCompleted, savedAction);

    if (node.drops) {
        for (const drop of node.drops) {
            // Support both fixed quantity and min/max range
            // For range, use average (rounded) since we're simulating many actions
            let quantityPerAction = drop.quantity || 1;
            if (drop.minQuantity !== undefined && drop.maxQuantity !== undefined) {
                quantityPerAction = Math.round((drop.minQuantity + drop.maxQuantity) / 2);
            }

            // Apply Bulk Production shaft bonus for woodworking shafts
            let shaftMultiplier = 1;
            if (skillId === SKILLS.WOODWORKING && savedAction?.subType === 'shafts') {
                const shaftBonus = getTalentFlatBonus(SKILLS.WOODWORKING, 'shaftBonus');
                if (shaftBonus > 0) {
                    shaftMultiplier = 1 + shaftBonus;
                }
            }

            // Apply Arrowsmith + Fletcher's Supply for smithing arrowheads
            let arrowheadMultiplier = 1;
            if (skillId === SKILLS.SMITHING && (savedAction?.arrowhead || savedAction?.arrowheadId)) {
                const arrowheadOutput = getTalentFlatBonus(SKILLS.SMITHING, 'arrowheadOutput');
                if (arrowheadOutput > 0) arrowheadMultiplier *= (1 + arrowheadOutput);
                const fletcherSupply = getTalentFlatBonus(SKILLS.SMITHING, 'fletcherSupply');
                if (fletcherSupply > 0) arrowheadMultiplier *= (1 + fletcherSupply);
            }

            // Apply Sigil of Plenty (+25% glyph output) for glyphbinding glyph crafting
            let glyphOutputMultiplier = 1;
            if (skillId === SKILLS.GLYPHBINDING && savedAction?.subType === 'glyph') {
                const glyphOutputBonus = getTalentFlatBonus(SKILLS.GLYPHBINDING, 'glyphOutputBonus');
                if (glyphOutputBonus > 0) glyphOutputMultiplier = 1 + glyphOutputBonus;
            }

            // Apply loot bonus and proc multiplier
            const totalQuantity = Math.floor(quantityPerAction * actionsCompleted * (1 + lootBonus) * lootMultiplierFromProcs * shaftMultiplier * arrowheadMultiplier * glyphOutputMultiplier);
            itemGains[drop.itemId] = totalQuantity;
        }
    }

    // Gatherer zones have no node.drops - synthesize food drops from TIER_DROPS + FOOD_YIELDS
    if (skillId === SKILLS.GATHERER && savedAction?.zone?.tier) {
        const zoneTier = savedAction.zone.tier;
        const tierData = TIER_DROPS[zoneTier];
        const foodYieldKey = savedAction.zone.foodYield || 'medium';
        const foodYield = FOOD_YIELDS[foodYieldKey];
        if (tierData && foodYield) {
            // Gathering Momentum: +25% yield at max stack (assume max stack reached quickly offline)
            const momentumBonus = getTalentFlatBonus(SKILLS.GATHERER, 'momentumYieldBonus');
            const momentumMult = momentumBonus > 0 ? (1 + momentumBonus) : 1;

            // Contract Momentum: +15% yield for items matching active bounty contracts
            const contractMomentumBonus = bountySkill.getContractMomentumBonus(tierData.food);
            const contractMomentumMult = contractMomentumBonus > 0 ? (1 + contractMomentumBonus) : 1;

            // Use average food quantity per action
            const avgFoodQty = Math.round((foodYield.min + foodYield.max) / 2);
            const totalFood = Math.floor(avgFoodQty * actionsCompleted * (1 + lootBonus) * lootMultiplierFromProcs * momentumMult * contractMomentumMult);
            if (totalFood > 0) {
                itemGains[tierData.food] = (itemGains[tierData.food] || 0) + totalFood;
            }

            // Bonus food from higher tiers (basic/refined from tier 2/3)
            if (tierData.bonusFood) {
                for (const bonus of tierData.bonusFood) {
                    const bonusFoodQty = Math.floor(actionsCompleted * bonus.chance * (1 + lootBonus) * lootMultiplierFromProcs);
                    if (bonusFoodQty > 0) {
                        itemGains[bonus.itemId] = (itemGains[bonus.itemId] || 0) + bonusFoodQty;
                    }
                }
            }

            // Rare awakening material drops (zone.rareChance + awakeningChanceBonus talent)
            const awakeningBonus = getTalentFlatBonus(SKILLS.GATHERER, 'awakeningChanceBonus');
            const rareChance = (savedAction.zone.rareChance || 0) + awakeningBonus;
            if (rareChance > 0 && tierData.rare) {
                const rareDrops = Math.floor(actionsCompleted * rareChance);
                if (rareDrops > 0) {
                    itemGains[tierData.rare] = (itemGains[tierData.rare] || 0) + rareDrops;
                }
            }
        }
    }

    // Apply Coin Magnet talent (+10% coins) to coin drops (matches drops.js online behavior)
    if (itemGains['coins'] && itemGains['coins'] > 0) {
        const coinBonus = getTalentFlatBonus(SKILLS.PILFERING, 'globalCoinBonus') || 0;
        if (coinBonus > 0) {
            itemGains['coins'] = Math.floor(itemGains['coins'] * (1 + coinBonus));
        }
    }

    // Fishing: Auto-cook converts raw fish to cooked fish
    if (skillId === SKILLS.FISHING && node?.drops?.[0]?.itemId) {
        const autoCookChance = getProcChance(SKILLS.FISHING, 'autoCook');
        if (autoCookChance > 0) {
            // Calculate expected procs with variance
            const expectedProcs = actionsCompleted * autoCookChance;
            const variance = 0.1;
            const minProcs = Math.floor(expectedProcs * (1 - variance));
            const maxProcs = Math.ceil(expectedProcs * (1 + variance));
            const actualProcs = Math.floor(Math.random() * (maxProcs - minProcs + 1)) + minProcs;

            if (actualProcs > 0) {
                // Map raw fish to cooked fish
                const rawToCooked = {
                    'raw_sand_smelt': 'cooked_sand_smelt', 'raw_silver_sprat': 'cooked_silver_sprat',
                    'raw_blueback': 'cooked_blueback', 'raw_river_char': 'cooked_river_char',
                    'raw_crimson_char': 'cooked_crimson_char', 'raw_skipjack': 'cooked_skipjack',
                    'raw_spiny_lobster': 'cooked_spiny_lobster', 'raw_broadbill': 'cooked_broadbill',
                    'raw_mako': 'cooked_mako'
                };
                const rawFishId = node.drops[0].itemId;
                const cookedFishId = rawToCooked[rawFishId];
                if (cookedFishId && itemGains[rawFishId]) {
                    // Convert some raw fish to cooked (respecting loot multipliers)
                    const cookedCount = Math.min(actualProcs, itemGains[rawFishId]);
                    itemGains[rawFishId] -= cookedCount;
                    itemGains[cookedFishId] = (itemGains[cookedFishId] || 0) + cookedCount;

                    // Flame-Kissed Mastery: Cooking doubleFood proc applies to auto-cooked fish
                    const hasFlameKissed = getTalentFlatBonus(SKILLS.COOKING, 'flameKissedMastery') > 0;
                    if (hasFlameKissed) {
                        const doubleFoodChance = getProcChance(SKILLS.COOKING, 'doubleFood');
                        if (doubleFoodChance > 0) {
                            const bonusCooked = Math.floor(cookedCount * doubleFoodChance);
                            if (bonusCooked > 0) {
                                itemGains[cookedFishId] = (itemGains[cookedFishId] || 0) + bonusCooked;
                            }
                        }
                    }
                }
            }
        }
    }

    // Simulate ashes bonus for glyphbinding and alchemy (potion subtype only)
    // Glyphbinding: 4 ashes = 50% extra output
    // Alchemy potions: 3 ashes = 25% double output
    let ashesConsumed = 0;
    let ashesBonusDrops = {};

    const isGlyphCrafting = savedAction?.type === 'glyphbinding' && savedAction?.subType === 'glyph';
    const isPotionBrewing = savedAction?.type === 'alchemy' && savedAction?.subType === 'potion';

    if ((isGlyphCrafting || isPotionBrewing) && actionsCompleted > 0 && node.drops) {
        const ashesCostPerAction = isGlyphCrafting ? 4 : 3;
        // Glyphbinding: base 50% + Ashes Affinity talent (+25%); Alchemy: fixed 25%
        const ashesAffinityBonus = isGlyphCrafting ? getTalentFlatBonus(SKILLS.GLYPHBINDING, 'ashesSuccessRate') : 0;
        const procChance = (isGlyphCrafting ? 0.50 : 0.25) + ashesAffinityBonus;

        const availableAshes = gameState.getItemCount('ashes');
        const actionsWithAshes = Math.min(actionsCompleted, Math.floor(availableAshes / ashesCostPerAction));

        if (actionsWithAshes > 0) {
            // Consume ashes for all actions that can use them
            ashesConsumed = actionsWithAshes * ashesCostPerAction;

            // Calculate expected bonus procs (statistically)
            const expectedProcs = actionsWithAshes * procChance;
            // Add variance (±10%)
            const minProcs = Math.floor(expectedProcs * 0.9);
            const maxProcs = Math.ceil(expectedProcs * 1.1);
            const actualProcs = Math.floor(Math.random() * (maxProcs - minProcs + 1)) + minProcs;

            // Award bonus drops for procs
            if (actualProcs > 0) {
                for (const drop of node.drops) {
                    let quantityPerAction = drop.quantity || 1;
                    if (drop.minQuantity !== undefined && drop.maxQuantity !== undefined) {
                        quantityPerAction = Math.round((drop.minQuantity + drop.maxQuantity) / 2);
                    }
                    const bonusQty = quantityPerAction * actualProcs;
                    ashesBonusDrops[drop.itemId] = bonusQty;
                    itemGains[drop.itemId] = (itemGains[drop.itemId] || 0) + bonusQty;
                }
            }
        }
    }

    // Simulate talent procs for bonus items (statistically based on actionsCompleted)
    const procBonuses = simulateTalentProcs(skillId, actionsCompleted, savedAction, node);

    // Merge XP gains (action XP + farm XP + pet XP + blueprint XP + milestone XP + guild XP)
    const xpGains = { [skillId]: totalXp };
    if (farmProgress?.farmXp) {
        xpGains[SKILLS.FARMING] = (xpGains[SKILLS.FARMING] || 0) + farmProgress.farmXp;
    }
    // Pet contract XP - Husbandry always, plus secondary skill based on contract type
    if (petProgress?.husbandryXp) {
        xpGains[SKILLS.HUSBANDRY] = (xpGains[SKILLS.HUSBANDRY] || 0) + petProgress.husbandryXp;
    }
    if (petProgress?.gathererXp) {
        xpGains[SKILLS.GATHERER] = (xpGains[SKILLS.GATHERER] || 0) + petProgress.gathererXp;
    }
    if (petProgress?.bountyXp) {
        xpGains[SKILLS.BOUNTY] = (xpGains[SKILLS.BOUNTY] || 0) + petProgress.bountyXp;
    }
    if (blueprintProgress?.xp) {
        xpGains[SKILLS.ARCHITECTING] = (xpGains[SKILLS.ARCHITECTING] || 0) + blueprintProgress.xp;
    }
    if (milestoneProgress?.xp) {
        xpGains[SKILLS.ARCHITECTING] = (xpGains[SKILLS.ARCHITECTING] || 0) + milestoneProgress.xp;
    }
    if (civicContractProgress?.totalXp) {
        xpGains[SKILLS.ARCHITECTING] = (xpGains[SKILLS.ARCHITECTING] || 0) + civicContractProgress.totalXp;
    }
    // Guild pilfering XP
    if (guildProgress?.pilferingXP) {
        xpGains[SKILLS.PILFERING] = (xpGains[SKILLS.PILFERING] || 0) + guildProgress.pilferingXP;
    }

    // Gatherer: Pet Companion talent awards Husbandry XP (50% of zone XP per proc)
    if (skillId === SKILLS.GATHERER) {
        const petAssistChance = getProcChance(SKILLS.GATHERER, 'petAssist');
        if (petAssistChance > 0) {
            // Calculate expected procs with variance (same pattern as other procs)
            const expectedProcs = actionsCompleted * petAssistChance;
            const variance = 0.1;
            const minProcs = Math.floor(expectedProcs * (1 - variance));
            const maxProcs = Math.ceil(expectedProcs * (1 + variance));
            const actualProcs = Math.floor(Math.random() * (maxProcs - minProcs + 1)) + minProcs;

            if (actualProcs > 0) {
                // 50% of zone XP per proc goes to Husbandry
                const husbandryXpFromPet = Math.floor(xpPerAction * 0.5 * actualProcs);
                xpGains[SKILLS.HUSBANDRY] = (xpGains[SKILLS.HUSBANDRY] || 0) + husbandryXpFromPet;
            }
        }
    }

    // Mining: Heart of the Mountain (autoSmelt) awards Smithing XP for each bar smelted
    if (skillId === SKILLS.MINING && node?.drops?.[0]) {
        const autoSmeltChance = getProcChance(SKILLS.MINING, 'autoSmelt');
        const miningDropBonus2 = getTalentFlatBonus(SKILLS.MINING, 'dropChanceBonus') || 0;
        if (autoSmeltChance > 0) {
            const oreToBarKey = {
                'pyrestone_ore': 'PYRESTONE', 'pale_shard_ore': 'PALE_SHARD',
                'blackiron_ore': 'BLACKIRON', 'gold_ore': 'GOLD',
                'skyiron_ore': 'SKYIRON', 'verdant_core_ore': 'VERDANT_CORE', 'voidsteel_ore': 'VOIDSTEEL'
            };
            const barKey = oreToBarKey[node.drops[0].itemId];
            if (barKey && BARS[barKey]) {
                const smeltProcs = Math.floor(actionsCompleted * autoSmeltChance * (1 + miningDropBonus2));
                const smithXP = Math.floor(BARS[barKey].xp * smeltProcs);
                if (smithXP > 0) {
                    xpGains[SKILLS.SMITHING] = (xpGains[SKILLS.SMITHING] || 0) + smithXP;
                }
            }
        }
    }

    // Woodworking: Arcane Carving talent awards Glyphbinding XP when crafting staffs
    if (skillId === SKILLS.WOODWORKING && savedAction?.subType === 'staffs') {
        const glyphXpBonus = getTalentFlatBonus(SKILLS.WOODWORKING, 'glyphbindingXpOnStaff');
        if (glyphXpBonus > 0) {
            // Award 15% of staff XP to Glyphbinding (based on base XP before other bonuses)
            const glyphXP = Math.floor(xpPerAction * glyphXpBonus * actionsCompleted);
            if (glyphXP > 0) {
                xpGains[SKILLS.GLYPHBINDING] = (xpGains[SKILLS.GLYPHBINDING] || 0) + glyphXP;
            }
        }
    }

    // Fishing: Auto-cook talent (Fresh Preparation + Flame-Kissed Catch) awards Cooking XP
    if (skillId === SKILLS.FISHING) {
        const autoCookChance = getProcChance(SKILLS.FISHING, 'autoCook');
        if (autoCookChance > 0) {
            // Calculate expected procs with variance
            const expectedProcs = actionsCompleted * autoCookChance;
            const variance = 0.1;
            const minProcs = Math.floor(expectedProcs * (1 - variance));
            const maxProcs = Math.ceil(expectedProcs * (1 + variance));
            const actualProcs = Math.floor(Math.random() * (maxProcs - minProcs + 1)) + minProcs;

            if (actualProcs > 0 && node?.drops?.[0]?.itemId) {
                // Map raw fish to cooking XP
                const rawToCookingXp = {
                    'raw_sand_smelt': 30, 'raw_silver_sprat': 40, 'raw_blueback': 50,
                    'raw_river_char': 70, 'raw_crimson_char': 90, 'raw_skipjack': 100,
                    'raw_spiny_lobster': 120, 'raw_broadbill': 140, 'raw_mako': 210
                };
                const rawFishId = node.drops[0].itemId;
                const cookingXpPerFish = rawToCookingXp[rawFishId] || 30;
                const totalCookingXp = cookingXpPerFish * actualProcs;
                xpGains[SKILLS.COOKING] = (xpGains[SKILLS.COOKING] || 0) + totalCookingXp;
            }
        }
    }

    // Merge item gains (action items + farm items + talent proc bonuses)
    // Note: Pet items go to loot chest, handled in applyOfflineProgress
    const mergedItemGains = { ...itemGains };

    // Merge talent proc bonus items
    for (const [itemId, qty] of Object.entries(procBonuses)) {
        mergedItemGains[itemId] = (mergedItemGains[itemId] || 0) + qty;
    }

    // Merge farm item gains
    if (farmProgress?.farmItemGains) {
        for (const [itemId, qty] of Object.entries(farmProgress.farmItemGains)) {
            mergedItemGains[itemId] = (mergedItemGains[itemId] || 0) + qty;
        }
    }

    return {
        offlineMs,
        offlineSeconds: Math.floor(offlineMs / 1000),
        hadAction: true,
        actionName: savedAction.name,
        actionType: savedAction.type,
        skillId,
        xpGains,
        itemGains: mergedItemGains,
        actionsCompleted,
        levelUps: [], // Will be populated during apply
        overflowItems,
        farmProgress,
        petProgress,
        blueprintProgress,
        milestoneProgress,
        civicContractProgress,
        guildProgress,
        combatProgress: null, // Non-combat action
        // Material consumption tracking
        materialsConsumed: materialConsumption.materialsToConsume,
        bloomholdConsumed: materialConsumption.bloomholdToConsume,
        goldConsumed: materialConsumption.goldToConsume,
        ashesConsumed: ashesConsumed,
        ashesBonusDrops: ashesBonusDrops,
        wasLimitedByMaterials,
        potentialActions: timeBasedActions // How many actions time allowed (for UI message)
    };
    } catch (error) {
        logger.error('[OFFLINE] Error calculating offline progress:', error);
        return null;
    }
}

/**
 * Apply offline progress to game state
 * @param {object} progress - Progress result from calculateOfflineProgress
 * @param {boolean} sendOverflowToBank - Whether to send overflow items to bank
 * @returns {object} Applied progress with updated level info
 */
export function applyOfflineProgress(progress, sendOverflowToBank = false) {
    try {
        // Allow processing if there's any passive progress OR action progress
        const hasFarmProgress = progress?.farmProgress?.completedPlots?.length > 0;
    const hasPetProgress = progress?.petProgress?.completedContracts?.length > 0;
    const hasBlueprintProgress = progress?.blueprintProgress?.completed;
    const hasMilestoneProgress = progress?.milestoneProgress?.completed;
    const hasCivicContractProgress = progress?.civicContractProgress?.completedPhases?.length > 0;
    const hasGuildProgress = progress?.guildProgress?.totalItemsGathered > 0;
    const hasCombatProgress = progress?.combatProgress?.kills > 0;
    const hasActionProgress = progress?.actionsCompleted > 0;

    if (!progress || (!hasActionProgress && !hasFarmProgress && !hasPetProgress && !hasBlueprintProgress && !hasMilestoneProgress && !hasCivicContractProgress && !hasGuildProgress && !hasCombatProgress)) {
        return progress;
    }

    const levelUps = [];

    // Apply XP gains (skip tracking to avoid XP/hour spike from offline dump)
    for (const [skillId, xp] of Object.entries(progress.xpGains || {})) {
        if (xp > 0) {
            const skill = gameState.getSkill(skillId);
            const oldLevel = skill?.level || 1;

            const result = addXP(skillId, xp, null, { skipTracking: true, skipCorruption: true });

            if (result.leveledUp) {
                levelUps.push({
                    skillId,
                    oldLevel,
                    newLevel: result.newLevel,
                    levelsGained: result.levelsGained
                });

                // Queue Pyre Veil lore for offline Pyre level-ups
                if (skillId === SKILLS.PYRE) {
                    const loreTriggers = checkPyreLoreTriggers(oldLevel, result.newLevel);
                    for (const loreId of loreTriggers) {
                        gameState.queueLore(loreId);
                    }
                }
            }
        }
    }

    // Consume materials used for crafting-type actions (from inventory)
    if (progress.materialsConsumed) {
        for (const [itemId, quantity] of Object.entries(progress.materialsConsumed)) {
            if (quantity > 0) {
                gameState.removeItem(itemId, quantity);
                logger.debug(`[OFFLINE] Consumed ${quantity}x ${itemId} from inventory`);
            }
        }
    }

    // Consume materials from BloomHold (for alchemy)
    if (progress.bloomholdConsumed) {
        for (const [itemId, quantity] of Object.entries(progress.bloomholdConsumed)) {
            if (quantity > 0) {
                gameState.removeBloomholdItem(itemId, quantity);
                logger.debug(`[OFFLINE] Consumed ${quantity}x ${itemId} from BloomHold`);
            }
        }
    }

    // Consume gold for crafting armor
    if (progress.goldConsumed && progress.goldConsumed > 0) {
        gameState.removeGold(progress.goldConsumed);
        logger.debug(`[OFFLINE] Consumed ${progress.goldConsumed} gold`);
    }

    // Consume ashes for glyphbinding/alchemy bonus
    if (progress.ashesConsumed && progress.ashesConsumed > 0) {
        gameState.removeItem('ashes', progress.ashesConsumed);
        logger.debug(`[OFFLINE] Consumed ${progress.ashesConsumed} ashes for bonus procs`);
    }

    // Apply item gains (non-farm items go to inventory)
    const overflowItems = {};

    for (const [itemId, quantity] of Object.entries(progress.itemGains || {})) {
        if (quantity <= 0) continue;

        // Skip farm items based on destination setting
        const isFarmItem = progress.farmProgress?.farmItemGains?.[itemId];
        const farmDestination = progress.farmProgress?.autoHarvestDestination;

        if (isFarmItem) {
            if (farmDestination === 'bloomhold' && gameState.isBloomholdUnlocked() && isBloomholdEligible(itemId)) {
                // Try BloomHold first
                const bhResult = gameState.addBloomholdItem(itemId, quantity);
                if (!bhResult.success || bhResult.added < quantity) {
                    // BloomHold full, send remainder to bank
                    const remainder = quantity - (bhResult.added || 0);
                    gameState.addBankItem(itemId, remainder);
                }
            } else if (farmDestination === 'bank') {
                gameState.addBankItem(itemId, quantity);
            } else {
                // Default: inventory
                const result = gameState.addItem(itemId, quantity);
                if (!result.success || result.added < quantity) {
                    const overflow = quantity - (result.added || 0);
                    overflowItems[itemId] = overflow;
                    if (sendOverflowToBank && overflow > 0) {
                        gameState.addBankItem(itemId, overflow);
                    }
                }
            }
        } else {
            // Check if this is combat loot - goes to combat loot chest
            const isCombatItem = hasCombatProgress && progress.combatProgress?.itemGains?.[itemId];

            if (isCombatItem) {
                // Combat loot goes to combat loot chest (auto-collects oldest if full)
                gameState.addCombatLoot(itemId, quantity);
            } else if (itemId.endsWith('_seed') && getTalentFlatBonus(SKILLS.GATHERER, 'seedsToBloomHold') > 0 &&
                       gameState.isBloomholdUnlocked() && isBloomholdEligible(itemId)) {
                // Nature's Harmony: Route seeds to BloomHold
                const bhResult = gameState.addBloomholdItem(itemId, quantity);
                if (!bhResult.success || bhResult.added < quantity) {
                    const remainder = quantity - (bhResult.added || 0);
                    gameState.addItem(itemId, remainder);
                }
            } else if (itemId.startsWith('grimy_') && getTalentFlatBonus(SKILLS.ALCHEMY, 'bloomAutoDeposit') > 0 &&
                       gameState.isBloomholdUnlocked() && isBloomholdEligible(itemId)) {
                // Bloom Touched: Route grimy herbs to BloomHold
                const bhResult = gameState.addBloomholdItem(itemId, quantity);
                if (!bhResult.success || bhResult.added < quantity) {
                    const remainder = quantity - (bhResult.added || 0);
                    gameState.addItem(itemId, remainder);
                }
            } else {
                // Non-combat items go to inventory
                const result = gameState.addItem(itemId, quantity);

                if (!result.success || result.added < quantity) {
                    const overflow = quantity - (result.added || 0);
                    overflowItems[itemId] = overflow;

                    if (sendOverflowToBank && overflow > 0) {
                        gameState.addBankItem(itemId, overflow);
                    }
                }
            }
        }
    }

    // Track all item gains for bounty contracts (matching online parity)
    // This updates progress counters used for UI display (progress bars, completion %)
    // Delivery eligibility uses inventory comparison and already works from addItem() above
    for (const [itemId, quantity] of Object.entries(progress.itemGains || {})) {
        if (quantity > 0) {
            bountySkill.trackItemProgress(itemId, quantity);
        }
    }

    // Apply farm plot state changes
    if (hasFarmProgress) {
        const farmProgress = progress.farmProgress;

        for (const plotInfo of farmProgress.completedPlots) {
            if (farmProgress.autoHarvestEnabled) {
                // Auto-harvested: clear the plot
                farmingSkill.clearPlot(plotInfo.plotIndex);
                logger.debug(`[OFFLINE] Auto-harvested ${plotInfo.cropData?.name} from plot ${plotInfo.plotIndex + 1}`);
            } else {
                // Not auto-harvested: mark as ready for manual harvest
                gameState.updateFarmPlot(plotInfo.plotIndex, { state: 'ready' });
                logger.debug(`[OFFLINE] ${plotInfo.cropData?.name} ready in plot ${plotInfo.plotIndex + 1}`);
            }
        }

        // Handle auto-replant (Eternal Garden capstone)
        if (farmProgress.hasAutoReplant && farmProgress.plotsToReplant?.length > 0) {
            for (const replantInfo of farmProgress.plotsToReplant) {
                // Check if we have seeds (from inventory, bloomhold, or harvested items)
                const inventorySeeds = gameState.getItemCount(replantInfo.seedId);
                const bloomholdSeeds = gameState.isBloomholdUnlocked() ? gameState.getBloomholdItemCount(replantInfo.seedId) : 0;
                // Also count seeds we just harvested (they're in farmItemGains)
                const harvestedSeeds = farmProgress.farmItemGains[replantInfo.seedId] || 0;
                const totalSeeds = inventorySeeds + bloomholdSeeds + harvestedSeeds;

                if (totalSeeds >= replantInfo.seedCost) {
                    // Use farmingSkill.plantInPlot which handles freeSeed proc and growth time reduction
                    const result = farmingSkill.plantInPlot(replantInfo.plotIndex, replantInfo.cropType, replantInfo.cropId);
                    if (result.success) {
                        logger.debug(`[OFFLINE] Auto-replanted ${replantInfo.cropId} in plot ${replantInfo.plotIndex + 1}`);
                    }
                }
            }
        }
    }

    // Apply pet contract completions
    if (hasPetProgress) {
        const petProgress = progress.petProgress;

        // Add pet items to loot chest (or auto-loot to bank, with chest fallback)
        for (const [itemId, quantity] of Object.entries(petProgress.petItemGains || {})) {
            if (gameState.hasAutoLootChests()) {
                const result = gameState.autoLootToBank(itemId, quantity, 'husbandry');
                // Fall back to loot chest if bank/bloomhold is full
                if (!result.success || result.added < quantity) {
                    const remaining = quantity - (result.added || 0);
                    gameState.addLootChestItem(itemId, remaining);
                }
            } else {
                gameState.addLootChestItem(itemId, quantity);
            }
        }

        // Complete contracts and try to restart
        for (const contractInfo of petProgress.completedContracts) {
            // Mark contract type as completed (for pet unlocks)
            gameState.markContractTypeCompleted(contractInfo.contract.type);

            // Clear the correct slot
            if (contractInfo.slot === 'secondary') {
                gameState.stopSecondaryPetContract(contractInfo.petId);
            } else {
                gameState.stopPetContract(contractInfo.petId);
            }

            // Try to restart the same contract in the same slot if loot chest has space
            if (gameState.hasLootChestSpace()) {
                husbandrySkill.assignContract(contractInfo.petId, contractInfo.contractId, contractInfo.slot);
                logger.debug(`[OFFLINE] Restarted ${contractInfo.contract.name} for ${contractInfo.petId} (${contractInfo.slot})`);
            }

            logger.debug(`[OFFLINE] Completed ${contractInfo.contract.name} for ${contractInfo.petId} (${contractInfo.slot})`);
        }
    }

    // Apply blueprint completion (passive architecting)
    if (hasBlueprintProgress) {
        const blueprintProgress = progress.blueprintProgress;
        // Stop completed blueprints in reverse order to preserve indices
        const completedSlots = blueprintProgress.completedBlueprints
            .map(cb => cb.slotIndex)
            .sort((a, b) => b - a);
        for (const slotIndex of completedSlots) {
            gameState.stopBlueprint(slotIndex);
        }
        // Apply preserveMaterials refunds
        if (blueprintProgress.refundedMaterials) {
            for (const [itemId, qty] of Object.entries(blueprintProgress.refundedMaterials)) {
                gameState.addItem(itemId, qty);
            }
        }
        const names = blueprintProgress.completedBlueprints.map(cb => cb.blueprint.name).join(', ');
        logger.debug(`[OFFLINE] Completed blueprint(s): ${names}`);
    }

    // Apply milestone completion (passive architecting)
    if (hasMilestoneProgress) {
        const milestoneProgress = progress.milestoneProgress;
        // Mark milestone as completed
        gameState.completeMilestone(milestoneProgress.milestoneId);
        gameState.clearActiveMilestone();
        logger.debug(`[OFFLINE] Completed milestone: ${milestoneProgress.milestone.name}`);
    }

    // Apply civic contract progress (passive architecting)
    if (hasCivicContractProgress) {
        const civicProgress = progress.civicContractProgress;
        if (civicProgress.completed) {
            // Contract fully completed - award rewards and clear
            if (civicProgress.completionGold > 0) {
                gameState.addItem('coins', civicProgress.completionGold);
            }
            for (const item of civicProgress.completionItems) {
                if (item.itemId && item.quantity) {
                    gameState.addItem(item.itemId, item.quantity);
                }
            }
            gameState.completeCivicContract(civicProgress.contractId);
            gameState.clearActiveCivicContract();
            logger.debug(`[OFFLINE] Completed civic contract: ${civicProgress.contract.name}`);
        } else if (civicProgress.finalStatus === 'ready_for_next') {
            // Partially completed - advance past completed phases, waiting for player to start next build phase
            gameState.updateActiveCivicContract({
                currentPhase: civicProgress.completedPhases[civicProgress.completedPhases.length - 1].phaseIndex,
                status: 'ready_for_next'
            });
            logger.debug(`[OFFLINE] Civic contract phase(s) completed, awaiting player for next build phase`);
        } else if (civicProgress.newPhaseIndex !== null) {
            // Advanced to a wait phase that hasn't completed yet
            gameState.updateActiveCivicContract({
                currentPhase: civicProgress.newPhaseIndex,
                phaseStartedAt: civicProgress.newPhaseStartedAt,
                status: 'waiting'
            });
            logger.debug(`[OFFLINE] Civic contract advanced to wait phase ${civicProgress.newPhaseIndex}`);
        }
    }

    // Apply guild (pilferer) progress
    if (hasGuildProgress) {
        const guildProgress = progress.guildProgress;

        // Add items to guild chest (or auto-loot to bank, with chest fallback)
        for (const [itemId, quantity] of Object.entries(guildProgress.itemsGathered)) {
            if (gameState.hasAutoLootChests()) {
                // Try auto-loot directly to bank (seeds to BloomHold first)
                const result = gameState.autoLootToBank(itemId, quantity, 'pilfering');
                const addedToBank = result.success ? (result.added || 0) : 0;

                // Fall back to guild chest for items that didn't fit
                const remaining = quantity - addedToBank;
                for (let i = 0; i < remaining; i++) {
                    gameState.addGuildChestItem(itemId, 1);
                }

                // Increment counter for all items
                for (let i = 0; i < quantity; i++) {
                    gameState.incrementGuildItemsGathered();
                }
            } else {
                for (let i = 0; i < quantity; i++) {
                    gameState.addGuildChestItem(itemId, 1);
                    gameState.incrementGuildItemsGathered();
                }
            }
        }

        // Reset pilferer timers (they completed their work, start fresh)
        const activePilferers = gameState.getActivePilferers();
        for (const pilferer of activePilferers) {
            if (pilferer.active) {
                gameState.resetPilfererTimer(pilferer.domainId, null);
            }
        }

        logger.debug(`[OFFLINE] Guild gathered ${guildProgress.totalItemsGathered} items (+${guildProgress.pilferingXP} Pilfering XP)`);
    }

    // Apply combat progress
    if (hasCombatProgress) {
        const combatProgress = progress.combatProgress;
        const combat = gameState.getCombat();

        // Update HP to final value from simulation
        gameState.setCurrentHp(combatProgress.finalHp);

        // Update auto-eat stored food quantity
        if (combatProgress.foodConsumed > 0) {
            const autoEat = gameState.getAutoEat();
            if (autoEat.storedFood) {
                autoEat.storedFood.quantity = combatProgress.storedFoodRemaining;
                if (autoEat.storedFood.quantity <= 0) {
                    autoEat.storedFood = null;
                }
            }
        }

        // Consume arrows for ranged combat
        if (combatProgress.attackStyle === 'ranged' && combatProgress.arrowsConsumed > 0) {
            gameState.consumeAmmo?.(combatProgress.arrowsConsumed);
        }

        // Consume glyphs for magic combat
        if (combatProgress.attackStyle === 'magic' && combatProgress.glyphsConsumed > 0 && combatProgress.glyphIdUsed) {
            // Remove glyphs - first from glyph pouch, then from inventory
            let remaining = combatProgress.glyphsConsumed;

            // Try glyph pouch first
            if (gameState.isGlyphPouchUnlocked?.()) {
                const glyphPouch = gameState.getGlyphPouch?.();
                if (glyphPouch?.slots) {
                    for (const slot of glyphPouch.slots) {
                        if (slot && slot.itemId === combatProgress.glyphIdUsed && remaining > 0) {
                            const toRemove = Math.min(slot.quantity, remaining);
                            slot.quantity -= toRemove;
                            remaining -= toRemove;
                            if (slot.quantity <= 0) {
                                slot.itemId = null;
                                slot.quantity = 0;
                            }
                        }
                    }
                }
            }

            // Then from inventory if still remaining
            if (remaining > 0) {
                gameState.removeItem(combatProgress.glyphIdUsed, remaining);
            }
        }

        // Track kills for statistics and bounty contracts
        // Use actual per-mob kill counts if available (more accurate for bounty contracts)
        if (combatProgress.killsPerMob && Object.keys(combatProgress.killsPerMob).length > 0) {
            for (const [mobId, count] of Object.entries(combatProgress.killsPerMob)) {
                // Track kills in gameState for statistics
                for (let j = 0; j < count; j++) {
                    gameState.trackKill(mobId);
                }
                // Track kills for bounty contracts
                bountySkill.trackKillProgress(mobId, count);
            }
        } else {
            // Fallback: distribute kills evenly across zone mobs
            const zone = getZone(combatProgress.zoneId);
            if (zone && zone.mobs.length > 0) {
                const killsPerMobCount = Math.floor(combatProgress.kills / zone.mobs.length);
                const remainder = combatProgress.kills % zone.mobs.length;

                for (let i = 0; i < zone.mobs.length; i++) {
                    const mobId = zone.mobs[i];
                    const kills = killsPerMobCount + (i < remainder ? 1 : 0);
                    for (let j = 0; j < kills; j++) {
                        gameState.trackKill(mobId);
                    }
                    // Track kills for bounty contracts
                    if (kills > 0) {
                        bountySkill.trackKillProgress(mobId, kills);
                    }
                }
            }
        }

        // Track mob loot history for Bestiary
        if (combatProgress.mobLootHistory) {
            for (const [mobId, items] of Object.entries(combatProgress.mobLootHistory)) {
                for (const [itemId, qty] of Object.entries(items)) {
                    gameState.trackMobLoot(mobId, itemId, qty);
                }
            }
        }

        // Check for bounty mark rank-ups from offline kills
        if (combatProgress.killsPerMob) {
            import('../ui/bountyMarkNotification.js').then(({ checkBountyMarkRankUp }) => {
                for (const [mobId, count] of Object.entries(combatProgress.killsPerMob)) {
                    const currentKills = gameState.getKillCount(mobId);
                    const prevKills = currentKills - count;
                    const mob = getMob(mobId);
                    if (mob) {
                        checkBountyMarkRankUp(mobId, mob.name, prevKills, currentKills);
                    }
                }
            });
        }

        logger.debug(`[OFFLINE] Combat: ${combatProgress.kills} kills, ${combatProgress.deaths} deaths in ${combatProgress.zoneName}`);
    }

    const totalXp = Object.values(progress.xpGains || {}).reduce((a, b) => a + b, 0);
    const petContractsCompleted = hasPetProgress ? progress.petProgress.completedContracts.length : 0;
    const guildItemsGathered = hasGuildProgress ? progress.guildProgress.totalItemsGathered : 0;
    logger.debug(`[OFFLINE] Applied progress: ${progress.actionsCompleted || 0} actions, ${totalXp} XP, ${hasFarmProgress ? progress.farmProgress.completedPlots.length : 0} plots, ${petContractsCompleted} pet contracts${hasBlueprintProgress ? ', 1 blueprint' : ''}${hasMilestoneProgress ? ', 1 milestone' : ''}${hasGuildProgress ? `, ${guildItemsGathered} guild items` : ''}`);

    return {
        ...progress,
        levelUps,
        overflowItems
    };
    } catch (error) {
        logger.error('[OFFLINE] Error applying offline progress:', error);
        return progress; // Return original progress on error
    }
}

/**
 * Format offline time for display
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string
 */
export function formatOfflineTime(seconds) {
    if (seconds < 60) {
        return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours === 0) {
        return `${minutes}m`;
    }

    return `${hours}h ${minutes}m`;
}

export default {
    calculateOfflineProgress,
    applyOfflineProgress,
    formatOfflineTime
};
